import { it as itLocale, es as esLocale, enUS, enGB, de as deLocale, fr as frLocale, nl as nlLocale, pt as ptLocale, ptBR, ru as ruLocale, pl as plLocale, ro as roLocale, zhCN, ja as jaLocale, hi as hiLocale, ko as koLocale, ar as arLocale } from 'date-fns/locale';

export const DATE_LOCALES = {
  // base codes (backward compat)
  en: enUS,
  es: esLocale,
  it: itLocale,
  de: deLocale,
  fr: frLocale,
  nl: nlLocale,
  pt: ptLocale,
  ru: ruLocale,
  pl: plLocale,
  ro: roLocale,
  zh: zhCN,
  ja: jaLocale,
  hi: hiLocale,
  ko: koLocale,
  ar: arLocale,
  // explicit variants
  'en-US': enUS,
  'en-GB': enGB,
  'es-ES': esLocale,
  'es-AR': esLocale,
  'es-MX': esLocale,
  'es-CO': esLocale,
  'pt-PT': ptLocale,
  'pt-BR': ptBR,
};

const T = {
  en: {
    nav: { today: 'Today', add: 'Add', tasks: 'Notes', search: 'Search', settings: 'Settings', groups: 'Calendars' },
    search: {
      placeholder: 'Search events…',
      searching: 'Searching…',
      noResults: 'No results for',
      noResultsHint: 'Try different keywords',
      hint: 'e.g. "dentist", "meeting", "birthday"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Login Failed',
      subtitle: 'Something went wrong. Please try again.',
      back: 'Go back',
    },
    login: {
      title: 'Memo',
      subtitle: 'Your personal notes & events manager with voice input',
      features: [
        'Add events by voice while driving',
        'Prioritize with colors',
        'Syncs to Google Calendar',
        'Daily Telegram summary at 7am',
      ],
      signIn: 'Sign in with Google',
    },
    onboarding: {
      demo: {
        views: ["Day", "Week", "Month", "Year"],
        allDay: "All day",
        prio: ["High", "Medium", "Low"],
        labels: ["WORK", "SPORT", "HOME"],
        shared: "Shared",
        create: "Create",
        join: "Join with code",
        sharedTitle: "Shared calendars",
        searchTitle: "Search events",
        searchHint: "e.g. dentist, meeting…",
        notePh: "Write a note…",
        settingsTitle: "Settings",
        light: "Light",
        dark: "Dark",
        signout: "Sign out",
        ics: "Download .ics",
        notif: "Notifications",
        summaryLbl: "Summary",
        time: "Time",
        telegram: "Send via Telegram",
        reminder: "30 min before",
        greeting: "Good morning",
        repeat: ["Daily", "Weekly"],
        ev: ["Meeting", "Gym", "Groceries", "Dentist", "Dinner", "Delivery"],
      },
      skip: "Skip",
      next: "Next →",
      start: "Get started",
      slides: [
        { key: "calendar", title: "The calendar that talks to you", text: "Day, week, month and year: see your plans however you like. And with one tap it reads the day's and week's schedule out loud." },
        { key: "add", title: "Add it your way", text: "Create an event by typing, by voice or with a photo: snap a sheet, an email or a poster and the AI fills it all in. You can also attach documents to the event." },
        { key: "planner", title: "Organise your day", text: "The daily and weekly planner shows free and busy slots: fit everything in at a glance." },
        { key: "multi", title: "Search, note, share", text: "Find any event in a second, keep your notes always at hand, and share the calendar with family or team — each in their own colour." },
        { key: "summary", title: "Every morning, your summary", text: "Get the day's plans on Telegram and by email, sorted by time and priority. Start your day without forgetting a thing." },
        { key: "settings", title: "Make MeMo work for you", text: "Choose language, summary time, notifications, ringtone and theme: MeMo adapts to how you work." },
      ],
    },

    welcome: {
      greeting: 'Welcome',
      subtitle: 'Choose your language to get started',
      saving: 'Saving...',
      continue: 'Continue →',
      countryLabel: 'Your country',
      countryPlaceholder: 'Select your country...',
      themeLabel: 'Choose your theme',
      timezoneLabel: 'Timezone',
      timezoneHint: 'Change this when you travel',
    },
    eventGenericType: 'Event',
    today: {
      noEvents: 'No events scheduled',
      eventsCount: (n) => `${n} event${n !== 1 ? 's' : ''} today`,
      emptyTitle: 'All clear today!',
      emptySub: 'Tap + to add an event',
      error: 'Could not load events. Please try again.',
      retry: 'Retry',
      signOutHint: 'If the problem persists, try signing out and signing back in.',
      slowConnection: 'Slow connection… still loading.',
      aiLimitReached: "You've used all 30 AI entries (voice & photo) of the Free plan this month. They reset next month.",
      weekList: 'List',
      weekGrid: 'Grid',
      conflictWarning: 'Heads up: this overlaps with “{title}” ({time}). Save anyway?',
      tomorrow: 'Tomorrow',
      today: 'Today',
      yesterday: 'Yesterday',
      snooze: 'Snooze',
      stop: 'Stop',
      prevWeek: 'Previous week',
      nextWeek: 'Next week',
      microphone: 'Microphone',
      noEventsDay: 'No events scheduled',
      emptyTitleDay: 'Nothing here',
      eventsCountDay: (n) => `${n} event${n !== 1 ? 's' : ''}`,
      prevDay: 'Previous day',
      nextDay: 'Next day',
      filterAll: 'All',
      filterMine: 'Only me',
      ttsButton: 'Listen',
      ttsStop: 'Stop',
      ttsNoEvents: 'No events today.',
      ttsIntro: 'Today you have {n} events',
      viewDay: 'Day',
      viewWeek: 'Week',
      viewMonth: 'Month',
      viewYear: 'Year',
      viewSlots: 'Plan',
      slotsHint: 'Tap a free slot to add an event',
      slotsTapDay: 'Tap a day to open it',
      slotsFree: 'Free',
      slotsBusy: 'Busy',
      weekOf: 'Week',
    },
    tasks: {
      emptyTitle: 'No notes yet',
      emptySub: 'e.g. Buy bread · Call the dentist',
      addPlaceholder: 'Write a note…',
      doneSection: "Done",
      deleteNoteConfirm: "Delete this note permanently?",
      restoreNote: "Tap to restore",
      completed: 'completed',
      noScope: 'Sign out and back in to enable notes',
      deleteConfirm: 'Delete this note?',
    },
    settings: {
      pageTitle: 'Settings',
      account: 'Account',
      signOut: 'Sign out',
      themeColor: "App theme color",
      themeColorBtn: "App color",
      a11yReminders: "Accessible reminders (vibration + voice)",
      a11yRemindersHint: "At reminder time the phone vibrates and reads the event aloud.",
      a11yMode: "Accessibility mode",
      a11yModeHint: "Turns on high contrast and vibrating/spoken reminders together.",
      a11yVibration: "Vibration",
      a11ySpeak: "Read event aloud",
      a11yContrast: "High contrast",
      usageTab: "Data usage",
      a11yTab: "Accessibility",
      updateAvailable: "A new version is available.",
      updateButton: "Update",
      bigText: "Large text",
      bigTextOn: "on",
      bigTextOff: "off",
      bigLarge: "Large",
      bigXLarge: "Extra large",
      feedTitle: "See MeMo in Apple Calendar",
      feedDesc: "For iPhone and iCloud: your MeMo plans, inside Apple Calendar.",
      feedCopy: "Copy link",
      feedCopied: "Copied!",
      feedSubscribe: "Show in my calendar",
      feedHint: "iPhone: Settings → Calendar → Accounts → Add → Other → Add Subscribed Calendar. Mac: File → New Calendar Subscription.",
      feedConnect: "Connect your calendars",
      feedRegenerate: "Regenerate link",
      saved: '✓ Settings saved!',
      saveError: 'Failed to save settings. Please try again.',
      language: 'Language',
      telegramSection: 'Daily Telegram Summary',
      sendTime: 'Send time',
      timezone: 'Your timezone',
      tzDetected: 'Your device is in',
      tzUseDetected: 'Use this timezone',
      tzAutoDetect: 'Auto-detect from my device',
      chatId: 'Telegram Chat ID',
      voicePitch: 'Pitch',
      voicePitchLow: 'Low',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'High',
      pushIntro: 'Notifications reach your phone even when the app is closed. Tap to enable and test.',
      pushDenied: 'Permission denied. Enable notifications for MeMo in your phone/browser settings.',
      pushUnsupported: 'This device does not support push. On iPhone, install MeMo to the Home screen.',
      pushUnsupportedApp: 'In the installed app the reminders come from the phone itself, not from the browser: just make sure MeMo is allowed to show notifications. Telegram and email are extras, if you want them.',
      pushFailed: 'Failed. Try again or check notification settings.',
      pushSending: 'Sending…',
      pushTestButton: '🔔 Enable / Send test',
      pushSent: '✓ Sent! Check your device.',
      pushError: 'Error — enable notifications in the browser.',
      pushNoDevice: 'No device reached. Try again, or turn MeMo notifications off and on in your browser settings.',
      chatIdPlaceholder: 'e.g. 123456789',
      chatIdHelp: (bot) => `How to get your Chat ID: open Telegram, find @${bot}, send it any message, then tap the link in the setup guide.`,
      testSending: 'Sending...',
      testButton: '📨 Send test message now',
      testSuccess: '✓ Message sent! Check your Telegram.',
      testError: 'Failed to send. Make sure your Chat ID is saved.',
      saving: 'Saving...',
      saveButton: 'Save Settings',
      telegramSetupTitle: 'How to connect Telegram:',
      telegramStep1: (bot) => `Open Telegram and search for @${bot}`,
      telegramStep2: 'Send it any message to start',
      telegramStep3: 'Open @userinfobot → send /start → copy your Chat ID',
      telegramStep4: 'Paste it in the field below and save',
      supportTitle: "Help & support",
      supportDesc: "Need help or want to report a problem? Write to us — we'll reply as soon as we can.",
      emailSupport: "Email us",
      contactSupport: 'Contact support on Telegram',
      themeSection: 'Appearance',
      themeLabel: 'Theme',
      themeLight: 'Light',
      themeDark: 'Dark',
      countryLabel: 'Country',
      subCalendarsSection: 'Labels',
      subCalendarName: 'Name',
      subCalendarAdd: 'Add label',
      subCalendarOwn: 'Colour my own events',
      subCalendarPickColour: 'Choose a colour',
      subCalendarAutoHint: 'Labels for calendars that are not yours get a temporary colour — tap one to choose your own.',
      subCalendarDeleteConfirm: 'Delete this label? Events will not be deleted.',
      summarySection: 'Morning Summary',
      summaryNowhere: "The summary is not going anywhere: MeMo has nowhere to send it. Add your email below, or connect Telegram.",
      summaryChannels: 'Where to send it',
      viaTelegram: 'Send via Telegram',
      viaEmail: 'Send via email',
      emailAnyProvider: 'Works with any email address — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Email Summary',
      emailLabel: 'Your email address',
      emailHelp: 'Enter any email address — Gmail, Outlook, iCloud, work email, etc.',
      emailTestSending: '⏳ Sending...',
      emailTestButton: '📧 Send test email',
      emailTestSuccess: '✓ Email sent! Check your inbox.',
      emailTestError: 'Failed to send email. Check server configuration.',
      voiceLabel: 'Reading voice',
      voiceDefault: 'System default',
      voiceTest: '▶ Test voice',
      voiceSection: "Voice & read-aloud",
      voiceSpeed: "Speed",
      voiceSlow: "Slow",
      voiceFast: "Fast",
      voiceNoVoices: 'No voices available for this language on this device.',
      voiceFemale: '♀ Female',
      voiceMale: '♂ Male',
      voiceNoMatch: 'No matching voice found — using system default.',
      alarmSection: 'Notification Settings',
      alarmVolume: 'Volume',
      alarmSnooze: 'Snooze duration',
      alarmMaxSnooze: 'Max snooze count',
      alarmForever: 'Forever',
      alarmMin: 'min',
      alarmSound: 'Sound',
      weatherReset: 'Weather',
      weatherActive: 'Weather active',
      weatherDisable: 'Disable',
      weatherDesc: 'Enable to see temperature and forecast on the main screen.',
      weatherActivating: 'Requesting…',
      weatherEnable: 'Enable weather',
      weatherReEnable: 'Re-enable weather',
      weatherDeniedHint: 'Location access blocked by the browser. Go to browser settings to unblock it, then try again.',
      weatherDeniedHintApp: "Location access was refused. Allow it for MeMo in your phone settings, then try again.",
      weatherUnavailable: 'GPS not available on this device.',
      exportLabel: 'Connect Apple Calendar, Outlook or any other non-Google app',
      exportButton: 'Download .ics',
      exportHint: 'Import into Apple Calendar, Outlook or any other app',
      exporting: 'Exporting…',
    },
    form: {
      allDay: "All day",
      timeMissing: "Pick a time, or turn on “All day”.",
      pageTitle: 'New Event',
      required: 'Please fill in all required fields.',
      saveError: 'Failed to save event. Please try again.',
      titleLabel: 'Event',
      titlePlaceholder: 'e.g. Doctor appointment',
      cancelButton: 'Cancel',
      recurringEditTitle: 'Edit recurring event',
      recurringThisOnly: 'Only this event',
      recurringAllSeries: 'All events in series',
      recurringNote: 'Recurring event — recurrence changes apply to this occurrence only',
      photoLabel: 'Photo',
      openInMaps: 'Open in Google Maps',
      recurringEvent: 'Recurring event',
      typeLabel: 'Type *',
      typeDefault: 'Select type...',
      datetimeLabel: 'Date & Time',
      dateFromLabel: 'From',
      dateToLabel: 'Until',
      locationLabel: 'Location (optional)',
      locationPlaceholder: 'e.g. Via Roma 10, Milan',
      priorityLabel: 'Priority',
      priorities: [
        { value: 'red',    label: 'High',   sublabel: 'Must do' },
        { value: 'yellow', label: 'Medium', sublabel: 'Should do' },
        { value: 'green',  label: 'Low',    sublabel: 'Nice to do' },
      ],
      notesLabel: 'Notes (optional)',
      notesPlaceholder: 'Add notes, or use voice...',
      attLabel: 'Attachments',
      attAdd: '📎 Attach photo or PDF',
      attHint: 'Max 5 files, removed 5 days after the event',
      attTooMany: 'Max 5 attachments',
      attBadType: 'Only photos and PDF',
      attTooBig: 'File too large (max 10 MB)',
      saving: 'Saving...',
      saveButton: '✓ Save Event',
      editPageTitle: 'Edit Event',
      editButton: 'Edit',
      updateButton: '✓ Update',
      dictateLabel: 'SAY',
      deleteButton: 'Delete',
      deleteConfirm: 'Delete this event from Google Calendar?',
      deleteError: 'Error deleting event. Please try again.',
      recurrenceLabel: 'Recurrence',
      recurrenceNone: 'Does not repeat',
      recurrenceDaily: 'Every day',
      recurrenceWeekly: 'Every week',
      recurrenceMonthly: 'Every month',
      recurrenceYearly: 'Every year',
      recurrenceEnds: 'Remember',
      recurrenceNever: 'Forever',
      recurrenceAfter: 'After',
      recurrenceTimes: 'occurrences',
      recurrenceOnDate: 'Until',
      recurrenceEvery: 'Every',
      recurrenceLast: 'last',
      recurrenceMonthlyDay: 'Same day of the month',
      recUnitDays: 'days',
      recUnitWeeks: 'weeks',
      recUnitMonths: 'months',
      recUnitYears: 'years',
      recurrenceDays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      deleteRecurring: 'This is a recurring event.',
      deleteOnlyThis: 'Only this event',
      deleteAllSeries: 'All events in the series',
      voiceHint: 'TAP AND DICTATE: Event · Date\nTime · Location · Priority · Notes',
      parsing: 'Filling form from voice...',
      parseError: 'Could not understand the recording. Please fill in the fields manually.',
      imageHint: 'SCAN IMAGE OR PHOTO',
      imageParsing: 'Reading image...',
      imageError: 'Could not read the image. Please fill in the fields manually.',
      multiTitle: "Events found",
      multiSubtitle: "Check, edit and create.",
      multiCreate: "Create all",
      multiNoLabel: "None",
      multiCreating: "Creating…",
      multiDetails: "Location & notes",
      overlapTitle: "Overlap",
      overlapText: "Overlaps «{title}» ({time}). You can still save.",
      subCalendarLabel: 'Label',
      subCalendarNone: 'None',
      visibilityLabel: 'Calendars',
      visibilityPrivate: 'Only me',
      visibilityGroup: 'Shared calendar',
      visibilitySelectGroup: 'Select group',
      conflictTitle: 'Schedule conflict',
      conflictHint: 'Busy at this time:',
      reminderLabel: 'Reminder',
      reminderNone: 'No reminder',
      reminderOn: '✓ Active',
      reminderOff: 'Enable',
      reminderOptions: [
        { value: 0,    label: 'At start time' },
        { value: 5,    label: '5 min before' },
        { value: 10,   label: '10 min before' },
        { value: 15,   label: '15 min before' },
        { value: 20,   label: '20 min before' },
        { value: 25,   label: '25 min before' },
        { value: 30,   label: '30 min before' },
        { value: 45,   label: '45 min before' },
        { value: 60,   label: '1 hour before' },
        { value: 120,  label: '2 hours before' },
        { value: 180,  label: '3 hours before' },
        { value: 720,  label: '12 hours before' },
        { value: 1440, label: '1 day before' },
        { value: 2880, label: '2 days before' },
        { value: 10080, label: '1 week before' },
      ],
    },
    groups: {
      pageTitle: 'Shared Calendars',
      noGroups: 'No calendars yet',
      noGroupsSub: 'Create a calendar or join with an invite code',
      createGroup: 'Create calendar',
      joinGroup: 'Join with code',
      planLimit: 'You\'ve reached your plan\'s shared-calendar limit. Upgrade to create more.',
      seePlans: 'See plans',
      calendarFull: 'This shared calendar has reached the owner plan limit.',
      groupNameLabel: 'Calendar name',
      groupNamePlaceholder: 'e.g. Smith Family Calendar',
      inviteCode: 'Invite code',
      inviteCodePlaceholder: 'e.g. A3F9B2',
      join: 'Join',
      create: 'Create',
      creating: 'Creating…',
      joining: 'Joining…',
      members: 'Members',
      you: '(you)',
      admin: 'Admin',
      member: 'Member',
      leave: 'Leave calendar',
      leaveConfirm: 'Leave this calendar?',
      delete: 'Delete calendar',
      deleteConfirm: 'Delete this calendar? All members will lose access.',
      shareInvite: 'Share code',
      copied: '✓ Code copied!',
      errorCreate: 'Could not create calendar. Please try again.',
      errorJoin: 'Invalid code. Please check and try again.',
      errorAlready: 'You are already a member of this calendar.',
      cancel: 'Cancel',
      saving: 'Saving…',
    },
    install: {
      title: '📲 Install MeMo',
      subtitle: 'Install it like a real app — no App Store needed.',
      installBtn: '📲 Install on this device',
      installed: '✓ Installed!',
      oneLogin: '🔐 Log in once — Memo remembers you forever.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Open this link in Safari', 'Tap the Share button □↑ at the bottom', 'Tap "Add to Home Screen"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Open this link in Chrome', 'Tap ⋮ in the top right', 'Tap "Add to Home Screen"'],
      },
      desktop: {
        title: '💻 Computer (Chrome)',
        steps: ['Open this link in Chrome', 'Click the 📲 icon in the address bar', 'Click "Install"'],
      },
    },
  },

  es: {
    nav: { today: 'Hoy', add: 'Agregar', tasks: 'Notas', search: 'Buscar', settings: 'Ajustes', groups: 'Grupos' },
    search: {
      placeholder: 'Buscar eventos…',
      searching: 'Buscando…',
      noResults: 'Sin resultados para',
      noResultsHint: 'Prueba con otras palabras',
      hint: 'ej. «dentista», «reunión», «cumpleaños»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Error de acceso',
      subtitle: 'Algo salió mal. Por favor, inténtalo de nuevo.',
      back: 'Volver',
    },
    login: {
      title: 'Memo',
      subtitle: 'Tu gestor personal de notas y eventos con entrada de voz',
      features: [
        'Agrega eventos por voz mientras conduces',
        'Prioriza con colores',
        'Sincroniza con Google Calendar',
        'Resumen diario por Telegram a las 7:00',
      ],
      signIn: 'Entrar con Google',
    },
    onboarding: {
      demo: {
        views: ["Día", "Sem.", "Mes", "Año"],
        allDay: "Todo el día",
        prio: ["Alta", "Media", "Baja"],
        labels: ["TRABAJO", "DEPORTE", "CASA"],
        shared: "Compartido",
        create: "Crear",
        join: "Unirse con código",
        sharedTitle: "Calendarios compartidos",
        searchTitle: "Buscar eventos",
        searchHint: "ej. dentista, reunión…",
        notePh: "Escribe una nota…",
        settingsTitle: "Ajustes",
        light: "Claro",
        dark: "Oscuro",
        signout: "Salir",
        ics: "Descargar .ics",
        notif: "Notificaciones",
        summaryLbl: "Resumen",
        time: "Hora",
        telegram: "Enviar por Telegram",
        reminder: "30 min antes",
        greeting: "Buenos días",
        repeat: ["Cada día", "Cada sem."],
        ev: ["Reunión", "Gimnasio", "Compra", "Dentista", "Cena", "Entrega"],
      },
      skip: "Saltar",
      next: "Siguiente →",
      start: "Empezar",
      slides: [
        { key: "calendar", title: "El calendario que te habla", text: "Día, semana, mes y año: mira tus planes como prefieras. Y con un toque te lee en voz alta lo del día y de la semana." },
        { key: "add", title: "Añádelo como quieras", text: "Crea un evento escribiendo, con la voz o con una foto: fotografía una hoja, un correo o un cartel y la IA lo rellena todo. También puedes adjuntar documentos al evento." },
        { key: "planner", title: "Organiza tu día", text: "El planificador diario y semanal muestra huecos libres y ocupados: encajas todo de un vistazo." },
        { key: "multi", title: "Busca, anota, comparte", text: "Encuentra cualquier evento en un segundo, ten tus notas siempre a mano y comparte el calendario con la familia o el equipo, cada uno con su color." },
        { key: "summary", title: "Cada mañana, tu resumen", text: "Recibe los planes del día en Telegram y por correo, ordenados por hora y prioridad. Empieza el día sin olvidar nada." },
        { key: "settings", title: "Haz que MeMo trabaje para ti", text: "Elige idioma, hora del resumen, notificaciones, tono y tema: MeMo se adapta a cómo trabajas." },
      ],
    },

    welcome: {
      greeting: 'Bienvenido',
      subtitle: 'Elige tu idioma para comenzar',
      saving: 'Guardando...',
      continue: 'Continuar →',
      countryLabel: 'Tu país',
      countryPlaceholder: 'Selecciona tu país...',
      themeLabel: 'Elige tu tema',
      timezoneLabel: 'Zona horaria',
      timezoneHint: 'Cámbialo cuando viajes',
    },
    eventGenericType: 'Evento',
    today: {
      noEvents: 'Sin eventos hoy',
      eventsCount: (n) => `${n} evento${n !== 1 ? 's' : ''} hoy`,
      emptyTitle: '¡Todo despejado hoy!',
      emptySub: 'Toca + para agregar un evento',
      error: 'No se pudieron cargar los eventos. Por favor, inténtalo de nuevo.',
      retry: 'Reintentar',
      signOutHint: 'Si el problema persiste, intenta cerrar sesión y volver a entrar.',
      slowConnection: 'Conexión lenta… seguimos cargando.',
      aiLimitReached: 'Has usado los 30 registros con IA (voz y foto) del plan Free este mes. Se reinician el próximo mes.',
      weekList: 'Lista',
      weekGrid: 'Cuadrícula',
      conflictWarning: 'Atención: se solapa con «{title}» ({time}). ¿Guardar igualmente?',
      tomorrow: 'Mañana',
      today: 'Hoy',
      yesterday: 'Ayer',
      snooze: 'Posponer',
      stop: 'Parar',
      prevWeek: 'Semana anterior',
      nextWeek: 'Semana siguiente',
      microphone: 'Micrófono',
      noEventsDay: 'Sin eventos',
      emptyTitleDay: 'Nada por aquí',
      eventsCountDay: (n) => `${n} evento${n !== 1 ? 's' : ''}`,
      prevDay: 'Día anterior',
      nextDay: 'Día siguiente',
      filterAll: 'Todo',
      filterMine: 'Solo yo',
      ttsButton: 'Escuchar',
      ttsStop: 'Stop',
      ttsNoEvents: 'Sin eventos hoy.',
      ttsIntro: 'Hoy tienes {n} eventos',
      viewDay: 'Día',
      viewWeek: 'Semana',
      viewMonth: 'Mes',
      viewYear: 'Año',
      viewSlots: 'Plan',
      slotsHint: 'Toca un espacio libre para añadir un evento',
      slotsTapDay: 'Toca un día para abrirlo',
      slotsFree: 'Libre',
      slotsBusy: 'Ocupado',
      weekOf: 'Semana',
    },
    tasks: {
      emptyTitle: 'Aún no hay notas',
      emptySub: 'ej. Comprar pan · Llamar al dentista',
      addPlaceholder: 'Escribe una nota…',
      doneSection: "Completadas",
      deleteNoteConfirm: "¿Eliminar esta nota definitivamente?",
      restoreNote: "Toca para restaurar",
      completed: 'completadas',
      noScope: 'Cierra sesión y vuelve a entrar para activar las notas',
      deleteConfirm: '¿Eliminar esta nota?',
    },
    settings: {
      pageTitle: 'Ajustes',
      account: 'Cuenta',
      signOut: 'Cerrar sesión',
      themeColor: "Color del tema de la app",
      themeColorBtn: "Color app",
      a11yReminders: "Recordatorios accesibles (vibración + voz)",
      a11yRemindersHint: "A la hora del recordatorio el teléfono vibra y lee el evento en voz alta.",
      a11yMode: "Modo de accesibilidad",
      a11yModeHint: "Activa a la vez alto contraste y recordatorios con vibración y voz.",
      a11yVibration: "Vibración",
      a11ySpeak: "Leer evento en voz alta",
      a11yContrast: "Alto contraste",
      usageTab: "Uso de datos",
      a11yTab: "Accesibilidad",
      updateAvailable: "Hay una nueva versión disponible.",
      updateButton: "Actualizar",
      bigText: "Texto grande",
      bigTextOn: "activado",
      bigTextOff: "desactivado",
      bigLarge: "Grande",
      bigXLarge: "Muy grande",
      feedTitle: "Ver MeMo en el Calendario de Apple",
      feedDesc: "Para iPhone e iCloud: tus planes de MeMo, dentro del Calendario de Apple.",
      feedCopy: "Copiar enlace",
      feedCopied: "¡Copiado!",
      feedSubscribe: "Ver en mi calendario",
      feedHint: "iPhone: Ajustes → Calendario → Cuentas → Añadir → Otra → Añadir calendario suscrito. Mac: Archivo → Nueva suscripción a calendario.",
      feedConnect: "Conecta tus calendarios",
      feedRegenerate: "Regenerar enlace",
      saved: '✓ ¡Ajustes guardados!',
      saveError: 'Error al guardar ajustes. Por favor, inténtalo de nuevo.',
      language: 'Idioma',
      telegramSection: 'Resumen Diario por Telegram',
      sendTime: 'Hora de envío',
      timezone: 'Tu zona horaria',
      tzDetected: 'Tu dispositivo está en',
      tzUseDetected: 'Usar esta zona horaria',
      tzAutoDetect: 'Detectar automáticamente',
      chatId: 'ID de chat de Telegram',
      voicePitch: 'Tono',
      voicePitchLow: 'Grave',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Agudo',
      pushIntro: 'Las notificaciones llegan al teléfono incluso con la app cerrada. Toca para activarlas y probarlas.',
      pushDenied: 'Permiso denegado. Activa las notificaciones de MeMo en los ajustes del teléfono/navegador.',
      pushUnsupported: 'Este dispositivo no admite notificaciones push. En iPhone, instala MeMo en la pantalla de inicio.',
      pushUnsupportedApp: 'En la app instalada los recordatorios llegan del propio teléfono, no del navegador: solo asegúrate de haber dado a MeMo permiso para mostrar notificaciones. Telegram y el correo son un extra, si los quieres.',
      pushFailed: 'Falló. Inténtalo de nuevo o revisa los ajustes de notificaciones.',
      pushSending: 'Enviando…',
      pushTestButton: '🔔 Activar / Enviar prueba',
      pushSent: '✓ ¡Enviada! Revisa tu dispositivo.',
      pushError: 'Error — activa las notificaciones en el navegador.',
      pushNoDevice: 'No se alcanzó ningún dispositivo. Inténtalo de nuevo o desactiva y reactiva las notificaciones de MeMo en los ajustes del navegador.',
      chatIdPlaceholder: 'ej. 123456789',
      chatIdHelp: (bot) => `Cómo obtener tu Chat ID: abre Telegram, busca @${bot}, envíale un mensaje, luego toca el enlace en la guía de configuración.`,
      testSending: 'Enviando...',
      testButton: '📨 Enviar mensaje de prueba',
      testSuccess: '✓ ¡Mensaje enviado! Revisa tu Telegram.',
      testError: 'Error al enviar. Asegúrate de que tu Chat ID esté guardado.',
      saving: 'Guardando...',
      saveButton: 'Guardar Ajustes',
      telegramSetupTitle: 'Cómo conectar Telegram:',
      telegramStep1: (bot) => `Abre Telegram y busca @${bot}`,
      telegramStep2: 'Envíale cualquier mensaje para empezar',
      telegramStep3: 'Abre @userinfobot → envía /start → copia tu Chat ID',
      telegramStep4: 'Pégalo en el campo de abajo y guarda',
      supportTitle: "Ayuda y soporte",
      supportDesc: "¿Necesitas ayuda o quieres informar de un problema? Escríbenos, responderemos lo antes posible.",
      emailSupport: "Escríbenos por email",
      contactSupport: 'Contactar con soporte en Telegram',
      themeSection: 'Apariencia',
      themeLabel: 'Tema',
      themeLight: 'Claro',
      themeDark: 'Oscuro',
      countryLabel: 'País',
      subCalendarsSection: 'Etiquetas',
      subCalendarName: 'Nombre',
      subCalendarAdd: 'Agregar etiqueta',
      subCalendarOwn: 'Colorear mis eventos',
      subCalendarPickColour: 'Elige un color',
      subCalendarAutoHint: 'Las etiquetas de los calendarios que no son tuyos reciben un color provisional: tócalas para elegir el tuyo.',
      subCalendarDeleteConfirm: '¿Eliminar esta etiqueta? Los eventos no se eliminarán.',
      summarySection: 'Resumen Matutino',
      summaryNowhere: "El resumen no sale: MeMo no sabe adónde enviarlo. Escribe tu email aquí abajo, o conecta Telegram.",
      summaryChannels: 'Dónde enviarlo',
      viaTelegram: 'Enviar por Telegram',
      viaEmail: 'Enviar por email',
      emailAnyProvider: 'Compatible con cualquier dirección — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Resumen por Email',
      emailLabel: 'Tu dirección de email',
      emailHelp: 'Puedes usar cualquier email — Gmail, Outlook, iCloud, trabajo, etc.',
      emailTestSending: '⏳ Enviando...',
      emailTestButton: '📧 Enviar email de prueba',
      emailTestSuccess: '✓ ¡Email enviado! Revisa tu bandeja de entrada.',
      emailTestError: 'Error al enviar el email. Comprueba la configuración del servidor.',
      voiceLabel: 'Voz de lectura',
      voiceDefault: 'Predeterminada del sistema',
      voiceTest: '▶ Probar voz',
      voiceSection: "Voz y lectura",
      voiceSpeed: "Velocidad",
      voiceSlow: "Lenta",
      voiceFast: "Rápida",
      voiceNoVoices: 'No hay voces disponibles para este idioma en este dispositivo.',
      voiceFemale: '♀ Femenina',
      voiceMale: '♂ Masculina',
      voiceNoMatch: 'No se encontró una voz coincidente — usando la predeterminada.',
      alarmSection: 'Configuración de notificaciones',
      alarmVolume: 'Volumen',
      alarmSnooze: 'Duración de posposición',
      alarmMaxSnooze: 'Máx. posposiciones',
      alarmForever: 'Siempre',
      alarmMin: 'min',
      alarmSound: 'Sonido',
      weatherReset: 'Tiempo',
      weatherActive: 'Tiempo activo',
      weatherDisable: 'Desactivar',
      weatherDesc: 'Activa para ver la temperatura y el pronóstico en la pantalla principal.',
      weatherActivating: 'Solicitando…',
      weatherEnable: 'Activar tiempo',
      weatherReEnable: 'Reactivar tiempo',
      weatherDeniedHint: 'Acceso a la ubicación bloqueado por el navegador. Ve a los ajustes del navegador para desbloquearlo y vuelve a intentarlo.',
      weatherDeniedHintApp: "Se denegó el acceso a la ubicación. Permítelo para MeMo en los ajustes del teléfono y vuelve a intentarlo.",
      weatherUnavailable: 'GPS no disponible en este dispositivo.',
      exportLabel: 'Conectar Apple Calendar, Outlook o cualquier otra app no Google',
      exportButton: 'Descargar .ics',
      exportHint: 'Importar en Apple Calendar, Outlook o cualquier otra app',
      exporting: 'Exportando…',
    },
    form: {
      allDay: "Todo el día",
      timeMissing: "Elige una hora, o activa «Todo el día».",
      pageTitle: 'Nuevo Evento',
      required: 'Por favor, completa todos los campos obligatorios.',
      saveError: 'Error al guardar el evento. Por favor, inténtalo de nuevo.',
      titleLabel: 'Evento',
      titlePlaceholder: 'ej. Cita con el médico',
      cancelButton: 'Cancelar',
      recurringEditTitle: 'Editar evento recurrente',
      recurringThisOnly: 'Solo este evento',
      recurringAllSeries: 'Todos los eventos de la serie',
      recurringNote: 'Evento recurrente — los cambios de recurrencia se aplican a esta ocurrencia',
      photoLabel: 'Foto',
      openInMaps: 'Abrir en Google Maps',
      recurringEvent: 'Evento recurrente',
      typeLabel: 'Tipo *',
      typeDefault: 'Seleccionar tipo...',
      datetimeLabel: 'Fecha y hora',
      dateFromLabel: 'Desde',
      dateToLabel: 'Hasta',
      locationLabel: 'Ubicación (opcional)',
      locationPlaceholder: 'ej. Calle Mayor 10, Madrid',
      priorityLabel: 'Prioridad',
      priorities: [
        { value: 'red',    label: 'Alta',    sublabel: 'Hay que hacer' },
        { value: 'yellow', label: 'Medio',   sublabel: 'Debería hacerse' },
        { value: 'green',  label: 'Bajo',    sublabel: 'Si hay tiempo' },
      ],
      notesLabel: 'Notas (opcional)',
      notesPlaceholder: 'Agrega notas o usa la voz...',
      attLabel: 'Adjuntos',
      attAdd: '📎 Adjuntar foto o PDF',
      attHint: 'Máx. 5 archivos, se borran 5 días después',
      attTooMany: 'Máx. 5 adjuntos',
      attBadType: 'Solo fotos y PDF',
      attTooBig: 'Archivo demasiado grande (máx. 10 MB)',
      saving: 'Guardando...',
      saveButton: '✓ Guardar Evento',
      editPageTitle: 'Editar Evento',
      editButton: 'Editar',
      updateButton: '✓ Actualizar',
      dictateLabel: 'DI',
      deleteButton: 'Eliminar',
      deleteConfirm: '¿Eliminar este evento de Google Calendar?',
      deleteError: 'Error al eliminar. Por favor, inténtalo de nuevo.',
      recurrenceLabel: 'Repetición',
      recurrenceNone: 'No se repite',
      recurrenceDaily: 'Cada día',
      recurrenceWeekly: 'Cada semana',
      recurrenceMonthly: 'Cada mes',
      recurrenceYearly: 'Cada año',
      recurrenceEnds: 'Recordar',
      recurrenceNever: 'Para siempre',
      recurrenceAfter: 'Después de',
      recurrenceTimes: 'veces',
      recurrenceOnDate: 'Hasta el',
      recurrenceEvery: 'Cada',
      recurrenceLast: 'último',
      recurrenceMonthlyDay: 'Mismo día del mes',
      recUnitDays: 'días',
      recUnitWeeks: 'semanas',
      recUnitMonths: 'meses',
      recUnitYears: 'años',
      recurrenceDays: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
      deleteRecurring: 'Este es un evento recurrente.',
      deleteOnlyThis: 'Solo este evento',
      deleteAllSeries: 'Todos los eventos de la serie',
      voiceHint: 'TOCA Y DICTA: Evento · Fecha\nHora · Lugar · Prioridad · Notas',
      parsing: 'Rellenando formulario con la voz...',
      parseError: 'No se pudo entender la grabación. Por favor, rellena los campos manualmente.',
      imageHint: 'ESCANEAR IMAGEN O FOTO',
      imageParsing: 'Leyendo imagen...',
      imageError: 'No se pudo leer la imagen. Por favor, rellena los campos manualmente.',
      multiTitle: "Eventos encontrados",
      multiSubtitle: "Revisa, corrige y crea.",
      multiCreate: "Crear todos",
      multiNoLabel: "Ninguna",
      multiCreating: "Creando…",
      multiDetails: "Ubicación y notas",
      overlapTitle: "Superposición",
      overlapText: "Se superpone con «{title}» ({time}). Puedes guardar igualmente.",
      subCalendarLabel: 'Etiqueta',
      subCalendarNone: 'Ninguno',
      visibilityLabel: 'Calendarios',
      visibilityPrivate: 'Solo yo',
      visibilityGroup: 'Calendario compartido',
      visibilitySelectGroup: 'Seleccionar grupo',
      conflictTitle: 'Conflicto de horario',
      conflictHint: 'Ocupados a esta hora:',
      reminderLabel: 'Recordatorio',
      reminderNone: 'Sin recordatorio',
      reminderOn: '✓ Activo',
      reminderOff: 'Activar',
      reminderOptions: [
        { value: 0,    label: 'Al inicio' },
        { value: 5,    label: '5 min antes' },
        { value: 10,   label: '10 min antes' },
        { value: 15,   label: '15 min antes' },
        { value: 20,   label: '20 min antes' },
        { value: 25,   label: '25 min antes' },
        { value: 30,   label: '30 min antes' },
        { value: 45,   label: '45 min antes' },
        { value: 60,   label: '1 hora antes' },
        { value: 120,  label: '2 horas antes' },
        { value: 180,  label: '3 horas antes' },
        { value: 720,  label: '12 horas antes' },
        { value: 1440, label: '1 día antes' },
        { value: 2880, label: '2 días antes' },
        { value: 10080, label: '1 semana antes' },
      ],
    },
    groups: {
      pageTitle: 'Calendarios compartidos',
      noGroups: 'Sin calendarios todavía',
      noGroupsSub: 'Crea un calendario o únete con un código de invitación',
      createGroup: 'Crear calendario',
      joinGroup: 'Unirse con código',
      planLimit: 'Has alcanzado el límite de calendarios compartidos de tu plan. Mejora tu plan para crear más.',
      seePlans: 'Ver planes',
      calendarFull: 'Este calendario compartido está completo para el plan del propietario.',
      groupNameLabel: 'Nombre del calendario',
      groupNamePlaceholder: 'ej. Familia García',
      inviteCode: 'Código de invitación',
      inviteCodePlaceholder: 'ej. A3F9B2',
      join: 'Unirse',
      create: 'Crear',
      creating: 'Creando…',
      joining: 'Uniéndose…',
      members: 'Miembros',
      you: '(tú)',
      admin: 'Admin',
      member: 'Miembro',
      leave: 'Abandonar grupo',
      leaveConfirm: '¿Abandonar este grupo?',
      delete: 'Eliminar grupo',
      deleteConfirm: '¿Eliminar este calendario? Todos los miembros perderán el acceso.',
      shareInvite: 'Compartir código',
      copied: '✓ ¡Código copiado!',
      errorCreate: 'No se pudo crear el grupo. Por favor, inténtalo de nuevo.',
      errorJoin: 'Código no válido. Por favor, comprueba e inténtalo de nuevo.',
      errorAlready: 'Ya eres miembro de este grupo.',
      cancel: 'Cancelar',
      saving: 'Guardando…',
    },
    install: {
      title: '📲 Instalar MeMo',
      subtitle: 'Instálalo como una app real — sin App Store.',
      installBtn: '📲 Instalar en este dispositivo',
      installed: '✓ ¡Instalado!',
      oneLogin: '🔐 Inicia sesión una vez — Memo te recuerda para siempre.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Abre este enlace en Safari', 'Toca Compartir □↑ abajo', 'Toca "Añadir a pantalla de inicio"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Abre este enlace en Chrome', 'Toca ⋮ en la esquina superior derecha', 'Toca "Añadir a pantalla de inicio"'],
      },
      desktop: {
        title: '💻 Ordenador (Chrome)',
        steps: ['Abre este enlace en Chrome', 'Haz clic en el icono 📲 de la barra de direcciones', 'Haz clic en "Instalar"'],
      },
    },
  },

  it: {
    nav: { today: 'Oggi', add: 'Aggiungi', tasks: 'Note', search: 'Cerca', settings: 'Impostazioni', groups: 'Calendari' },
    search: {
      placeholder: 'Cerca eventi…',
      searching: 'Ricerca in corso…',
      noResults: 'Nessun risultato per',
      noResultsHint: 'Prova con parole diverse',
      hint: 'es. «dentista», «riunione», «compleanno»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Accesso fallito',
      subtitle: 'Qualcosa è andato storto. Riprova.',
      back: 'Torna indietro',
    },
    login: {
      title: 'Memo',
      subtitle: 'Il tuo gestore personale di note ed eventi con input vocale',
      features: [
        'Aggiungi eventi a voce mentre guidi',
        'Dai priorità con i colori',
        'Sincronizza con Google Calendar',
        'Riepilogo giornaliero su Telegram alle 7:00',
      ],
      signIn: 'Accedi con Google',
    },
    onboarding: {
      demo: {
        views: ["Giorno", "Sett.", "Mese", "Anno"],
        allDay: "Tutto il giorno",
        prio: ["Alta", "Media", "Bassa"],
        labels: ["LAVORO", "SPORT", "CASA"],
        shared: "Condiviso",
        create: "Crea",
        join: "Unisciti con codice",
        sharedTitle: "Calendari condivisi",
        searchTitle: "Cerca eventi",
        searchHint: "es. dentista, riunione…",
        notePh: "Scrivi una nota…",
        settingsTitle: "Impostazioni",
        light: "Chiaro",
        dark: "Scuro",
        signout: "Esci",
        ics: "Scarica .ics",
        notif: "Notifiche",
        summaryLbl: "Riepilogo",
        time: "Orario",
        telegram: "Invio con Telegram",
        reminder: "30 min prima",
        greeting: "Buongiorno",
        repeat: ["Ogni giorno", "Ogni sett."],
        ev: ["Riunione", "Palestra", "Spesa", "Dentista", "Cena", "Consegna"],
      },
      skip: "Salta",
      next: "Avanti →",
      start: "Inizia",
      slides: [
        { key: "calendar", title: "Il calendario che ti parla", text: "Giorno, settimana, mese e anno: guarda i tuoi impegni come preferisci. E con un tocco puoi farti leggere gli impegni della giornata e della settimana ad alta voce." },
        { key: "add", title: "Aggiungi come vuoi", text: "Crea un evento scrivendo, con la voce o con una foto: fotografa un foglio, un'email o un cartellone e l'AI compila tutto. Puoi anche allegare documenti all'evento." },
        { key: "planner", title: "Organizza la tua giornata", text: "Il planner giornaliero e settimanale ti mostra spazi liberi e pieni: incastri gli impegni con un colpo d'occhio." },
        { key: "multi", title: "Cerca, annota, condividi", text: "Ritrovi ogni evento in un attimo, tieni le note sempre a portata e condividi il calendario con la famiglia o il team, ognuno con il suo colore." },
        { key: "summary", title: "Ogni mattina, il tuo riepilogo", text: "Ricevi gli impegni del giorno su Telegram e via email, ordinati per orario e priorità. Inizi la giornata senza dimenticare niente." },
        { key: "settings", title: "Fai lavorare MeMo per te", text: "Scegli lingua, orario del riepilogo, notifiche, suoneria e tema: MeMo si adatta a come lavori tu." },
      ],
    },

    welcome: {
      greeting: 'Benvenuto',
      subtitle: 'Scegli la tua lingua per iniziare',
      saving: 'Salvataggio...',
      continue: 'Continua →',
      countryLabel: 'Il tuo paese',
      countryPlaceholder: 'Seleziona il tuo paese...',
      themeLabel: 'Scegli il tema',
      timezoneLabel: 'Fuso orario',
      timezoneHint: 'Cambia il tuo orario quando viaggi',
    },
    eventGenericType: 'Evento',
    settings: {
      pageTitle: 'Impostazioni',
      account: 'Account',
      signOut: 'Esci',
      themeColor: "Colore tema app",
      themeColorBtn: "Colore app",
      a11yReminders: "Promemoria accessibili (vibrazione + voce)",
      a11yRemindersHint: "All'ora del promemoria il telefono vibra e legge l'evento ad alta voce.",
      a11yMode: "Modalità accessibilità",
      a11yModeHint: "Accende insieme alto contrasto e promemoria con vibrazione e voce.",
      a11yVibration: "Vibrazione",
      a11ySpeak: "Lettura evento a voce",
      a11yContrast: "Alto contrasto",
      usageTab: "Utilizzo dati",
      a11yTab: "Accessibilità",
      updateAvailable: "È disponibile una nuova versione.",
      updateButton: "Aggiorna",
      bigText: "Testo grande",
      bigTextOn: "attivo",
      bigTextOff: "disattivato",
      bigLarge: "Grande",
      bigXLarge: "Molto grande",
      feedTitle: "Vedere MeMo nel Calendario Apple",
      feedDesc: "Per iPhone e iCloud: i tuoi impegni di MeMo, dentro il Calendario di Apple.",
      feedCopy: "Copia link",
      feedCopied: "Copiato!",
      feedSubscribe: "Vedi nel mio calendario",
      feedHint: "iPhone: Impostazioni → Calendario → Account → Aggiungi → Altro → Calendario con iscrizione. Mac: File → Nuova iscrizione calendario.",
      feedConnect: "Collega i tuoi calendari",
      feedRegenerate: "Rigenera link",
      saved: '✓ Impostazioni salvate!',
      saveError: 'Errore nel salvare le impostazioni. Riprova.',
      language: 'Lingua',
      telegramSection: 'Riepilogo Giornaliero su Telegram',
      sendTime: 'Orario di invio',
      timezone: 'Il tuo fuso orario',
      tzDetected: 'Il tuo dispositivo è in',
      tzUseDetected: 'Usa questo fuso orario',
      tzAutoDetect: 'Rileva automaticamente',
      chatId: 'ID chat Telegram',
      voicePitch: 'Tonalità',
      voicePitchLow: 'Grave',
      voicePitchMid: 'Normale',
      voicePitchHigh: 'Acuta',
      pushIntro: 'Le notifiche arrivano sul telefono anche con l\'app chiusa. Tocca per attivarle e verificarle.',
      pushDenied: 'Permesso negato. Attiva le notifiche per MeMo nelle impostazioni del telefono/browser.',
      pushUnsupported: 'Questo dispositivo non supporta le notifiche. Su iPhone installa MeMo nella schermata Home.',
      pushUnsupportedApp: 'Nell’app installata i promemoria arrivano dal telefono stesso, non dal browser: assicurati solo di aver dato a MeMo il permesso di mostrare notifiche. Telegram ed email restano in più, se li vuoi.',
      pushFailed: 'Non riuscito. Riprova o controlla le notifiche nelle impostazioni.',
      pushSending: 'Invio…',
      pushTestButton: '🔔 Attiva / Invia notifica di prova',
      pushSent: '✓ Inviata! Controlla il dispositivo.',
      pushError: 'Errore — controlla che le notifiche siano attive nel browser.',
      pushNoDevice: 'Nessun dispositivo raggiunto. Riprova, oppure disattiva e riattiva le notifiche di MeMo nelle impostazioni del browser.',
      chatIdPlaceholder: 'es. 123456789',
      chatIdHelp: (bot) => `Come ottenere il tuo Chat ID: apri Telegram, trova @${bot}, invia un messaggio, poi tocca il link nella guida di configurazione.`,
      testSending: 'Invio in corso...',
      testButton: '📨 Invia messaggio di prova',
      testSuccess: '✓ Messaggio inviato! Controlla Telegram.',
      testError: "Invio fallito. Assicurati che l'ID chat sia salvato.",
      saving: 'Salvataggio...',
      saveButton: 'Salva Impostazioni',
      telegramSetupTitle: 'Come collegare Telegram:',
      telegramStep1: (bot) => `Apri Telegram e cerca @${bot}`,
      telegramStep2: 'Invia qualsiasi messaggio per avviare il bot',
      telegramStep3: 'Apri @userinfobot → invia /start → copia il tuo Chat ID',
      telegramStep4: 'Incollalo nel campo qui sotto e salva',
      supportTitle: "Aiuto e supporto",
      supportDesc: "Hai bisogno di aiuto o vuoi segnalare un problema? Scrivici, rispondiamo appena possibile.",
      emailSupport: "Scrivici via email",
      contactSupport: 'Contatta il supporto su Telegram',
      themeSection: 'Aspetto',
      themeLabel: 'Tema',
      themeLight: 'Chiaro',
      themeDark: 'Scuro',
      countryLabel: 'Paese',
      subCalendarsSection: 'Etichette',
      subCalendarName: 'Nome',
      subCalendarAdd: 'Aggiungi etichetta',
      subCalendarOwn: 'Colora i miei eventi',
      subCalendarPickColour: 'Scegli un colore',
      subCalendarAutoHint: 'Le etichette dei calendari che non sono tuoi prendono un colore provvisorio: toccale per sceglierlo tu.',
      subCalendarDeleteConfirm: 'Eliminare questa etichetta? Gli eventi non verranno eliminati.',
      summarySection: 'Riepilogo Mattutino',
      summaryNowhere: "Il riepilogo non parte: MeMo non sa dove mandartelo. Scrivi qui sotto la tua email, oppure collega Telegram.",
      summaryChannels: 'Dove inviarlo',
      viaTelegram: 'Invio con Telegram',
      viaEmail: 'Invio con email',
      emailAnyProvider: 'Funziona con qualsiasi indirizzo — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Riepilogo via Email',
      emailLabel: 'Il tuo indirizzo email',
      emailHelp: 'Puoi usare qualsiasi email — Gmail, Outlook, iCloud, lavoro, ecc.',
      emailTestSending: '⏳ Invio in corso...',
      emailTestButton: '📧 Invia email di test',
      emailTestSuccess: '✓ Email inviata! Controlla la tua inbox.',
      emailTestError: 'Errore invio email. Controlla la configurazione sul server.',
      voiceLabel: 'Voce di lettura',
      voiceDefault: 'Predefinita del sistema',
      voiceTest: '▶ Prova voce',
      voiceSection: "Voce e lettura",
      voiceSpeed: "Velocità",
      voiceSlow: "Lenta",
      voiceFast: "Veloce",
      voiceNoVoices: 'Nessuna voce disponibile per questa lingua su questo dispositivo.',
      voiceFemale: '♀ Femminile',
      voiceMale: '♂ Maschile',
      voiceNoMatch: 'Nessuna voce corrispondente trovata — uso quella predefinita.',
      alarmSection: 'Impostazioni notifica',
      alarmVolume: 'Volume',
      alarmSnooze: 'Durata posticipo',
      alarmMaxSnooze: 'Max posticipo',
      alarmForever: 'Per sempre',
      alarmMin: 'min',
      alarmSound: 'Suono',
      weatherReset: 'Meteo',
      weatherActive: 'Meteo attivo',
      weatherDisable: 'Disattiva',
      weatherDesc: 'Attiva per vedere la temperatura e le previsioni nella schermata principale.',
      weatherActivating: 'In attesa…',
      weatherEnable: 'Attiva meteo',
      weatherReEnable: 'Riattiva meteo',
      weatherDeniedHint: 'Accesso alla posizione bloccato dal browser. Vai nelle impostazioni del browser per sbloccarlo, poi riprova.',
      weatherDeniedHintApp: "Il permesso alla posizione è stato negato. Consentilo a MeMo nelle impostazioni del telefono, poi riprova.",
      weatherUnavailable: 'GPS non disponibile su questo dispositivo.',
      exportLabel: 'Collega calendario Apple, Outlook o qualsiasi altra app non Google',
      exportButton: 'Scarica .ics',
      exportHint: 'Importa in Apple Calendar, Outlook o qualsiasi altra app',
      exporting: 'Esportazione…',
    },
    today: {
      noEvents: 'Nessun evento in programma',
      eventsCount: (n) => `${n} event${n !== 1 ? 'i' : 'o'} oggi`,
      emptyTitle: 'Tutto libero oggi!',
      emptySub: 'Tocca + per aggiungere un evento',
      error: 'Impossibile caricare gli eventi. Riprova.',
      retry: 'Riprova',
      signOutHint: 'Se il problema persiste, prova a disconnetterti e riaccedere.',
      slowConnection: 'Connessione lenta… sto ancora caricando.',
      aiLimitReached: 'Hai usato tutti i 30 inserimenti AI (voce e foto) del piano Free di questo mese. Si azzerano il mese prossimo.',
      weekList: 'Lista',
      weekGrid: 'Griglia',
      conflictWarning: 'Attenzione: si sovrappone a «{title}» ({time}). Salvare comunque?',
      tomorrow: 'Domani',
      today: 'Oggi',
      yesterday: 'Ieri',
      snooze: 'Posticipa',
      stop: 'Ferma',
      prevWeek: 'Settimana precedente',
      nextWeek: 'Settimana successiva',
      microphone: 'Microfono',
      noEventsDay: 'Nessun evento in programma',
      emptyTitleDay: 'Niente qui',
      eventsCountDay: (n) => `${n} event${n !== 1 ? 'i' : 'o'}`,
      prevDay: 'Giorno precedente',
      nextDay: 'Giorno successivo',
      filterAll: 'Tutti',
      filterMine: 'Solo io',
      ttsButton: 'Ascolta',
      ttsStop: 'Stop',
      ttsNoEvents: 'Nessun evento oggi.',
      ttsIntro: 'Oggi hai {n} eventi',
      viewDay: 'Giorno',
      viewWeek: 'Settimana',
      viewMonth: 'Mese',
      viewYear: 'Anno',
      viewSlots: 'Plan',
      slotsHint: 'Tocca uno slot libero per aggiungere un evento',
      slotsTapDay: 'Tocca un giorno per aprirlo',
      slotsFree: 'Libero',
      slotsBusy: 'Occupato',
      weekOf: 'Settimana',
    },
    tasks: {
      emptyTitle: 'Ancora nessuna nota',
      emptySub: 'es. Comprare il pane · Chiamare il dentista',
      addPlaceholder: 'Scrivi una nota…',
      doneSection: "Eseguiti",
      deleteNoteConfirm: "Eliminare definitivamente questa nota?",
      restoreNote: "Tocca per ripristinare",
      completed: 'completate',
      noScope: 'Esci e rientra per attivare le note',
      deleteConfirm: 'Eliminare questa nota?',
    },
    form: {
      allDay: "Tutto il giorno",
      timeMissing: "Scegli un'ora, oppure attiva «Tutto il giorno».",
      pageTitle: 'Nuovo Evento',
      required: 'Compila tutti i campi obbligatori.',
      saveError: "Errore nel salvare l'evento. Riprova.",
      titleLabel: 'Evento',
      titlePlaceholder: 'es. Visita medica',
      cancelButton: 'Annulla',
      recurringEditTitle: 'Modifica evento ricorrente',
      recurringThisOnly: 'Solo questo evento',
      recurringAllSeries: 'Tutti gli eventi della serie',
      recurringNote: 'Evento ricorrente — le modifiche di ricorrenza qui si applicano a questa occorrenza',
      photoLabel: 'Foto',
      openInMaps: 'Apri in Google Maps',
      recurringEvent: 'Evento ricorrente',
      typeLabel: 'Tipo *',
      typeDefault: 'Seleziona tipo...',
      datetimeLabel: 'Data e ora',
      dateFromLabel: 'Da',
      dateToLabel: 'Fino a',
      locationLabel: 'Luogo (opzionale)',
      locationPlaceholder: 'es. Via Roma 10, Milano',
      priorityLabel: 'Priorità',
      priorities: [
        { value: 'red',    label: 'Alta',    sublabel: 'Da fare assolutamente' },
        { value: 'yellow', label: 'Media',   sublabel: 'Da fare appena possibile' },
        { value: 'green',  label: 'Bassa',   sublabel: 'Da fare' },
      ],
      notesLabel: 'Note (opzionale)',
      notesPlaceholder: 'Aggiungi note, o usa la voce...',
      attLabel: 'Allegati',
      attAdd: '📎 Allega foto o PDF',
      attHint: 'Max 5 file, rimossi 5 giorni dopo la data',
      attTooMany: 'Massimo 5 allegati',
      attBadType: 'Solo foto e PDF',
      attTooBig: 'File troppo grande (max 10 MB)',
      saving: 'Salvataggio...',
      saveButton: '✓ Salva Evento',
      editPageTitle: 'Modifica Evento',
      editButton: 'Modifica',
      updateButton: '✓ Aggiorna',
      dictateLabel: 'DETTA',
      deleteButton: 'Elimina',
      deleteConfirm: 'Eliminare questo evento da Google Calendar?',
      deleteError: "Errore nell'eliminazione. Riprova.",
      recurrenceLabel: 'Ripetizione',
      recurrenceNone: 'Non si ripete',
      recurrenceDaily: 'Ogni giorno',
      recurrenceWeekly: 'Ogni settimana',
      recurrenceMonthly: 'Ogni mese',
      recurrenceYearly: 'Ogni anno',
      recurrenceEnds: 'Ricorda',
      recurrenceNever: 'Per sempre',
      recurrenceAfter: 'Dopo',
      recurrenceTimes: 'volte',
      recurrenceOnDate: 'Fino al',
      recurrenceEvery: 'Ogni',
      recurrenceLast: 'ultimo',
      recurrenceMonthlyDay: 'Stesso giorno del mese',
      recUnitDays: 'giorni',
      recUnitWeeks: 'settimane',
      recUnitMonths: 'mesi',
      recUnitYears: 'anni',
      recurrenceDays: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
      deleteRecurring: 'Questo è un evento ricorrente.',
      deleteOnlyThis: 'Solo questo evento',
      deleteAllSeries: 'Tutti gli eventi della serie',
      voiceHint: 'TOCCA E DETTA: Evento · Data\nOra · Luogo · Priorità · Note',
      parsing: 'Compilazione modulo dalla voce...',
      parseError: 'Non ho capito la registrazione. Compila i campi manualmente.',
      imageHint: 'SCANSIONA IMMAGINE O FOTO',
      imageParsing: 'Lettura immagine...',
      imageError: 'Non sono riuscito a leggere l\'immagine. Compila i campi manualmente.',
      multiTitle: "Eventi trovati",
      multiSubtitle: "Controlla, correggi e crea.",
      multiCreate: "Crea tutti",
      multiNoLabel: "Nessuna",
      multiCreating: "Creazione…",
      multiDetails: "Luogo e note",
      overlapTitle: "Sovrapposizione",
      overlapText: "Si sovrappone a «{title}» ({time}). Puoi salvare comunque.",
      subCalendarLabel: 'Etichetta',
      subCalendarNone: 'Nessuno',
      visibilityLabel: 'Calendari',
      visibilityPrivate: 'Solo io',
      visibilityGroup: 'Calendario condiviso',
      visibilitySelectGroup: 'Seleziona gruppo',
      conflictTitle: 'Conflitto di orario',
      conflictHint: 'Occupati in questo orario:',
      reminderLabel: 'Promemoria',
      reminderNone: 'Nessun promemoria',
      reminderOn: '✓ Attivo',
      reminderOff: 'Attivo',
      reminderOptions: [
        { value: 0,    label: "All'inizio" },
        { value: 5,    label: '5 min prima' },
        { value: 10,   label: '10 min prima' },
        { value: 15,   label: '15 min prima' },
        { value: 20,   label: '20 min prima' },
        { value: 25,   label: '25 min prima' },
        { value: 30,   label: '30 min prima' },
        { value: 45,   label: '45 min prima' },
        { value: 60,   label: '1 ora prima' },
        { value: 120,  label: '2 ore prima' },
        { value: 180,  label: '3 ore prima' },
        { value: 720,  label: '12 ore prima' },
        { value: 1440, label: '1 giorno prima' },
        { value: 2880, label: '2 giorni prima' },
        { value: 10080, label: '1 settimana prima' },
      ],
    },
    groups: {
      pageTitle: 'Calendari condivisi',
      noGroups: 'Nessun calendario ancora',
      noGroupsSub: 'Crea un calendario o unisciti con un codice invito',
      createGroup: 'Crea calendario',
      joinGroup: 'Unisciti con codice',
      planLimit: 'Hai raggiunto il limite di calendari condivisi del tuo piano. Passa a un piano superiore per crearne altri.',
      seePlans: 'Scopri i piani',
      calendarFull: 'Questo calendario condiviso è al completo per il piano del proprietario.',
      groupNameLabel: 'Nome del calendario',
      groupNamePlaceholder: 'es. Casa Rossi',
      inviteCode: 'Codice invito',
      inviteCodePlaceholder: 'es. A3F9B2',
      join: 'Unisciti',
      create: 'Crea',
      creating: 'Creazione…',
      joining: 'Accesso…',
      members: 'Membri',
      you: '(tu)',
      admin: 'Admin',
      member: 'Membro',
      leave: 'Abbandona calendario',
      leaveConfirm: 'Abbandonare questo calendario?',
      delete: 'Elimina calendario',
      deleteConfirm: 'Eliminare questo calendario? Tutti i membri perderanno l\'accesso.',
      shareInvite: 'Condividi codice',
      copied: '✓ Codice copiato!',
      errorCreate: 'Impossibile creare il calendario. Riprova.',
      errorJoin: 'Codice non valido. Controlla e riprova.',
      errorAlready: 'Sei già membro di questo calendario.',
      cancel: 'Annulla',
      saving: 'Salvataggio…',
    },
    install: {
      title: '📲 Installa MeMo',
      subtitle: 'Installalo come una vera app — senza App Store.',
      installBtn: '📲 Installa su questo dispositivo',
      installed: '✓ Installato!',
      oneLogin: '🔐 Accedi una volta sola — Memo ti ricorda per sempre.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Apri questo link su Safari', 'Tocca il tasto Condividi □↑ in basso', 'Tocca "Aggiungi a schermata Home"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Apri questo link su Chrome', 'Tocca ⋮ in alto a destra', 'Tocca "Aggiungi a schermata Home"'],
      },
      desktop: {
        title: '💻 Computer (Chrome)',
        steps: ['Apri questo link su Chrome', 'Clicca sull\'icona 📲 nella barra degli indirizzi', 'Clicca "Installa"'],
      },
    },
  },
  de: {
    nav: { today: 'Heute', add: 'Neu', tasks: 'Notizen', search: 'Suchen', settings: 'Optionen', groups: 'Kalender' },
    search: {
      placeholder: 'Termine suchen…',
      searching: 'Suche läuft…',
      noResults: 'Keine Ergebnisse für',
      noResultsHint: 'Andere Suchbegriffe versuchen',
      hint: 'z. B. „Zahnarzt", „Meeting", „Geburtstag"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Anmeldung fehlgeschlagen',
      subtitle: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
      back: 'Zurück',
    },
    login: {
      title: 'Memo',
      subtitle: 'Dein persönlicher Notizen- und Terminmanager mit Spracheingabe',
      features: [
        'Termine per Sprache hinzufügen – auch beim Fahren',
        'Priorisieren mit Farben',
        'Synchronisiert mit Google Kalender',
        'Tägliche Telegram-Zusammenfassung um 7:00 Uhr',
      ],
      signIn: 'Mit Google anmelden',
    },
    onboarding: {
      demo: {
        views: ["Tag", "Woche", "Monat", "Jahr"],
        allDay: "Ganztägig",
        prio: ["Hoch", "Mittel", "Niedrig"],
        labels: ["ARBEIT", "SPORT", "ZUHAUSE"],
        shared: "Geteilt",
        create: "Erstellen",
        join: "Mit Code beitreten",
        sharedTitle: "Geteilte Kalender",
        searchTitle: "Termine suchen",
        searchHint: "z.B. Zahnarzt, Meeting…",
        notePh: "Notiz schreiben…",
        settingsTitle: "Einstellungen",
        light: "Hell",
        dark: "Dunkel",
        signout: "Abmelden",
        ics: ".ics laden",
        notif: "Mitteilungen",
        summaryLbl: "Übersicht",
        time: "Uhrzeit",
        telegram: "Per Telegram senden",
        reminder: "30 Min vorher",
        greeting: "Guten Morgen",
        repeat: ["Täglich", "Wöchentl."],
        ev: ["Meeting", "Sport", "Einkauf", "Zahnarzt", "Abendessen", "Lieferung"],
      },
      skip: "Überspringen",
      next: "Weiter →",
      start: "Los geht's",
      slides: [
        { key: "calendar", title: "Der Kalender, der mit dir spricht", text: "Tag, Woche, Monat und Jahr: sieh deine Termine, wie du willst. Und mit einem Tippen liest er dir Tag und Woche laut vor." },
        { key: "add", title: "Füg es hinzu, wie du willst", text: "Erstelle einen Termin durch Tippen, per Sprache oder mit einem Foto: fotografiere einen Zettel, eine E-Mail oder ein Plakat, und die KI trägt alles ein. Du kannst auch Dokumente anhängen." },
        { key: "planner", title: "Organisiere deinen Tag", text: "Der Tages- und Wochenplaner zeigt freie und belegte Zeiten: alles auf einen Blick eingeplant." },
        { key: "multi", title: "Suchen, notieren, teilen", text: "Finde jeden Termin in Sekunden, hab deine Notizen immer griffbereit und teile den Kalender mit Familie oder Team – jeder in seiner Farbe." },
        { key: "summary", title: "Jeden Morgen deine Übersicht", text: "Erhalte die Termine des Tages per Telegram und E-Mail, nach Zeit und Priorität sortiert. Starte den Tag, ohne etwas zu vergessen." },
        { key: "settings", title: "Lass MeMo für dich arbeiten", text: "Wähle Sprache, Uhrzeit der Übersicht, Benachrichtigungen, Klingelton und Design: MeMo passt sich an, wie du arbeitest." },
      ],
    },

    welcome: {
      greeting: 'Willkommen',
      subtitle: 'Wähle deine Sprache, um zu starten',
      saving: 'Speichern...',
      continue: 'Weiter →',
      countryLabel: 'Dein Land',
      countryPlaceholder: 'Land auswählen...',
      themeLabel: 'Design wählen',
      timezoneLabel: 'Zeitzone',
      timezoneHint: 'Ändere dies, wenn du reist',
    },
    eventGenericType: 'Termin',
    today: {
      noEvents: 'Keine Termine geplant',
      eventsCount: (n) => `${n} Termin${n !== 1 ? 'e' : ''} heute`,
      emptyTitle: 'Heute alles frei!',
      emptySub: 'Tippe auf + um einen Termin hinzuzufügen',
      error: 'Termine konnten nicht geladen werden. Bitte versuche es erneut.',
      retry: 'Erneut versuchen',
      signOutHint: 'Falls das Problem weiterhin besteht, versuche dich ab- und wieder anzumelden.',
      slowConnection: 'Langsame Verbindung… wird noch geladen.',
      aiLimitReached: 'Du hast die 30 KI-Einträge (Sprache & Foto) des Free-Plans für diesen Monat aufgebraucht. Sie werden nächsten Monat zurückgesetzt.',
      weekList: 'Liste',
      weekGrid: 'Raster',
      conflictWarning: 'Achtung: Überschneidung mit „{title}“ ({time}). Trotzdem speichern?',
      tomorrow: 'Morgen',
      today: 'Heute',
      yesterday: 'Gestern',
      snooze: 'Schlummern',
      stop: 'Stopp',
      prevWeek: 'Vorherige Woche',
      nextWeek: 'Nächste Woche',
      microphone: 'Mikrofon',
      noEventsDay: 'Keine Termine',
      emptyTitleDay: 'Nichts hier',
      eventsCountDay: (n) => `${n} Termin${n !== 1 ? 'e' : ''}`,
      prevDay: 'Vorheriger Tag',
      nextDay: 'Nächster Tag',
      filterAll: 'Alle',
      filterMine: 'Nur ich',
      ttsButton: 'Vorlesen',
      ttsStop: 'Stopp',
      ttsNoEvents: 'Keine Termine heute.',
      ttsIntro: 'Heute hast du {n} Termine',
      viewDay: 'Tag',
      viewWeek: 'Woche',
      viewMonth: 'Monat',
      viewYear: 'Jahr',
      viewSlots: 'Plan',
      slotsHint: 'Tippe auf einen freien Slot für ein neues Ereignis',
      slotsTapDay: 'Tippe auf einen Tag, um ihn zu öffnen',
      slotsFree: 'Frei',
      slotsBusy: 'Belegt',
      weekOf: 'Woche',
    },
    settings: {
      pageTitle: 'Einstellungen',
      account: 'Konto',
      signOut: 'Abmelden',
      themeColor: "App-Themenfarbe",
      themeColorBtn: "App-Farbe",
      a11yReminders: "Barrierefreie Erinnerungen (Vibration + Stimme)",
      a11yRemindersHint: "Zur Erinnerungszeit vibriert das Telefon und liest das Ereignis vor.",
      a11yMode: "Barrierefreiheitsmodus",
      a11yModeHint: "Aktiviert zusammen hohen Kontrast und Erinnerungen mit Vibration und Stimme.",
      a11yVibration: "Vibration",
      a11ySpeak: "Termin vorlesen",
      a11yContrast: "Hoher Kontrast",
      usageTab: "Datennutzung",
      a11yTab: "Barrierefreiheit",
      updateAvailable: "Eine neue Version ist verfügbar.",
      updateButton: "Aktualisieren",
      bigText: "Großer Text",
      bigTextOn: "ein",
      bigTextOff: "aus",
      bigLarge: "Groß",
      bigXLarge: "Sehr groß",
      feedTitle: "MeMo im Apple Kalender sehen",
      feedDesc: "Für iPhone und iCloud: deine MeMo-Termine, im Apple Kalender.",
      feedCopy: "Link kopieren",
      feedCopied: "Kopiert!",
      feedSubscribe: "In meinem Kalender anzeigen",
      feedHint: "iPhone: Einstellungen → Kalender → Accounts → Hinzufügen → Andere → Kalenderabo hinzufügen. Mac: Ablage → Neues Kalenderabonnement.",
      feedConnect: "Kalender verbinden",
      feedRegenerate: "Link neu erzeugen",
      saved: '✓ Einstellungen gespeichert!',
      saveError: 'Fehler beim Speichern. Bitte versuche es erneut.',
      language: 'Sprache',
      telegramSection: 'Tägliche Telegram-Zusammenfassung',
      sendTime: 'Sendezeit',
      timezone: 'Deine Zeitzone',
      tzDetected: 'Dein Gerät ist in',
      tzUseDetected: 'Diese Zeitzone verwenden',
      tzAutoDetect: 'Automatisch erkennen',
      chatId: 'Telegram Chat-ID',
      voicePitch: 'Tonhöhe',
      voicePitchLow: 'Tief',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Hoch',
      pushIntro: 'Benachrichtigungen kommen auch bei geschlossener App aufs Handy. Tippe zum Aktivieren und Testen.',
      pushDenied: 'Berechtigung verweigert. Aktiviere die Benachrichtigungen für MeMo in den Handy-/Browser-Einstellungen.',
      pushUnsupported: 'Dieses Gerät unterstützt keine Push-Benachrichtigungen. Auf dem iPhone MeMo zum Home-Bildschirm hinzufügen.',
      pushUnsupportedApp: 'In der installierten App kommen die Erinnerungen vom Telefon selbst, nicht vom Browser: achte nur darauf, dass MeMo Mitteilungen zeigen darf. Telegram und E-Mail sind zusätzlich, wenn du magst.',
      pushFailed: 'Fehlgeschlagen. Versuche es erneut oder prüfe die Benachrichtigungseinstellungen.',
      pushSending: 'Senden…',
      pushTestButton: '🔔 Aktivieren / Test senden',
      pushSent: '✓ Gesendet! Prüfe dein Gerät.',
      pushError: 'Fehler — aktiviere die Benachrichtigungen im Browser.',
      pushNoDevice: 'Kein Gerät erreicht. Versuche es erneut oder schalte die MeMo-Benachrichtigungen in den Browser-Einstellungen aus und wieder ein.',
      chatIdPlaceholder: 'z.B. 123456789',
      chatIdHelp: (bot) => `So bekommst du deine Chat-ID: Öffne Telegram, suche @${bot}, schreibe eine Nachricht, dann tippe den Link in der Einrichtungsanleitung.`,
      testSending: 'Senden...',
      testButton: '📨 Testnachricht jetzt senden',
      testSuccess: '✓ Nachricht gesendet! Überprüfe Telegram.',
      testError: 'Senden fehlgeschlagen. Stelle sicher, dass deine Chat-ID gespeichert ist.',
      saving: 'Speichern...',
      saveButton: 'Einstellungen speichern',
      telegramSetupTitle: 'So verbindest du Telegram:',
      telegramStep1: (bot) => `Öffne Telegram und suche @${bot}`,
      telegramStep2: 'Schreibe eine Nachricht, um zu starten',
      telegramStep3: 'Öffne @userinfobot → sende /start → kopiere deine Chat-ID',
      telegramStep4: 'Füge sie in das Feld unten ein und speichere',
      supportTitle: "Hilfe & Support",
      supportDesc: "Brauchst du Hilfe oder möchtest ein Problem melden? Schreib uns – wir antworten so schnell wie möglich.",
      emailSupport: "E-Mail schreiben",
      contactSupport: 'Support auf Telegram kontaktieren',
      themeSection: 'Erscheinungsbild',
      themeLabel: 'Design',
      themeLight: 'Hell',
      themeDark: 'Dunkel',
      countryLabel: 'Land',
      subCalendarsSection: 'Labels',
      subCalendarName: 'Name',
      subCalendarAdd: 'Label hinzufügen',
      subCalendarOwn: 'Meine Termine einfärben',
      subCalendarPickColour: 'Wähle eine Farbe',
      subCalendarAutoHint: 'Labels für Kalender, die nicht deine sind, bekommen eine vorläufige Farbe — tippe darauf, um selbst zu wählen.',
      subCalendarDeleteConfirm: 'Dieses Label löschen? Termine werden nicht gelöscht.',
      summarySection: 'Morgendliche Zusammenfassung',
      summaryNowhere: "Die Übersicht geht nirgendwohin: MeMo weiß nicht, wohin damit. Trag unten deine E-Mail ein oder verbinde Telegram.",
      summaryChannels: 'Wohin senden',
      viaTelegram: 'Über Telegram senden',
      viaEmail: 'Per E-Mail senden',
      emailAnyProvider: 'Funktioniert mit jeder E-Mail-Adresse — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'E-Mail-Zusammenfassung',
      emailLabel: 'Deine E-Mail-Adresse',
      emailHelp: 'Beliebige E-Mail-Adresse — Gmail, Outlook, iCloud, Arbeit usw.',
      emailTestSending: '⏳ Senden...',
      emailTestButton: '📧 Test-E-Mail senden',
      emailTestSuccess: '✓ E-Mail gesendet! Überprüfe deinen Posteingang.',
      emailTestError: 'Fehler beim Senden der E-Mail. Überprüfe die Serverkonfiguration.',
      voiceLabel: 'Lesestimme',
      voiceDefault: 'Systemstandard',
      voiceTest: '▶ Stimme testen',
      voiceSection: "Stimme & Vorlesen",
      voiceSpeed: "Geschwindigkeit",
      voiceSlow: "Langsam",
      voiceFast: "Schnell",
      voiceNoVoices: 'Keine Stimmen für diese Sprache auf diesem Gerät verfügbar.',
      voiceFemale: '♀ Weiblich',
      voiceMale: '♂ Männlich',
      voiceNoMatch: 'Keine passende Stimme gefunden — Systemstandard wird verwendet.',
      alarmSection: 'Benachrichtigungs-Einstellungen',
      alarmVolume: 'Lautstärke',
      alarmSnooze: 'Schlummer-Dauer',
      alarmMaxSnooze: 'Max. Schlummern',
      alarmForever: 'Immer',
      alarmMin: 'Min',
      alarmSound: 'Ton',
      weatherReset: 'Wetter',
      weatherActive: 'Wetter aktiv',
      weatherDisable: 'Deaktivieren',
      weatherDesc: 'Aktivieren, um Temperatur und Vorhersage auf dem Hauptbildschirm zu sehen.',
      weatherActivating: 'Wird angefragt…',
      weatherEnable: 'Wetter aktivieren',
      weatherReEnable: 'Wetter reaktivieren',
      weatherDeniedHint: 'Standortzugriff vom Browser blockiert. Gehe in die Browsereinstellungen, um ihn freizugeben, und versuche es erneut.',
      weatherDeniedHintApp: "Der Zugriff auf den Standort wurde abgelehnt. Erlaube ihn für MeMo in den Telefoneinstellungen und versuche es erneut.",
      weatherUnavailable: 'GPS auf diesem Gerät nicht verfügbar.',
      exportLabel: 'Apple Calendar, Outlook oder andere App verbinden (kein Google)',
      exportButton: '.ics herunterladen',
      exportHint: 'In Apple Calendar, Outlook oder eine andere App importieren',
      exporting: 'Exportieren…',
    },
    tasks: {
      emptyTitle: 'Noch keine Notizen',
      emptySub: 'z. B. Brot kaufen · Zahnarzt anrufen',
      addPlaceholder: 'Notiz schreiben…',
      doneSection: "Erledigt",
      deleteNoteConfirm: "Diese Notiz endgültig löschen?",
      restoreNote: "Zum Wiederherstellen tippen",
      completed: 'erledigt',
      noScope: 'Ab- und wieder anmelden, um Notizen zu aktivieren',
      deleteConfirm: 'Diese Notiz löschen?',
    },
    form: {
      allDay: "Ganztägig",
      timeMissing: "Wähle eine Uhrzeit oder schalte „Ganztägig“ ein.",
      pageTitle: 'Neuer Termin',
      required: 'Bitte alle Pflichtfelder ausfüllen.',
      saveError: 'Fehler beim Speichern. Bitte versuche es erneut.',
      titleLabel: 'Termin',
      titlePlaceholder: 'z.B. Arzttermin',
      cancelButton: 'Abbrechen',
      recurringEditTitle: 'Wiederkehrendes Ereignis bearbeiten',
      recurringThisOnly: 'Nur dieses Ereignis',
      recurringAllSeries: 'Alle Ereignisse der Serie',
      recurringNote: 'Wiederkehrendes Ereignis — Änderungen gelten nur für dieses Vorkommen',
      photoLabel: 'Foto',
      openInMaps: 'In Google Maps öffnen',
      recurringEvent: 'Wiederkehrendes Ereignis',
      typeLabel: 'Typ *',
      typeDefault: 'Typ auswählen...',
      datetimeLabel: 'Datum & Uhrzeit',
      dateFromLabel: 'Von',
      dateToLabel: 'Bis zum',
      locationLabel: 'Ort (optional)',
      locationPlaceholder: 'z.B. Hauptstraße 10, Berlin',
      priorityLabel: 'Priorität',
      priorities: [
        { value: 'red',    label: 'Hoch',     sublabel: 'Muss erledigt werden' },
        { value: 'yellow', label: 'Mittel',   sublabel: 'Sollte erledigt werden' },
        { value: 'green',  label: 'Niedrig',  sublabel: 'Wenn Zeit bleibt' },
      ],
      notesLabel: 'Notizen (optional)',
      notesPlaceholder: 'Notizen hinzufügen oder per Sprache eingeben...',
      attLabel: 'Anhänge',
      attAdd: '📎 Foto oder PDF anhängen',
      attHint: 'Max. 5 Dateien, 5 Tage nach dem Termin gelöscht',
      attTooMany: 'Max. 5 Anhänge',
      attBadType: 'Nur Fotos und PDF',
      attTooBig: 'Datei zu groß (max. 10 MB)',
      saving: 'Speichern...',
      saveButton: '✓ Termin speichern',
      editPageTitle: 'Termin bearbeiten',
      editButton: 'Bearbeiten',
      updateButton: '✓ Aktualisieren',
      dictateLabel: 'SPRICH',
      deleteButton: 'Löschen',
      deleteConfirm: 'Diesen Termin aus Google Kalender löschen?',
      deleteError: 'Fehler beim Löschen. Bitte versuche es erneut.',
      recurrenceLabel: 'Wiederholung',
      recurrenceNone: 'Nicht wiederholen',
      recurrenceDaily: 'Täglich',
      recurrenceWeekly: 'Wöchentlich',
      recurrenceMonthly: 'Monatlich',
      recurrenceYearly: 'Jährlich',
      recurrenceEnds: 'Merken',
      recurrenceNever: 'Für immer',
      recurrenceAfter: 'Nach',
      recurrenceTimes: 'Mal',
      recurrenceOnDate: 'Bis zum',
      recurrenceEvery: 'Alle',
      recurrenceLast: 'letzter',
      recurrenceMonthlyDay: 'Gleicher Tag im Monat',
      recUnitDays: 'Tage',
      recUnitWeeks: 'Wochen',
      recUnitMonths: 'Monate',
      recUnitYears: 'Jahre',
      recurrenceDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      deleteRecurring: 'Dies ist ein wiederkehrendes Ereignis.',
      deleteOnlyThis: 'Nur dieses Ereignis',
      deleteAllSeries: 'Alle Ereignisse der Serie',
      voiceHint: 'TIPPEN UND DIKTIEREN: Termin · Datum\nUhrzeit · Ort · Priorität · Notizen',
      parsing: 'Formular aus Sprache ausfüllen...',
      parseError: 'Die Aufnahme konnte nicht verstanden werden. Bitte Felder manuell ausfüllen.',
      imageHint: 'BILD ODER FOTO SCANNEN',
      imageParsing: 'Bild wird gelesen...',
      imageError: 'Das Bild konnte nicht gelesen werden. Bitte Felder manuell ausfüllen.',
      multiTitle: "Gefundene Termine",
      multiSubtitle: "Prüfen, anpassen und erstellen.",
      multiCreate: "Alle erstellen",
      multiNoLabel: "Keine",
      multiCreating: "Wird erstellt…",
      multiDetails: "Ort & Notizen",
      overlapTitle: "Überschneidung",
      overlapText: "Überschneidet sich mit «{title}» ({time}). Du kannst trotzdem speichern.",
      subCalendarLabel: 'Label',
      subCalendarNone: 'Keiner',
      visibilityLabel: 'Kalender',
      visibilityPrivate: 'Nur ich',
      visibilityGroup: 'Geteilter Kalender',
      visibilitySelectGroup: 'Gruppe auswählen',
      conflictTitle: 'Terminkonflikt',
      conflictHint: 'Zu dieser Zeit beschäftigt:',
      reminderLabel: 'Erinnerung',
      reminderNone: 'Keine Erinnerung',
      reminderOn: '✓ Aktiv',
      reminderOff: 'Aktivieren',
      reminderOptions: [
        { value: 0,    label: 'Zu Beginn' },
        { value: 5,    label: '5 Min. vorher' },
        { value: 10,   label: '10 Min. vorher' },
        { value: 15,   label: '15 Min. vorher' },
        { value: 20,   label: '20 Min. vorher' },
        { value: 25,   label: '25 Min. vorher' },
        { value: 30,   label: '30 Min. vorher' },
        { value: 45,   label: '45 Min. vorher' },
        { value: 60,   label: '1 Std. vorher' },
        { value: 120,  label: '2 Std. vorher' },
        { value: 180,  label: '3 Std. vorher' },
        { value: 720,  label: '12 Std. vorher' },
        { value: 1440, label: '1 Tag vorher' },
        { value: 2880, label: '2 Tage vorher' },
        { value: 10080, label: '1 Woche vorher' },
      ],
    },
    groups: {
      pageTitle: 'Geteilte Kalender',
      noGroups: 'Noch keine Kalender',
      noGroupsSub: 'Erstelle einen Kalender oder tritt mit einem Einladungscode bei',
      createGroup: 'Kalender erstellen',
      joinGroup: 'Mit Code beitreten',
      planLimit: 'Du hast das Limit an geteilten Kalendern deines Tarifs erreicht. Upgrade, um weitere zu erstellen.',
      seePlans: 'Tarife ansehen',
      calendarFull: 'Dieser geteilte Kalender ist für den Tarif des Inhabers voll.',
      groupNameLabel: 'Kalendername',
      groupNamePlaceholder: 'z.B. Familie Müller',
      inviteCode: 'Einladungscode',
      inviteCodePlaceholder: 'z.B. A3F9B2',
      join: 'Beitreten',
      create: 'Erstellen',
      creating: 'Wird erstellt…',
      joining: 'Beitreten…',
      members: 'Mitglieder',
      you: '(du)',
      admin: 'Admin',
      member: 'Mitglied',
      leave: 'Kalender verlassen',
      leaveConfirm: 'Diesen Kalender verlassen?',
      delete: 'Kalender löschen',
      deleteConfirm: 'Diesen Kalender löschen? Alle Mitglieder verlieren den Zugang.',
      shareInvite: 'Code teilen',
      copied: '✓ Code kopiert!',
      errorCreate: 'Kalender konnte nicht erstellt werden. Bitte erneut versuchen.',
      errorJoin: 'Ungültiger Code. Bitte prüfen und erneut versuchen.',
      errorAlready: 'Du bist bereits Mitglied dieses Kalenders.',
      cancel: 'Abbrechen',
      saving: 'Speichern…',
    },
    install: {
      title: '📲 MeMo installieren',
      subtitle: 'Als echte App installieren — kein App Store nötig.',
      installBtn: '📲 Auf diesem Gerät installieren',
      installed: '✓ Installiert!',
      oneLogin: '🔐 Einmal anmelden — Memo erinnert sich für immer.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Diesen Link in Safari öffnen', 'Tippe auf Teilen □↑ unten', 'Tippe auf "Zum Home-Bildschirm"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Diesen Link in Chrome öffnen', 'Tippe auf ⋮ oben rechts', 'Tippe auf "Zum Startbildschirm hinzufügen"'],
      },
      desktop: {
        title: '💻 Computer (Chrome)',
        steps: ['Diesen Link in Chrome öffnen', 'Klicke auf das 📲 Symbol in der Adressleiste', 'Klicke auf "Installieren"'],
      },
    },
  },

  fr: {
    nav: { today: "Auj.", add: 'Ajouter', tasks: 'Notes', search: 'Chercher', settings: 'Réglages', groups: 'Agendas' },
    search: {
      placeholder: 'Chercher des événements…',
      searching: 'Recherche en cours…',
      noResults: 'Aucun résultat pour',
      noResultsHint: 'Essayez d\'autres mots-clés',
      hint: 'p. ex. « dentiste », « réunion », « anniversaire »',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Échec de connexion',
      subtitle: "Quelque chose s'est mal passé. Veuillez réessayer.",
      back: 'Retour',
    },
    login: {
      title: 'Memo',
      subtitle: 'Votre gestionnaire personnel de notes et événements avec saisie vocale',
      features: [
        'Ajoutez des événements par la voix en conduisant',
        'Priorisez avec des couleurs',
        'Synchronise avec Google Agenda',
        'Résumé quotidien Telegram à 7h00',
      ],
      signIn: 'Se connecter avec Google',
    },
    onboarding: {
      demo: {
        views: ["Jour", "Sem.", "Mois", "Année"],
        allDay: "Toute la journée",
        prio: ["Haute", "Moyenne", "Basse"],
        labels: ["TRAVAIL", "SPORT", "MAISON"],
        shared: "Partagé",
        create: "Créer",
        join: "Rejoindre par code",
        sharedTitle: "Agendas partagés",
        searchTitle: "Rechercher",
        searchHint: "ex. dentiste, réunion…",
        notePh: "Écrire une note…",
        settingsTitle: "Réglages",
        light: "Clair",
        dark: "Sombre",
        signout: "Se déconnecter",
        ics: "Télécharger .ics",
        notif: "Notifications",
        summaryLbl: "Récap",
        time: "Heure",
        telegram: "Envoyer via Telegram",
        reminder: "30 min avant",
        greeting: "Bonjour",
        repeat: ["Chaque jour", "Chaque sem."],
        ev: ["Réunion", "Sport", "Courses", "Dentiste", "Dîner", "Livraison"],
      },
      skip: "Passer",
      next: "Suivant →",
      start: "Commencer",
      slides: [
        { key: "calendar", title: "L'agenda qui vous parle", text: "Jour, semaine, mois et année : voyez vos rendez-vous comme vous voulez. Et d'un toucher, il vous lit à voix haute la journée et la semaine." },
        { key: "add", title: "Ajoutez à votre façon", text: "Créez un événement en écrivant, à la voix ou avec une photo : prenez une feuille, un e-mail ou une affiche et l'IA remplit tout. Vous pouvez aussi joindre des documents." },
        { key: "planner", title: "Organisez votre journée", text: "Le planning quotidien et hebdomadaire montre les créneaux libres et occupés : tout s'agence d'un coup d'œil." },
        { key: "multi", title: "Cherchez, notez, partagez", text: "Retrouvez chaque événement en une seconde, gardez vos notes à portée et partagez l'agenda avec la famille ou l'équipe, chacun sa couleur." },
        { key: "summary", title: "Chaque matin, votre récap", text: "Recevez les rendez-vous du jour sur Telegram et par e-mail, triés par heure et priorité. Commencez la journée sans rien oublier." },
        { key: "settings", title: "Faites travailler MeMo pour vous", text: "Choisissez langue, heure du récap, notifications, sonnerie et thème : MeMo s'adapte à votre façon de travailler." },
      ],
    },

    welcome: {
      greeting: 'Bienvenue',
      subtitle: 'Choisissez votre langue pour commencer',
      saving: 'Enregistrement...',
      continue: 'Continuer →',
      countryLabel: 'Votre pays',
      countryPlaceholder: 'Sélectionner votre pays...',
      themeLabel: 'Choisir votre thème',
      timezoneLabel: 'Fuseau horaire',
      timezoneHint: 'Modifiez-le quand vous voyagez',
    },
    eventGenericType: 'Événement',
    today: {
      noEvents: 'Aucun événement prévu',
      eventsCount: (n) => `${n} événement${n !== 1 ? 's' : ''} aujourd'hui`,
      emptyTitle: 'Journée libre !',
      emptySub: 'Appuyez sur + pour ajouter un événement',
      error: 'Impossible de charger les événements. Veuillez réessayer.',
      retry: 'Réessayer',
      signOutHint: 'Si le problème persiste, déconnectez-vous et reconnectez-vous.',
      slowConnection: 'Connexion lente… chargement en cours.',
      aiLimitReached: 'Vous avez utilisé les 30 saisies IA (voix et photo) du plan Free ce mois-ci. Elles se réinitialisent le mois prochain.',
      weekList: 'Liste',
      weekGrid: 'Grille',
      conflictWarning: 'Attention : chevauchement avec « {title} » ({time}). Enregistrer quand même ?',
      tomorrow: 'Demain',
      today: 'Aujourd\'hui',
      yesterday: 'Hier',
      snooze: 'Reporter',
      stop: 'Arrêter',
      prevWeek: 'Semaine précédente',
      nextWeek: 'Semaine suivante',
      microphone: 'Microphone',
      noEventsDay: 'Aucun événement',
      emptyTitleDay: 'Rien ici',
      eventsCountDay: (n) => `${n} événement${n !== 1 ? 's' : ''}`,
      prevDay: 'Jour précédent',
      nextDay: 'Jour suivant',
      filterAll: 'Tous',
      filterMine: 'Moi seul',
      ttsButton: 'Écouter',
      ttsStop: 'Arrêter',
      ttsNoEvents: "Aucun événement aujourd'hui.",
      ttsIntro: "Aujourd'hui vous avez {n} événements",
      viewDay: 'Jour',
      viewWeek: 'Semaine',
      viewMonth: 'Mois',
      viewYear: 'Année',
      viewSlots: 'Plan',
      slotsHint: 'Touchez un créneau libre pour ajouter un événement',
      slotsTapDay: 'Touchez un jour pour l’ouvrir',
      slotsFree: 'Libre',
      slotsBusy: 'Occupé',
      weekOf: 'Semaine',
    },
    settings: {
      pageTitle: 'Paramètres',
      account: 'Compte',
      signOut: 'Se déconnecter',
      themeColor: "Couleur du thème de l'app",
      themeColorBtn: "Couleur app",
      a11yReminders: "Rappels accessibles (vibration + voix)",
      a11yRemindersHint: "À l'heure du rappel, le téléphone vibre et lit l'événement à voix haute.",
      a11yMode: "Mode accessibilité",
      a11yModeHint: "Active ensemble le contraste élevé et les rappels avec vibration et voix.",
      a11yVibration: "Vibration",
      a11ySpeak: "Lire l'événement à voix haute",
      a11yContrast: "Contraste élevé",
      usageTab: "Utilisation données",
      a11yTab: "Accessibilité",
      updateAvailable: "Une nouvelle version est disponible.",
      updateButton: "Mettre à jour",
      bigText: "Grand texte",
      bigTextOn: "activé",
      bigTextOff: "désactivé",
      bigLarge: "Grand",
      bigXLarge: "Très grand",
      feedTitle: "Voir MeMo dans le Calendrier Apple",
      feedDesc: "Pour iPhone et iCloud : vos rendez-vous MeMo, dans le Calendrier Apple.",
      feedCopy: "Copier le lien",
      feedCopied: "Copié !",
      feedSubscribe: "Voir dans mon agenda",
      feedHint: "iPhone : Réglages → Calendrier → Comptes → Ajouter → Autre → Ajouter un calendrier avec abonnement. Mac : Fichier → Nouvel abonnement à un calendrier.",
      feedConnect: "Connecter vos agendas",
      feedRegenerate: "Régénérer le lien",
      saved: '✓ Paramètres enregistrés !',
      saveError: "Erreur lors de l'enregistrement. Veuillez réessayer.",
      language: 'Langue',
      telegramSection: 'Résumé Telegram quotidien',
      sendTime: "Heure d'envoi",
      timezone: 'Votre fuseau horaire',
      tzDetected: 'Votre appareil est en',
      tzUseDetected: 'Utiliser ce fuseau horaire',
      tzAutoDetect: 'Détection automatique',
      chatId: 'ID de chat Telegram',
      voicePitch: 'Hauteur',
      voicePitchLow: 'Grave',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Aigu',
      pushIntro: 'Les notifications arrivent même app fermée. Touche pour activer et tester.',
      pushDenied: 'Autorisation refusée. Active les notifications de MeMo dans les réglages du téléphone/navigateur.',
      pushUnsupported: 'Cet appareil ne prend pas en charge les notifications push. Sur iPhone, installe MeMo sur l\'écran d\'accueil.',
      pushUnsupportedApp: 'Dans l’application installée, les rappels viennent du téléphone lui-même, pas du navigateur : vérifiez simplement que MeMo a l’autorisation d’afficher des notifications. Telegram et l’e-mail restent en plus, si vous les voulez.',
      pushFailed: 'Échec. Réessaie ou vérifie les réglages de notifications.',
      pushSending: 'Envoi…',
      pushTestButton: '🔔 Activer / Tester',
      pushSent: '✓ Envoyée ! Vérifie ton appareil.',
      pushError: 'Erreur — active les notifications dans le navigateur.',
      pushNoDevice: 'Aucun appareil atteint. Réessaie, ou désactive puis réactive les notifications de MeMo dans les réglages du navigateur.',
      chatIdPlaceholder: 'ex. 123456789',
      chatIdHelp: (bot) => `Comment obtenir votre Chat ID : ouvrez Telegram, cherchez @${bot}, envoyez un message, puis appuyez sur le lien dans le guide de configuration.`,
      testSending: 'Envoi...',
      testButton: '📨 Envoyer un message de test',
      testSuccess: '✓ Message envoyé ! Vérifiez Telegram.',
      testError: "Échec de l'envoi. Assurez-vous que votre Chat ID est enregistré.",
      saving: 'Enregistrement...',
      saveButton: 'Enregistrer les paramètres',
      telegramSetupTitle: 'Comment connecter Telegram :',
      telegramStep1: (bot) => `Ouvrez Telegram et cherchez @${bot}`,
      telegramStep2: "Envoyez n'importe quel message pour démarrer",
      telegramStep3: 'Ouvrez @userinfobot → envoyez /start → copiez votre Chat ID',
      telegramStep4: 'Collez-le dans le champ ci-dessous et enregistrez',
      supportTitle: "Aide et assistance",
      supportDesc: "Besoin d'aide ou envie de signaler un problème ? Écris-nous, nous répondrons dès que possible.",
      emailSupport: "Écrire par e-mail",
      contactSupport: 'Contacter le support sur Telegram',
      themeSection: 'Apparence',
      themeLabel: 'Thème',
      themeLight: 'Clair',
      themeDark: 'Sombre',
      countryLabel: 'Pays',
      subCalendarsSection: 'Étiquettes',
      subCalendarName: 'Nom',
      subCalendarAdd: 'Ajouter une étiquette',
      subCalendarOwn: 'Colorer mes événements',
      subCalendarPickColour: 'Choisissez une couleur',
      subCalendarAutoHint: 'Les étiquettes des agendas qui ne sont pas les vôtres reçoivent une couleur provisoire : touchez-les pour choisir la vôtre.',
      subCalendarDeleteConfirm: 'Supprimer cette étiquette ? Les événements ne seront pas supprimés.',
      summarySection: 'Résumé du matin',
      summaryNowhere: "Le récapitulatif ne part pas : MeMo ne sait pas où l'envoyer. Indiquez votre e-mail ci-dessous, ou connectez Telegram.",
      summaryChannels: 'Où envoyer',
      viaTelegram: 'Envoyer via Telegram',
      viaEmail: 'Envoyer par e-mail',
      emailAnyProvider: 'Compatible avec toute adresse — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Résumé par e-mail',
      emailLabel: 'Votre adresse e-mail',
      emailHelp: 'Toute adresse e-mail — Gmail, Outlook, iCloud, professionnel, etc.',
      emailTestSending: '⏳ Envoi...',
      emailTestButton: '📧 Envoyer un e-mail de test',
      emailTestSuccess: '✓ E-mail envoyé ! Vérifiez votre boîte de réception.',
      emailTestError: "Échec de l'envoi. Vérifiez la configuration du serveur.",
      voiceLabel: 'Voix de lecture',
      voiceDefault: 'Défaut du système',
      voiceTest: '▶ Tester la voix',
      voiceSection: "Voix et lecture",
      voiceSpeed: "Vitesse",
      voiceSlow: "Lente",
      voiceFast: "Rapide",
      voiceNoVoices: "Aucune voix disponible pour cette langue sur cet appareil.",
      voiceFemale: '♀ Féminine',
      voiceMale: '♂ Masculine',
      voiceNoMatch: 'Aucune voix correspondante — utilisation de la voix par défaut.',
      alarmSection: 'Paramètres de notification',
      alarmVolume: 'Volume',
      alarmSnooze: 'Durée du rappel',
      alarmMaxSnooze: 'Max. rappels',
      alarmForever: 'Toujours',
      alarmMin: 'min',
      alarmSound: 'Son',
      weatherReset: 'Météo',
      weatherActive: 'Météo active',
      weatherDisable: 'Désactiver',
      weatherDesc: 'Activer pour voir la température et les prévisions sur l\'écran principal.',
      weatherActivating: 'En cours…',
      weatherEnable: 'Activer la météo',
      weatherReEnable: 'Réactiver la météo',
      weatherDeniedHint: 'Accès à la localisation bloqué par le navigateur. Allez dans les paramètres du navigateur pour le débloquer, puis réessayez.',
      weatherDeniedHintApp: "L'accès à la position a été refusé. Autorisez-le pour MeMo dans les réglages du téléphone, puis réessayez.",
      weatherUnavailable: 'GPS non disponible sur cet appareil.',
      exportLabel: 'Connecter Apple Calendar, Outlook ou toute autre app non Google',
      exportButton: 'Télécharger .ics',
      exportHint: 'Importer dans Apple Calendar, Outlook ou toute autre app',
      exporting: 'Exportation…',
    },
    tasks: {
      emptyTitle: 'Aucune note pour le moment',
      emptySub: 'p. ex. Acheter du pain · Appeler le dentiste',
      addPlaceholder: 'Écrire une note…',
      doneSection: "Terminées",
      deleteNoteConfirm: "Supprimer définitivement cette note ?",
      restoreNote: "Toucher pour restaurer",
      completed: 'terminées',
      noScope: 'Déconnecte-toi et reconnecte-toi pour activer les notes',
      deleteConfirm: 'Supprimer cette note ?',
    },
    form: {
      allDay: "Toute la journée",
      timeMissing: "Choisissez une heure, ou activez « Toute la journée ».",
      pageTitle: 'Nouvel événement',
      required: 'Veuillez remplir tous les champs obligatoires.',
      saveError: "Erreur lors de l'enregistrement. Veuillez réessayer.",
      titleLabel: 'Événement',
      titlePlaceholder: 'ex. Rendez-vous chez le médecin',
      cancelButton: 'Annuler',
      recurringEditTitle: 'Modifier l\'événement récurrent',
      recurringThisOnly: 'Cet événement seulement',
      recurringAllSeries: 'Tous les événements de la série',
      recurringNote: 'Événement récurrent — les modifications s\'appliquent à cette occurrence',
      photoLabel: 'Photo',
      openInMaps: 'Ouvrir dans Google Maps',
      recurringEvent: 'Événement récurrent',
      typeLabel: 'Type *',
      typeDefault: 'Sélectionner un type...',
      datetimeLabel: 'Date et heure',
      dateFromLabel: 'Du',
      dateToLabel: "Jusqu'au",
      locationLabel: 'Lieu (optionnel)',
      locationPlaceholder: 'ex. 10 Rue de la Paix, Paris',
      priorityLabel: 'Priorité',
      priorities: [
        { value: 'red',    label: 'Haute',   sublabel: 'À faire absolument' },
        { value: 'yellow', label: 'Moyen',   sublabel: 'À faire dès que possible' },
        { value: 'green',  label: 'Faible',  sublabel: 'Si possible' },
      ],
      notesLabel: 'Notes (optionnel)',
      notesPlaceholder: 'Ajouter des notes ou dicter...',
      attLabel: 'Pièces jointes',
      attAdd: '📎 Joindre une photo ou un PDF',
      attHint: 'Max 5 fichiers, supprimés 5 jours après',
      attTooMany: '5 pièces jointes max',
      attBadType: 'Uniquement photos et PDF',
      attTooBig: 'Fichier trop grand (max 10 Mo)',
      saving: 'Enregistrement...',
      saveButton: "✓ Enregistrer l'événement",
      editPageTitle: "Modifier l'événement",
      editButton: 'Modifier',
      updateButton: '✓ Mettre à jour',
      dictateLabel: 'DIS',
      deleteButton: 'Supprimer',
      deleteConfirm: 'Supprimer cet événement de Google Agenda ?',
      deleteError: 'Erreur lors de la suppression. Veuillez réessayer.',
      recurrenceLabel: 'Récurrence',
      recurrenceNone: 'Ne pas répéter',
      recurrenceDaily: 'Chaque jour',
      recurrenceWeekly: 'Chaque semaine',
      recurrenceMonthly: 'Chaque mois',
      recurrenceYearly: 'Chaque année',
      recurrenceEnds: 'Se souvenir',
      recurrenceNever: 'Pour toujours',
      recurrenceAfter: 'Après',
      recurrenceTimes: 'fois',
      recurrenceOnDate: 'Jusqu’au',
      recurrenceEvery: 'Tous les',
      recurrenceLast: 'dernier',
      recurrenceMonthlyDay: 'Même jour du mois',
      recUnitDays: 'jours',
      recUnitWeeks: 'semaines',
      recUnitMonths: 'mois',
      recUnitYears: 'ans',
      recurrenceDays: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
      deleteRecurring: 'Ceci est un événement récurrent.',
      deleteOnlyThis: 'Seulement cet événement',
      deleteAllSeries: 'Tous les événements de la série',
      voiceHint: 'APPUYEZ ET DICTEZ: Événement · Date\nHeure · Lieu · Priorité · Notes',
      parsing: 'Remplissage du formulaire par la voix...',
      parseError: "L'enregistrement n'a pas pu être compris. Veuillez remplir les champs manuellement.",
      imageHint: 'SCANNER IMAGE OU PHOTO',
      imageParsing: 'Lecture de l\'image...',
      imageError: 'L\'image n\'a pas pu être lue. Veuillez remplir les champs manuellement.',
      multiTitle: "Événements trouvés",
      multiSubtitle: "Vérifiez, corrigez et créez.",
      multiCreate: "Tout créer",
      multiNoLabel: "Aucune",
      multiCreating: "Création…",
      multiDetails: "Lieu et notes",
      overlapTitle: "Chevauchement",
      overlapText: "Chevauche «{title}» ({time}). Tu peux quand même enregistrer.",
      subCalendarLabel: 'Étiquette',
      subCalendarNone: 'Aucun',
      visibilityLabel: 'Agendas',
      visibilityPrivate: 'Moi seulement',
      visibilityGroup: 'Calendrier partagé',
      visibilitySelectGroup: 'Sélectionner le groupe',
      conflictTitle: 'Conflit d\'horaire',
      conflictHint: 'Occupés à cette heure :',
      reminderLabel: 'Rappel',
      reminderNone: 'Pas de rappel',
      reminderOn: '✓ Actif',
      reminderOff: 'Activer',
      reminderOptions: [
        { value: 0,    label: 'Au début' },
        { value: 5,    label: '5 min avant' },
        { value: 10,   label: '10 min avant' },
        { value: 15,   label: '15 min avant' },
        { value: 20,   label: '20 min avant' },
        { value: 25,   label: '25 min avant' },
        { value: 30,   label: '30 min avant' },
        { value: 45,   label: '45 min avant' },
        { value: 60,   label: '1 h avant' },
        { value: 120,  label: '2 h avant' },
        { value: 180,  label: '3 h avant' },
        { value: 720,  label: '12 h avant' },
        { value: 1440, label: '1 jour avant' },
        { value: 2880, label: '2 jours avant' },
        { value: 10080, label: '1 semaine avant' },
      ],
    },
    groups: {
      pageTitle: 'Calendriers partagés',
      noGroups: 'Pas encore de calendriers',
      noGroupsSub: 'Créez un groupe ou rejoignez-en un avec un code d\'invitation',
      createGroup: 'Créer un calendrier',
      joinGroup: 'Rejoindre avec un code',
      planLimit: 'Vous avez atteint la limite d\'agendas partagés de votre offre. Passez à une offre supérieure pour en créer d\'autres.',
      seePlans: 'Voir les offres',
      calendarFull: 'Cet agenda partagé a atteint la limite du forfait.',
      groupNameLabel: 'Nom du calendrier',
      groupNamePlaceholder: 'ex. Famille Martin',
      inviteCode: 'Code d\'invitation',
      inviteCodePlaceholder: 'ex. A3F9B2',
      join: 'Rejoindre',
      create: 'Créer',
      creating: 'Création…',
      joining: 'Adhésion…',
      members: 'Membres',
      you: '(vous)',
      admin: 'Admin',
      member: 'Membre',
      leave: 'Quitter le calendrier',
      leaveConfirm: 'Quitter ce calendrier ?',
      delete: 'Supprimer le calendrier',
      deleteConfirm: 'Supprimer ce calendrier ? Tous les membres perdront l\'accès.',
      shareInvite: 'Partager le code',
      copied: '✓ Code copié !',
      errorCreate: 'Impossible de créer le calendrier. Veuillez réessayer.',
      errorJoin: 'Code invalide. Veuillez vérifier et réessayer.',
      errorAlready: 'Vous êtes déjà membre de ce calendrier.',
      cancel: 'Annuler',
      saving: 'Enregistrement…',
    },
    install: {
      title: '📲 Installer MeMo',
      subtitle: 'Installe-le comme une vraie app — sans App Store.',
      installBtn: '📲 Installer sur cet appareil',
      installed: '✓ Installé !',
      oneLogin: '🔐 Connecte-toi une fois — Memo se souvient pour toujours.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Ouvre ce lien dans Safari', 'Appuie sur Partager □↑ en bas', 'Appuie sur "Sur l\'écran d\'accueil"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Ouvre ce lien dans Chrome', 'Appuie sur ⋮ en haut à droite', 'Appuie sur "Ajouter à l\'écran d\'accueil"'],
      },
      desktop: {
        title: '💻 Ordinateur (Chrome)',
        steps: ['Ouvre ce lien dans Chrome', 'Clique sur l\'icône 📲 dans la barre d\'adresse', 'Clique sur "Installer"'],
      },
    },
  },

  nl: {
    nav: { today: 'Vandaag', add: 'Nieuw', tasks: 'Notities', search: 'Zoeken', settings: 'Opties', groups: 'Kalenders' },
    search: {
      placeholder: 'Afspraken zoeken…',
      searching: 'Zoeken…',
      noResults: 'Geen resultaten voor',
      noResultsHint: 'Probeer andere zoekwoorden',
      hint: 'bijv. "tandarts", "vergadering", "verjaardag"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Aanmelden mislukt',
      subtitle: 'Er is iets misgegaan. Probeer het opnieuw.',
      back: 'Terug',
    },
    login: {
      title: 'Memo',
      subtitle: 'Jouw persoonlijke notitie- en agendabeheerder met spraakinvoer',
      features: [
        'Voeg afspraken toe via spraak, ook tijdens het rijden',
        'Prioriteer met kleuren',
        'Synchroniseert met Google Agenda',
        'Dagelijkse Telegram-samenvatting om 7:00',
      ],
      signIn: 'Inloggen met Google',
    },
    onboarding: {
      demo: {
        views: ["Dag", "Week", "Maand", "Jaar"],
        allDay: "Hele dag",
        prio: ["Hoog", "Middel", "Laag"],
        labels: ["WERK", "SPORT", "THUIS"],
        shared: "Gedeeld",
        create: "Maken",
        join: "Deelnemen met code",
        sharedTitle: "Gedeelde agenda's",
        searchTitle: "Zoek afspraken",
        searchHint: "bijv. tandarts, overleg…",
        notePh: "Schrijf een notitie…",
        settingsTitle: "Instellingen",
        light: "Licht",
        dark: "Donker",
        signout: "Afmelden",
        ics: ".ics downloaden",
        notif: "Meldingen",
        summaryLbl: "Overzicht",
        time: "Tijd",
        telegram: "Via Telegram sturen",
        reminder: "30 min vooraf",
        greeting: "Goedemorgen",
        repeat: ["Dagelijks", "Wekelijks"],
        ev: ["Vergadering", "Sport", "Boodschappen", "Tandarts", "Diner", "Levering"],
      },
      skip: "Overslaan",
      next: "Volgende →",
      start: "Beginnen",
      slides: [
        { key: "calendar", title: "De agenda die tegen je praat", text: "Dag, week, maand en jaar: bekijk je afspraken zoals jij wilt. En met één tik leest hij de dag en de week hardop voor." },
        { key: "add", title: "Voeg toe zoals jij wilt", text: "Maak een afspraak door te typen, met je stem of met een foto: maak een foto van een blaadje, een e-mail of een poster en de AI vult alles in. Je kunt ook documenten toevoegen." },
        { key: "planner", title: "Organiseer je dag", text: "De dag- en weekplanner toont vrije en bezette momenten: je past alles in met één oogopslag." },
        { key: "multi", title: "Zoek, noteer, deel", text: "Vind elke afspraak in een seconde, houd je notities altijd bij de hand en deel de agenda met familie of team — elk in een eigen kleur." },
        { key: "summary", title: "Elke ochtend jouw overzicht", text: "Ontvang de afspraken van de dag via Telegram en e-mail, op tijd en prioriteit gesorteerd. Begin de dag zonder iets te vergeten." },
        { key: "settings", title: "Laat MeMo voor je werken", text: "Kies taal, tijd van het overzicht, meldingen, beltoon en thema: MeMo past zich aan jouw manier van werken aan." },
      ],
    },

    welcome: {
      greeting: 'Welkom',
      subtitle: 'Kies je taal om te beginnen',
      saving: 'Opslaan...',
      continue: 'Doorgaan →',
      countryLabel: 'Jouw land',
      countryPlaceholder: 'Selecteer je land...',
      themeLabel: 'Kies je thema',
      timezoneLabel: 'Tijdzone',
      timezoneHint: 'Wijzig dit als je reist',
    },
    eventGenericType: 'Afspraak',
    today: {
      noEvents: 'Geen afspraken gepland',
      eventsCount: (n) => `${n} afspraak${n !== 1 ? 'en' : ''} vandaag`,
      emptyTitle: 'Vandaag niets gepland!',
      emptySub: 'Tik op + om een afspraak toe te voegen',
      error: 'Afspraken konden niet worden geladen. Probeer het opnieuw.',
      retry: 'Opnieuw proberen',
      signOutHint: 'Als het probleem aanhoudt, probeer dan uit en opnieuw in te loggen.',
      slowConnection: 'Trage verbinding… nog aan het laden.',
      aiLimitReached: 'Je hebt de 30 AI-invoeren (spraak & foto) van het Free-plan deze maand gebruikt. Ze worden volgende maand gereset.',
      weekList: 'Lijst',
      weekGrid: 'Raster',
      conflictWarning: 'Let op: overlapt met “{title}” ({time}). Toch opslaan?',
      tomorrow: 'Morgen',
      today: 'Vandaag',
      yesterday: 'Gisteren',
      snooze: 'Snooze',
      stop: 'Stoppen',
      prevWeek: 'Vorige week',
      nextWeek: 'Volgende week',
      microphone: 'Microfoon',
      noEventsDay: 'Geen afspraken',
      emptyTitleDay: 'Niets hier',
      eventsCountDay: (n) => `${n} afspraak${n !== 1 ? 'en' : ''}`,
      prevDay: 'Vorige dag',
      nextDay: 'Volgende dag',
      filterAll: 'Alle',
      filterMine: 'Alleen ik',
      ttsButton: 'Beluisteren',
      ttsStop: 'Stoppen',
      ttsNoEvents: 'Vandaag geen afspraken.',
      ttsIntro: 'Vandaag heb je {n} afspraken',
      viewDay: 'Dag',
      viewWeek: 'Week',
      viewMonth: 'Maand',
      viewYear: 'Jaar',
      viewSlots: 'Plan',
      slotsHint: 'Tik op een vrij slot om een afspraak toe te voegen',
      slotsTapDay: 'Tik op een dag om die te openen',
      slotsFree: 'Vrij',
      slotsBusy: 'Bezet',
      weekOf: 'Week',
    },
    settings: {
      pageTitle: 'Instellingen',
      account: 'Account',
      signOut: 'Uitloggen',
      themeColor: "App-themakleur",
      themeColorBtn: "App-kleur",
      a11yReminders: "Toegankelijke herinneringen (trillen + stem)",
      a11yRemindersHint: "Op het herinneringstijdstip trilt de telefoon en leest het item voor.",
      a11yMode: "Toegankelijkheidsmodus",
      a11yModeHint: "Zet samen hoog contrast en herinneringen met trillen en stem aan.",
      a11yVibration: "Trillen",
      a11ySpeak: "Afspraak voorlezen",
      a11yContrast: "Hoog contrast",
      usageTab: "Datagebruik",
      a11yTab: "Toegankelijkheid",
      updateAvailable: "Er is een nieuwe versie beschikbaar.",
      updateButton: "Bijwerken",
      bigText: "Grote tekst",
      bigTextOn: "aan",
      bigTextOff: "uit",
      bigLarge: "Groot",
      bigXLarge: "Zeer groot",
      feedTitle: "MeMo in Apple Agenda zien",
      feedDesc: "Voor iPhone en iCloud: je MeMo-afspraken, in Apple Agenda.",
      feedCopy: "Link kopiëren",
      feedCopied: "Gekopieerd!",
      feedSubscribe: "In mijn agenda tonen",
      feedHint: "iPhone: Instellingen → Agenda → Accounts → Voeg toe → Anders → Voeg geabonneerde agenda toe. Mac: Archief → Nieuw agenda-abonnement.",
      feedConnect: "Verbind je agenda's",
      feedRegenerate: "Link vernieuwen",
      saved: '✓ Instellingen opgeslagen!',
      saveError: 'Fout bij opslaan. Probeer het opnieuw.',
      language: 'Taal',
      telegramSection: 'Dagelijkse Telegram-samenvatting',
      sendTime: 'Verzendtijd',
      timezone: 'Jouw tijdzone',
      tzDetected: 'Jouw apparaat is in',
      tzUseDetected: 'Gebruik deze tijdzone',
      tzAutoDetect: 'Automatisch detecteren',
      chatId: 'Telegram chat-ID',
      voicePitch: 'Toonhoogte',
      voicePitchLow: 'Laag',
      voicePitchMid: 'Normaal',
      voicePitchHigh: 'Hoog',
      pushIntro: 'Meldingen komen op je telefoon, ook als de app gesloten is. Tik om in te schakelen en te testen.',
      pushDenied: 'Toestemming geweigerd. Schakel meldingen voor MeMo in via je telefoon-/browserinstellingen.',
      pushUnsupported: 'Dit apparaat ondersteunt geen push. Installeer MeMo op iPhone op het startscherm.',
      pushUnsupportedApp: 'In de geïnstalleerde app komen de herinneringen van de telefoon zelf, niet van de browser: zorg er alleen voor dat MeMo meldingen mag tonen. Telegram en e-mail zijn extra, als je die wilt.',
      pushFailed: 'Mislukt. Probeer opnieuw of controleer de meldingsinstellingen.',
      pushSending: 'Versturen…',
      pushTestButton: '🔔 Inschakelen / Test sturen',
      pushSent: '✓ Verstuurd! Controleer je apparaat.',
      pushError: 'Fout — schakel meldingen in de browser in.',
      pushNoDevice: 'Geen apparaat bereikt. Probeer opnieuw, of zet MeMo-meldingen uit en weer aan in je browserinstellingen.',
      chatIdPlaceholder: 'bijv. 123456789',
      chatIdHelp: (bot) => `Hoe je je Chat ID krijgt: open Telegram, zoek @${bot}, stuur een bericht, tik dan op de link in de installatiegids.`,
      testSending: 'Verzenden...',
      testButton: '📨 Stuur nu een testbericht',
      testSuccess: '✓ Bericht verzonden! Controleer Telegram.',
      testError: 'Verzenden mislukt. Zorg dat je chat-ID is opgeslagen.',
      saving: 'Opslaan...',
      saveButton: 'Instellingen opslaan',
      telegramSetupTitle: 'Telegram verbinden:',
      telegramStep1: (bot) => `Open Telegram en zoek @${bot}`,
      telegramStep2: 'Stuur een bericht om te starten',
      telegramStep3: 'Open @userinfobot → stuur /start → kopieer je Chat ID',
      telegramStep4: 'Plak het in het veld hieronder en sla op',
      supportTitle: "Hulp en ondersteuning",
      supportDesc: "Hulp nodig of wil je een probleem melden? Stuur ons een bericht – we reageren zo snel mogelijk.",
      emailSupport: "Mail ons",
      contactSupport: 'Contact opnemen met support op Telegram',
      themeSection: 'Weergave',
      themeLabel: 'Thema',
      themeLight: 'Licht',
      themeDark: 'Donker',
      countryLabel: 'Land',
      subCalendarsSection: 'Labels',
      subCalendarName: 'Naam',
      subCalendarAdd: 'Label toevoegen',
      subCalendarOwn: 'Mijn afspraken kleuren',
      subCalendarPickColour: 'Kies een kleur',
      subCalendarAutoHint: "Labels voor agenda's die niet van jou zijn krijgen een voorlopige kleur — tik erop om zelf te kiezen.",
      subCalendarDeleteConfirm: 'Dit label verwijderen? Afspraken worden niet verwijderd.',
      summarySection: 'Ochtendsamenvatting',
      summaryNowhere: "Het overzicht gaat nergens heen: MeMo weet niet waarheen. Vul hieronder je e-mail in, of koppel Telegram.",
      summaryChannels: 'Waarheen sturen',
      viaTelegram: 'Verzenden via Telegram',
      viaEmail: 'Verzenden via e-mail',
      emailAnyProvider: 'Werkt met elk e-mailadres — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'E-mailsamenvatting',
      emailLabel: 'Jouw e-mailadres',
      emailHelp: 'Elk e-mailadres — Gmail, Outlook, iCloud, werk, enz.',
      emailTestSending: '⏳ Verzenden...',
      emailTestButton: '📧 Test-e-mail verzenden',
      emailTestSuccess: '✓ E-mail verzonden! Controleer je inbox.',
      emailTestError: 'Verzenden mislukt. Controleer de serverconfiguratie.',
      voiceLabel: 'Leesstem',
      voiceDefault: 'Systeemstandaard',
      voiceTest: '▶ Stem testen',
      voiceSection: "Stem en voorlezen",
      voiceSpeed: "Snelheid",
      voiceSlow: "Langzaam",
      voiceFast: "Snel",
      voiceNoVoices: 'Geen stemmen beschikbaar voor deze taal op dit apparaat.',
      voiceFemale: '♀ Vrouwelijk',
      voiceMale: '♂ Mannelijk',
      voiceNoMatch: 'Geen passende stem gevonden — systeemstandaard wordt gebruikt.',
      alarmSection: 'Meldingsinstellingen',
      alarmVolume: 'Volume',
      alarmSnooze: 'Sluimerduur',
      alarmMaxSnooze: 'Max. sluimeren',
      alarmForever: 'Altijd',
      alarmMin: 'min',
      alarmSound: 'Geluid',
      weatherReset: 'Weer',
      weatherActive: 'Weer actief',
      weatherDisable: 'Uitschakelen',
      weatherDesc: 'Schakel in om temperatuur en weersvoorspelling op het hoofdscherm te zien.',
      weatherActivating: 'Bezig…',
      weatherEnable: 'Weer inschakelen',
      weatherReEnable: 'Weer opnieuw inschakelen',
      weatherDeniedHint: 'Locatietoegang geblokkeerd door de browser. Ga naar de browserinstellingen om het te deblokkeren en probeer opnieuw.',
      weatherDeniedHintApp: "Toegang tot je locatie is geweigerd. Sta het toe voor MeMo in de telefooninstellingen en probeer opnieuw.",
      weatherUnavailable: 'GPS niet beschikbaar op dit apparaat.',
      exportLabel: 'Apple Calendar, Outlook of andere app koppelen (geen Google)',
      exportButton: '.ics downloaden',
      exportHint: 'Importeer in Apple Calendar, Outlook of een andere app',
      exporting: 'Exporteren…',
    },
    tasks: {
      emptyTitle: 'Nog geen notities',
      emptySub: 'bijv. Brood kopen · Tandarts bellen',
      addPlaceholder: 'Schrijf een notitie…',
      doneSection: "Voltooid",
      deleteNoteConfirm: "Deze notitie definitief verwijderen?",
      restoreNote: "Tik om te herstellen",
      completed: 'voltooid',
      noScope: 'Log uit en weer in om notities in te schakelen',
      deleteConfirm: 'Deze notitie verwijderen?',
    },
    form: {
      allDay: "Hele dag",
      timeMissing: "Kies een tijd, of zet “Hele dag” aan.",
      pageTitle: 'Nieuwe afspraak',
      required: 'Vul alle verplichte velden in.',
      saveError: 'Fout bij opslaan. Probeer het opnieuw.',
      titleLabel: 'Afspraak',
      titlePlaceholder: 'bijv. Doktersafspraak',
      cancelButton: 'Annuleren',
      recurringEditTitle: 'Terugkerende afspraak bewerken',
      recurringThisOnly: 'Alleen deze afspraak',
      recurringAllSeries: 'Alle afspraken in de reeks',
      recurringNote: 'Terugkerende afspraak — wijzigingen gelden alleen voor deze keer',
      photoLabel: 'Foto',
      openInMaps: 'Openen in Google Maps',
      recurringEvent: 'Terugkerende afspraak',
      typeLabel: 'Type *',
      typeDefault: 'Selecteer type...',
      datetimeLabel: 'Datum & tijd',
      dateFromLabel: 'Van',
      dateToLabel: 'Tot en met',
      locationLabel: 'Locatie (optioneel)',
      locationPlaceholder: 'bijv. Hoofdstraat 10, Amsterdam',
      priorityLabel: 'Prioriteit',
      priorities: [
        { value: 'red',    label: 'Hoog',     sublabel: 'Moet gedaan worden' },
        { value: 'yellow', label: 'Gemiddeld', sublabel: 'Zo snel mogelijk' },
        { value: 'green',  label: 'Laag',     sublabel: 'Als er tijd is' },
      ],
      notesLabel: 'Notities (optioneel)',
      notesPlaceholder: 'Voeg notities toe of gebruik spraak...',
      attLabel: 'Bijlagen',
      attAdd: '📎 Foto of PDF bijvoegen',
      attHint: 'Max 5 bestanden, 5 dagen erna verwijderd',
      attTooMany: 'Max 5 bijlagen',
      attBadType: 'Alleen foto en PDF',
      attTooBig: 'Bestand te groot (max 10 MB)',
      saving: 'Opslaan...',
      saveButton: '✓ Afspraak opslaan',
      editPageTitle: 'Afspraak bewerken',
      editButton: 'Bewerken',
      updateButton: '✓ Bijwerken',
      dictateLabel: 'ZEG',
      deleteButton: 'Verwijderen',
      deleteConfirm: 'Deze afspraak uit Google Agenda verwijderen?',
      deleteError: 'Fout bij verwijderen. Probeer het opnieuw.',
      recurrenceLabel: 'Herhaling',
      recurrenceNone: 'Niet herhalen',
      recurrenceDaily: 'Elke dag',
      recurrenceWeekly: 'Elke week',
      recurrenceMonthly: 'Elke maand',
      recurrenceYearly: 'Elk jaar',
      recurrenceEnds: 'Onthouden',
      recurrenceNever: 'Voor altijd',
      recurrenceAfter: 'Na',
      recurrenceTimes: 'keer',
      recurrenceOnDate: 'Tot',
      recurrenceEvery: 'Elke',
      recurrenceLast: 'laatste',
      recurrenceMonthlyDay: 'Zelfde dag van de maand',
      recUnitDays: 'dagen',
      recUnitWeeks: 'weken',
      recUnitMonths: 'maanden',
      recUnitYears: 'jaren',
      recurrenceDays: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
      deleteRecurring: 'Dit is een terugkerend evenement.',
      deleteOnlyThis: 'Alleen dit evenement',
      deleteAllSeries: 'Alle evenementen in de reeks',
      voiceHint: 'TIK EN DICTEER: Afspraak · Datum\nTijd · Locatie · Prioriteit · Notities',
      parsing: 'Formulier invullen vanuit spraak...',
      parseError: 'De opname kon niet worden begrepen. Vul de velden handmatig in.',
      imageHint: 'AFBEELDING OF FOTO SCANNEN',
      imageParsing: 'Afbeelding lezen...',
      imageError: 'De afbeelding kon niet worden gelezen. Vul de velden handmatig in.',
      multiTitle: "Gevonden afspraken",
      multiSubtitle: "Controleer, pas aan en maak aan.",
      multiCreate: "Alles aanmaken",
      multiNoLabel: "Geen",
      multiCreating: "Aanmaken…",
      multiDetails: "Locatie en notities",
      overlapTitle: "Overlap",
      overlapText: "Overlapt met «{title}» ({time}). Je kunt toch opslaan.",
      subCalendarLabel: 'Label',
      subCalendarNone: 'Geen',
      visibilityLabel: 'Kalenders',
      visibilityPrivate: 'Alleen ik',
      visibilityGroup: 'Gedeelde kalender',
      visibilitySelectGroup: 'Groep selecteren',
      conflictTitle: 'Roosterconflict',
      conflictHint: 'Bezet op dit tijdstip:',
      reminderLabel: 'Herinnering',
      reminderNone: 'Geen herinnering',
      reminderOn: '✓ Actief',
      reminderOff: 'Inschakelen',
      reminderOptions: [
        { value: 0,    label: 'Op starttijd' },
        { value: 5,    label: '5 min van tevoren' },
        { value: 10,   label: '10 min van tevoren' },
        { value: 15,   label: '15 min van tevoren' },
        { value: 20,   label: '20 min van tevoren' },
        { value: 25,   label: '25 min van tevoren' },
        { value: 30,   label: '30 min van tevoren' },
        { value: 45,   label: '45 min van tevoren' },
        { value: 60,   label: '1 uur van tevoren' },
        { value: 120,  label: '2 uur van tevoren' },
        { value: 180,  label: '3 uur van tevoren' },
        { value: 720,  label: '12 uur van tevoren' },
        { value: 1440, label: '1 dag van tevoren' },
        { value: 2880, label: '2 dagen van tevoren' },
        { value: 10080, label: '1 week van tevoren' },
      ],
    },
    groups: {
      pageTitle: 'Gedeelde kalenders',
      noGroups: 'Nog geen kalenders',
      noGroupsSub: 'Maak een kalender aan of doe mee met een uitnodigingscode',
      createGroup: 'Kalender aanmaken',
      joinGroup: 'Meedoen met code',
      planLimit: 'Je hebt de limiet voor gedeelde agenda\'s van je abonnement bereikt. Upgrade om er meer te maken.',
      seePlans: 'Bekijk abonnementen',
      calendarFull: 'Deze gedeelde agenda zit vol voor het abonnement van de eigenaar.',
      groupNameLabel: 'Kalendernaam',
      groupNamePlaceholder: 'bijv. Familie De Vries',
      inviteCode: 'Uitnodigingscode',
      inviteCodePlaceholder: 'bijv. A3F9B2',
      join: 'Meedoen',
      create: 'Aanmaken',
      creating: 'Aanmaken…',
      joining: 'Meedoen…',
      members: 'Leden',
      you: '(jij)',
      admin: 'Admin',
      member: 'Lid',
      leave: 'Groep verlaten',
      leaveConfirm: 'Deze kalender verlaten?',
      delete: 'Groep verwijderen',
      deleteConfirm: 'Deze kalender verwijderen? Alle leden verliezen de toegang.',
      shareInvite: 'Code delen',
      copied: '✓ Code gekopieerd!',
      errorCreate: 'Kon kalender niet aanmaken. Probeer opnieuw.',
      errorJoin: 'Ongeldige code. Controleer en probeer opnieuw.',
      errorAlready: 'Je bent al lid van deze kalender.',
      cancel: 'Annuleren',
      saving: 'Opslaan…',
    },
    install: {
      title: '📲 MeMo installeren',
      subtitle: 'Installeer het als een echte app — geen App Store nodig.',
      installBtn: '📲 Installeren op dit apparaat',
      installed: '✓ Geïnstalleerd!',
      oneLogin: '🔐 Eén keer inloggen — Memo onthoudt je voor altijd.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Open deze link in Safari', 'Tik op Delen □↑ onderaan', 'Tik op "Zet op beginscherm"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Open deze link in Chrome', 'Tik op ⋮ rechtsboven', 'Tik op "Toevoegen aan startscherm"'],
      },
      desktop: {
        title: '💻 Computer (Chrome)',
        steps: ['Open deze link in Chrome', 'Klik op het 📲 icoon in de adresbalk', 'Klik op "Installeren"'],
      },
    },
  },

  pt: {
    nav: { today: 'Hoje', add: 'Novo', tasks: 'Notas', search: 'Buscar', settings: 'Opções', groups: 'Grupos' },
    search: {
      placeholder: 'Pesquisar eventos…',
      searching: 'A pesquisar…',
      noResults: 'Sem resultados para',
      noResultsHint: 'Tente palavras diferentes',
      hint: 'ex. «dentista», «reunião», «aniversário»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Falha ao entrar',
      subtitle: 'Algo correu mal. Por favor tente novamente.',
      back: 'Voltar',
    },
    login: {
      title: 'Memo',
      subtitle: 'O seu gestor pessoal de notas e eventos com entrada de voz',
      features: [
        'Adicione eventos por voz enquanto conduz',
        'Priorize com cores',
        'Sincroniza com o Google Calendário',
        'Resumo diário no Telegram às 7:00',
      ],
      signIn: 'Entrar com o Google',
    },
    onboarding: {
      demo: {
        views: ["Dia", "Sem.", "Mês", "Ano"],
        allDay: "Todo o dia",
        prio: ["Alta", "Média", "Baixa"],
        labels: ["TRABALHO", "DESPORTO", "CASA"],
        shared: "Partilhado",
        create: "Criar",
        join: "Entrar com código",
        sharedTitle: "Calendários partilhados",
        searchTitle: "Procurar eventos",
        searchHint: "ex. dentista, reunião…",
        notePh: "Escrever uma nota…",
        settingsTitle: "Definições",
        light: "Claro",
        dark: "Escuro",
        signout: "Sair",
        ics: "Transferir .ics",
        notif: "Notificações",
        summaryLbl: "Resumo",
        time: "Hora",
        telegram: "Enviar por Telegram",
        reminder: "30 min antes",
        greeting: "Bom dia",
        repeat: ["Todos os dias", "Toda a sem."],
        ev: ["Reunião", "Ginásio", "Compras", "Dentista", "Jantar", "Entrega"],
      },
      skip: "Saltar",
      next: "Seguinte →",
      start: "Começar",
      slides: [
        { key: "calendar", title: "O calendário que fala consigo", text: "Dia, semana, mês e ano: veja os seus compromissos como preferir. E com um toque lê em voz alta o dia e a semana." },
        { key: "add", title: "Adicione como quiser", text: "Crie um evento a escrever, com a voz ou com uma foto: fotografe uma folha, um e-mail ou um cartaz e a IA preenche tudo. Também pode anexar documentos ao evento." },
        { key: "planner", title: "Organize o seu dia", text: "O planeador diário e semanal mostra horários livres e ocupados: encaixa tudo num relance." },
        { key: "multi", title: "Pesquise, anote, partilhe", text: "Encontre qualquer evento num segundo, tenha as notas sempre à mão e partilhe o calendário com a família ou a equipa, cada um com a sua cor." },
        { key: "summary", title: "Todas as manhãs, o seu resumo", text: "Receba os compromissos do dia no Telegram e por e-mail, ordenados por hora e prioridade. Comece o dia sem esquecer nada." },
        { key: "settings", title: "Ponha o MeMo a trabalhar por si", text: "Escolha idioma, hora do resumo, notificações, toque e tema: o MeMo adapta-se à sua forma de trabalhar." },
      ],
    },

    welcome: {
      greeting: 'Bem-vindo',
      subtitle: 'Escolha o seu idioma para começar',
      saving: 'A guardar...',
      continue: 'Continuar →',
      countryLabel: 'O seu país',
      countryPlaceholder: 'Selecione o seu país...',
      themeLabel: 'Escolha o seu tema',
      timezoneLabel: 'Fuso horário',
      timezoneHint: 'Altere isto quando viajar',
    },
    eventGenericType: 'Evento',
    today: {
      noEvents: 'Sem eventos agendados',
      eventsCount: (n) => `${n} evento${n !== 1 ? 's' : ''} hoje`,
      emptyTitle: 'Dia livre hoje!',
      emptySub: 'Toque em + para adicionar um evento',
      error: 'Não foi possível carregar os eventos. Por favor tente novamente.',
      retry: 'Tentar novamente',
      signOutHint: 'Se o problema persistir, tente terminar sessão e entrar novamente.',
      slowConnection: 'Ligação lenta… ainda a carregar.',
      aiLimitReached: 'Usaste as 30 entradas de IA (voz e foto) do plano Free deste mês. Renovam-se no próximo mês.',
      weekList: 'Lista',
      weekGrid: 'Grelha',
      conflictWarning: 'Atenção: sobrepõe-se a «{title}» ({time}). Guardar mesmo assim?',
      tomorrow: 'Amanhã',
      today: 'Hoje',
      yesterday: 'Ontem',
      snooze: 'Adiar',
      stop: 'Parar',
      prevWeek: 'Semana anterior',
      nextWeek: 'Semana seguinte',
      microphone: 'Microfone',
      noEventsDay: 'Sem eventos',
      emptyTitleDay: 'Nada aqui',
      eventsCountDay: (n) => `${n} evento${n !== 1 ? 's' : ''}`,
      prevDay: 'Dia anterior',
      nextDay: 'Dia seguinte',
      filterAll: 'Todos',
      filterMine: 'Só eu',
      ttsButton: 'Ouvir',
      ttsStop: 'Parar',
      ttsNoEvents: 'Sem eventos hoje.',
      ttsIntro: 'Hoje tem {n} eventos',
      viewDay: 'Dia',
      viewWeek: 'Semana',
      viewMonth: 'Mês',
      viewYear: 'Ano',
      viewSlots: 'Plan',
      slotsHint: 'Toque num espaço livre para adicionar um evento',
      slotsTapDay: 'Toque num dia para o abrir',
      slotsFree: 'Livre',
      slotsBusy: 'Ocupado',
      weekOf: 'Semana',
    },
    settings: {
      pageTitle: 'Definições',
      account: 'Conta',
      signOut: 'Terminar sessão',
      themeColor: "Cor do tema da app",
      themeColorBtn: "Cor da app",
      a11yReminders: "Lembretes acessíveis (vibração + voz)",
      a11yRemindersHint: "À hora do lembrete o telefone vibra e lê o evento em voz alta.",
      a11yMode: "Modo de acessibilidade",
      a11yModeHint: "Ativa em conjunto alto contraste e lembretes com vibração e voz.",
      a11yVibration: "Vibração",
      a11ySpeak: "Ler evento em voz alta",
      a11yContrast: "Alto contraste",
      usageTab: "Uso de dados",
      a11yTab: "Acessibilidade",
      updateAvailable: "Está disponível uma nova versão.",
      updateButton: "Atualizar",
      bigText: "Texto grande",
      bigTextOn: "ativado",
      bigTextOff: "desativado",
      bigLarge: "Grande",
      bigXLarge: "Muito grande",
      feedTitle: "Ver o MeMo no Calendário Apple",
      feedDesc: "Para iPhone e iCloud: os teus compromissos do MeMo, dentro do Calendário Apple.",
      feedCopy: "Copiar link",
      feedCopied: "Copiado!",
      feedSubscribe: "Ver no meu calendário",
      feedHint: "iPhone: Definições → Calendário → Contas → Adicionar → Outra → Adicionar calendário subscrito. Mac: Ficheiro → Nova subscrição de calendário.",
      feedConnect: "Liga os teus calendários",
      feedRegenerate: "Regenerar link",
      saved: '✓ Definições guardadas!',
      saveError: 'Erro ao guardar as definições. Por favor tente novamente.',
      language: 'Idioma',
      telegramSection: 'Resumo diário no Telegram',
      sendTime: 'Hora de envio',
      timezone: 'O seu fuso horário',
      tzDetected: 'O seu dispositivo está em',
      tzUseDetected: 'Usar este fuso horário',
      tzAutoDetect: 'Detetar automaticamente',
      chatId: 'ID do chat do Telegram',
      voicePitch: 'Tom',
      voicePitchLow: 'Grave',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Agudo',
      pushIntro: 'As notificações chegam ao telemóvel mesmo com a app fechada. Toca para ativar e testar.',
      pushDenied: 'Permissão negada. Ativa as notificações do MeMo nas definições do telemóvel/navegador.',
      pushUnsupported: 'Este dispositivo não suporta notificações push. No iPhone, instala o MeMo no ecrã principal.',
      pushUnsupportedApp: 'Na aplicação instalada os lembretes vêm do próprio telemóvel, não do navegador: garante apenas que deste ao MeMo permissão para mostrar notificações. O Telegram e o email ficam a mais, se os quiseres.',
      pushFailed: 'Falhou. Tenta novamente ou verifica as definições de notificações.',
      pushSending: 'A enviar…',
      pushTestButton: '🔔 Ativar / Enviar teste',
      pushSent: '✓ Enviada! Verifica o teu dispositivo.',
      pushError: 'Erro — ativa as notificações no navegador.',
      pushNoDevice: 'Nenhum dispositivo alcançado. Tenta novamente ou desativa e reativa as notificações do MeMo nas definições do navegador.',
      chatIdPlaceholder: 'ex. 123456789',
      chatIdHelp: (bot) => `Como obter o seu Chat ID: abra o Telegram, pesquise @${bot}, envie uma mensagem, depois toque no link no guia de configuração.`,
      testSending: 'A enviar...',
      testButton: '📨 Enviar mensagem de teste agora',
      testSuccess: '✓ Mensagem enviada! Verifique o Telegram.',
      testError: 'Falha ao enviar. Certifique-se de que o seu Chat ID está guardado.',
      saving: 'A guardar...',
      saveButton: 'Guardar definições',
      telegramSetupTitle: 'Como ligar o Telegram:',
      telegramStep1: (bot) => `Abra o Telegram e pesquise @${bot}`,
      telegramStep2: 'Envie qualquer mensagem para começar',
      telegramStep3: 'Abra @userinfobot → envie /start → copie o seu Chat ID',
      telegramStep4: 'Cole-o no campo abaixo e guarde',
      supportTitle: "Ajuda e suporte",
      supportDesc: "Precisas de ajuda ou queres relatar um problema? Escreve-nos, respondemos assim que possível.",
      emailSupport: "Enviar e-mail",
      contactSupport: 'Contactar o suporte no Telegram',
      themeSection: 'Aparência',
      themeLabel: 'Tema',
      themeLight: 'Claro',
      themeDark: 'Escuro',
      countryLabel: 'País',
      subCalendarsSection: 'Etiquetas',
      subCalendarName: 'Nome',
      subCalendarAdd: 'Adicionar etiqueta',
      subCalendarOwn: 'Colorir os meus eventos',
      subCalendarPickColour: 'Escolhe uma cor',
      subCalendarAutoHint: 'As etiquetas dos calendários que não são teus recebem uma cor provisória: toca-lhes para escolheres a tua.',
      subCalendarDeleteConfirm: 'Eliminar esta etiqueta? Os eventos não serão eliminados.',
      summarySection: 'Resumo Matinal',
      summaryNowhere: "O resumo não sai: o MeMo não sabe para onde o enviar. Escreve o teu email aqui em baixo, ou liga o Telegram.",
      summaryChannels: 'Para onde enviar',
      viaTelegram: 'Enviar pelo Telegram',
      viaEmail: 'Enviar por email',
      emailAnyProvider: 'Funciona com qualquer endereço — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Resumo por e-mail',
      emailLabel: 'O seu endereço de e-mail',
      emailHelp: 'Qualquer e-mail — Gmail, Outlook, iCloud, trabalho, etc.',
      emailTestSending: '⏳ A enviar...',
      emailTestButton: '📧 Enviar e-mail de teste',
      emailTestSuccess: '✓ E-mail enviado! Verifique a sua caixa de entrada.',
      emailTestError: 'Falha ao enviar o e-mail. Verifique a configuração do servidor.',
      voiceLabel: 'Voz de leitura',
      voiceDefault: 'Predefinição do sistema',
      voiceTest: '▶ Testar voz',
      voiceSection: "Voz e leitura",
      voiceSpeed: "Velocidade",
      voiceSlow: "Lenta",
      voiceFast: "Rápida",
      voiceNoVoices: 'Nenhuma voz disponível para este idioma neste dispositivo.',
      voiceFemale: '♀ Feminina',
      voiceMale: '♂ Masculina',
      voiceNoMatch: 'Nenhuma voz correspondente — usando a predefinição do sistema.',
      alarmSection: 'Definições de notificação',
      alarmVolume: 'Volume',
      alarmSnooze: 'Duração da soneca',
      alarmMaxSnooze: 'Máx. sonecas',
      alarmForever: 'Sempre',
      alarmMin: 'min',
      alarmSound: 'Som',
      weatherReset: 'Meteorologia',
      weatherActive: 'Meteorologia ativa',
      weatherDisable: 'Desativar',
      weatherDesc: 'Ativar para ver a temperatura e a previsão no ecrã principal.',
      weatherActivating: 'A aguardar…',
      weatherEnable: 'Ativar meteorologia',
      weatherReEnable: 'Reativar meteorologia',
      weatherDeniedHint: 'Acesso à localização bloqueado pelo browser. Vá às definições do browser para desbloquear e tente novamente.',
      weatherDeniedHintApp: "O acesso à localização foi recusado. Permite-o ao MeMo nas definições do telemóvel e tenta de novo.",
      weatherUnavailable: 'GPS não disponível neste dispositivo.',
      exportLabel: 'Ligar ao Apple Calendar, Outlook ou outra app não Google',
      exportButton: 'Descarregar .ics',
      exportHint: 'Importar no Apple Calendar, Outlook ou outra aplicação',
      exporting: 'A exportar…',
    },
    tasks: {
      emptyTitle: 'Ainda sem notas',
      emptySub: 'ex. Comprar pão · Ligar ao dentista',
      addPlaceholder: 'Escreve uma nota…',
      doneSection: "Concluídas",
      deleteNoteConfirm: "Eliminar esta nota definitivamente?",
      restoreNote: "Toca para restaurar",
      completed: 'concluídas',
      noScope: 'Sai e volta a entrar para ativar as notas',
      deleteConfirm: 'Eliminar esta nota?',
    },
    form: {
      allDay: "Todo o dia",
      timeMissing: "Escolhe uma hora, ou ativa «Todo o dia».",
      pageTitle: 'Novo evento',
      required: 'Por favor preencha todos os campos obrigatórios.',
      saveError: 'Erro ao guardar o evento. Por favor tente novamente.',
      titleLabel: 'Evento',
      titlePlaceholder: 'ex. Consulta médica',
      cancelButton: 'Cancelar',
      recurringEditTitle: 'Editar evento recorrente',
      recurringThisOnly: 'Apenas este evento',
      recurringAllSeries: 'Todos os eventos da série',
      recurringNote: 'Evento recorrente — as alterações aplicam-se apenas a esta ocorrência',
      photoLabel: 'Foto',
      openInMaps: 'Abrir no Google Maps',
      recurringEvent: 'Evento recorrente',
      typeLabel: 'Tipo *',
      typeDefault: 'Selecionar tipo...',
      datetimeLabel: 'Data e hora',
      dateFromLabel: 'De',
      dateToLabel: 'Até',
      locationLabel: 'Local (opcional)',
      locationPlaceholder: 'ex. Rua Augusta 10, Lisboa',
      priorityLabel: 'Prioridade',
      priorities: [
        { value: 'red',    label: 'Alta',    sublabel: 'Tem de ser feito' },
        { value: 'yellow', label: 'Médio',   sublabel: 'Deve ser feito' },
        { value: 'green',  label: 'Baixo',   sublabel: 'Se houver tempo' },
      ],
      notesLabel: 'Notas (opcional)',
      notesPlaceholder: 'Adicione notas ou use a voz...',
      attLabel: 'Anexos',
      attAdd: '📎 Anexar foto ou PDF',
      attHint: 'Máx. 5 ficheiros, removidos 5 dias depois',
      attTooMany: 'Máx. 5 anexos',
      attBadType: 'Só fotos e PDF',
      attTooBig: 'Ficheiro muito grande (máx. 10 MB)',
      saving: 'A guardar...',
      saveButton: '✓ Guardar evento',
      editPageTitle: 'Editar evento',
      editButton: 'Editar',
      updateButton: '✓ Atualizar',
      dictateLabel: 'DITE',
      deleteButton: 'Eliminar',
      deleteConfirm: 'Eliminar este evento do Google Calendário?',
      deleteError: 'Erro ao eliminar. Por favor tente novamente.',
      recurrenceLabel: 'Repetição',
      recurrenceNone: 'Não repete',
      recurrenceDaily: 'Todos os dias',
      recurrenceWeekly: 'Todas as semanas',
      recurrenceMonthly: 'Todos os meses',
      recurrenceYearly: 'Todos os anos',
      recurrenceEnds: 'Lembrar',
      recurrenceNever: 'Para sempre',
      recurrenceAfter: 'Após',
      recurrenceTimes: 'vezes',
      recurrenceOnDate: 'Até',
      recurrenceEvery: 'A cada',
      recurrenceLast: 'último',
      recurrenceMonthlyDay: 'Mesmo dia do mês',
      recUnitDays: 'dias',
      recUnitWeeks: 'semanas',
      recUnitMonths: 'meses',
      recUnitYears: 'anos',
      recurrenceDays: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sá'],
      deleteRecurring: 'Este é um evento recorrente.',
      deleteOnlyThis: 'Apenas este evento',
      deleteAllSeries: 'Todos os eventos da série',
      voiceHint: 'TOQUE E DITE: Evento · Data\nHora · Local · Prioridade · Notas',
      parsing: 'A preencher formulário a partir da voz...',
      parseError: 'Não foi possível entender a gravação. Por favor preencha os campos manualmente.',
      imageHint: 'DIGITALIZAR IMAGEM OU FOTO',
      imageParsing: 'A ler imagem...',
      imageError: 'Não foi possível ler a imagem. Por favor preencha os campos manualmente.',
      multiTitle: "Eventos encontrados",
      multiSubtitle: "Verifica, corrige e cria.",
      multiCreate: "Criar todos",
      multiNoLabel: "Nenhuma",
      multiCreating: "A criar…",
      multiDetails: "Local e notas",
      overlapTitle: "Sobreposição",
      overlapText: "Sobrepõe-se a «{title}» ({time}). Podes guardar mesmo assim.",
      subCalendarLabel: 'Etiqueta',
      subCalendarNone: 'Nenhum',
      visibilityLabel: 'Calendários',
      visibilityPrivate: 'Só eu',
      visibilityGroup: 'Calendário partilhado',
      visibilitySelectGroup: 'Selecionar grupo',
      conflictTitle: 'Conflito de horário',
      conflictHint: 'Ocupados neste horário:',
      reminderLabel: 'Lembrete',
      reminderNone: 'Sem lembrete',
      reminderOn: '✓ Ativo',
      reminderOff: 'Ativar',
      reminderOptions: [
        { value: 0,    label: 'No início' },
        { value: 5,    label: '5 min antes' },
        { value: 10,   label: '10 min antes' },
        { value: 15,   label: '15 min antes' },
        { value: 20,   label: '20 min antes' },
        { value: 25,   label: '25 min antes' },
        { value: 30,   label: '30 min antes' },
        { value: 45,   label: '45 min antes' },
        { value: 60,   label: '1 hora antes' },
        { value: 120,  label: '2 horas antes' },
        { value: 180,  label: '3 horas antes' },
        { value: 720,  label: '12 horas antes' },
        { value: 1440, label: '1 dia antes' },
        { value: 2880, label: '2 dias antes' },
        { value: 10080, label: '1 semana antes' },
      ],
    },
    groups: {
      pageTitle: 'Calendários partilhados',
      noGroups: 'Sem calendários ainda',
      noGroupsSub: 'Crie um calendário ou junte-se com um código de convite',
      createGroup: 'Criar calendário',
      joinGroup: 'Entrar com código',
      planLimit: 'Atingiste o limite de calendários partilhados do teu plano. Faz upgrade para criar mais.',
      seePlans: 'Ver planos',
      calendarFull: 'Este calendário partilhado está cheio para o plano do proprietário.',
      groupNameLabel: 'Nome do calendário',
      groupNamePlaceholder: 'ex. Família Silva',
      inviteCode: 'Código de convite',
      inviteCodePlaceholder: 'ex. A3F9B2',
      join: 'Entrar',
      create: 'Criar',
      creating: 'A criar…',
      joining: 'A entrar…',
      members: 'Membros',
      you: '(tu)',
      admin: 'Admin',
      member: 'Membro',
      leave: 'Sair do grupo',
      leaveConfirm: 'Sair deste grupo?',
      delete: 'Eliminar grupo',
      deleteConfirm: 'Eliminar este grupo? Todos os membros perderão o acesso.',
      shareInvite: 'Partilhar código',
      copied: '✓ Código copiado!',
      errorCreate: 'Não foi possível criar o grupo. Tente novamente.',
      errorJoin: 'Código inválido. Verifique e tente novamente.',
      errorAlready: 'Já é membro deste grupo.',
      cancel: 'Cancelar',
      saving: 'A guardar…',
    },
    install: {
      title: '📲 Instalar MeMo',
      subtitle: 'Instala como uma app real — sem App Store.',
      installBtn: '📲 Instalar neste dispositivo',
      installed: '✓ Instalado!',
      oneLogin: '🔐 Acede uma vez só — o Memo lembra-te para sempre.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Abre este link no Safari', 'Toca em Partilhar □↑ em baixo', 'Toca em "Adicionar ao ecrã inicial"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Abre este link no Chrome', 'Toca em ⋮ no canto superior direito', 'Toca em "Adicionar ao ecrã inicial"'],
      },
      desktop: {
        title: '💻 Computador (Chrome)',
        steps: ['Abre este link no Chrome', 'Clica no ícone 📲 na barra de endereços', 'Clica em "Instalar"'],
      },
    },
  },

  'pt-BR': {
    nav: { today: 'Hoje', add: 'Novo', tasks: 'Notas', search: 'Buscar', settings: 'Ajustes', groups: 'Grupos' },
    search: {
      placeholder: 'Pesquisar eventos…',
      searching: 'Pesquisando…',
      noResults: 'Sem resultados para',
      noResultsHint: 'Tente palavras diferentes',
      hint: 'ex. «dentista», «reunião», «aniversário»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Falha ao entrar',
      subtitle: 'Algo deu errado. Por favor, tente novamente.',
      back: 'Voltar',
    },
    login: {
      title: 'Memo',
      subtitle: 'Seu gerenciador pessoal de notas e eventos com entrada de voz',
      features: [
        'Adicione eventos por voz enquanto dirige',
        'Priorize com cores',
        'Sincroniza com o Google Agenda',
        'Resumo diário no Telegram às 7h',
      ],
      signIn: 'Entrar com o Google',
    },
    onboarding: {
      demo: {
        views: ["Dia", "Sem.", "Mês", "Ano"],
        allDay: "Dia todo",
        prio: ["Alta", "Média", "Baixa"],
        labels: ["TRABALHO", "ESPORTE", "CASA"],
        shared: "Compartilhado",
        create: "Criar",
        join: "Entrar com código",
        sharedTitle: "Calendários compartilhados",
        searchTitle: "Buscar eventos",
        searchHint: "ex. dentista, reunião…",
        notePh: "Escrever uma nota…",
        settingsTitle: "Ajustes",
        light: "Claro",
        dark: "Escuro",
        signout: "Sair",
        ics: "Baixar .ics",
        notif: "Notificações",
        summaryLbl: "Resumo",
        time: "Hora",
        telegram: "Enviar pelo Telegram",
        reminder: "30 min antes",
        greeting: "Bom dia",
        repeat: ["Todo dia", "Toda sem."],
        ev: ["Reunião", "Academia", "Compras", "Dentista", "Jantar", "Entrega"],
      },
      skip: "Pular",
      next: "Avançar →",
      start: "Começar",
      slides: [
        { key: "calendar", title: "O calendário que fala com você", text: "Dia, semana, mês e ano: veja seus compromissos como preferir. E com um toque ele lê em voz alta o dia e a semana." },
        { key: "add", title: "Adicione do seu jeito", text: "Crie um evento digitando, com a voz ou com uma foto: fotografe uma folha, um e-mail ou um cartaz e a IA preenche tudo. Você também pode anexar documentos ao evento." },
        { key: "planner", title: "Organize o seu dia", text: "O planejador diário e semanal mostra horários livres e ocupados: encaixa tudo num relance." },
        { key: "multi", title: "Pesquise, anote, compartilhe", text: "Encontre qualquer evento num segundo, tenha as notas sempre à mão e compartilhe o calendário com a família ou a equipe, cada um com a sua cor." },
        { key: "summary", title: "Toda manhã, o seu resumo", text: "Receba os compromissos do dia no Telegram e por e-mail, ordenados por hora e prioridade. Comece o dia sem esquecer nada." },
        { key: "settings", title: "Ponha o MeMo para trabalhar por você", text: "Escolha idioma, horário do resumo, notificações, toque e tema: o MeMo se adapta ao seu jeito de trabalhar." },
      ],
    },

    welcome: {
      greeting: 'Bem-vindo',
      subtitle: 'Escolha seu idioma para começar',
      saving: 'Salvando...',
      continue: 'Continuar →',
      countryLabel: 'Seu país',
      countryPlaceholder: 'Selecione seu país...',
      themeLabel: 'Escolha seu tema',
      timezoneLabel: 'Fuso horário',
      timezoneHint: 'Mude quando você viajar',
    },
    eventGenericType: 'Evento',
    today: {
      noEvents: 'Nenhum evento agendado',
      eventsCount: (n) => `${n} evento${n !== 1 ? 's' : ''} hoje`,
      emptyTitle: 'Dia livre hoje!',
      emptySub: 'Toque em + para adicionar um evento',
      error: 'Não foi possível carregar os eventos. Tente novamente.',
      retry: 'Tentar novamente',
      signOutHint: 'Se o problema persistir, tente sair e entrar novamente.',
      slowConnection: 'Conexão lenta… ainda carregando.',
      aiLimitReached: 'Você usou as 30 entradas de IA (voz e foto) do plano Free neste mês. Elas renovam no próximo mês.',
      weekList: 'Lista',
      weekGrid: 'Grade',
      conflictWarning: 'Atenção: sobrepõe “{title}” ({time}). Salvar mesmo assim?',
      tomorrow: 'Amanhã',
      today: 'Hoje',
      yesterday: 'Ontem',
      snooze: 'Adiar',
      stop: 'Parar',
      prevWeek: 'Semana anterior',
      nextWeek: 'Próxima semana',
      microphone: 'Microfone',
      noEventsDay: 'Nenhum evento',
      emptyTitleDay: 'Nada aqui',
      eventsCountDay: (n) => `${n} evento${n !== 1 ? 's' : ''}`,
      prevDay: 'Dia anterior',
      nextDay: 'Próximo dia',
      filterAll: 'Todos',
      filterMine: 'Só eu',
      ttsButton: 'Ouvir',
      ttsStop: 'Parar',
      ttsNoEvents: 'Nenhum evento hoje.',
      ttsIntro: 'Hoje você tem {n} eventos',
      viewDay: 'Dia',
      viewWeek: 'Semana',
      viewMonth: 'Mês',
      viewYear: 'Ano',
      viewSlots: 'Plan',
      slotsHint: 'Toque em um espaço livre para adicionar um evento',
      slotsTapDay: 'Toque em um dia para abri-lo',
      slotsFree: 'Livre',
      slotsBusy: 'Ocupado',
      weekOf: 'Semana',
    },
    settings: {
      pageTitle: 'Configurações',
      account: 'Conta',
      signOut: 'Sair',
      themeColor: "Cor do tema do app",
      themeColorBtn: "Cor do app",
      a11yReminders: "Lembretes acessíveis (vibração + voz)",
      a11yRemindersHint: "Na hora do lembrete o telefone vibra e lê o evento em voz alta.",
      a11yMode: "Modo de acessibilidade",
      a11yModeHint: "Ativa junto alto contraste e lembretes com vibração e voz.",
      a11yVibration: "Vibração",
      a11ySpeak: "Ler evento em voz alta",
      a11yContrast: "Alto contraste",
      usageTab: "Uso de dados",
      a11yTab: "Acessibilidade",
      updateAvailable: "Uma nova versão está disponível.",
      updateButton: "Atualizar",
      bigText: "Texto grande",
      bigTextOn: "ativado",
      bigTextOff: "desativado",
      bigLarge: "Grande",
      bigXLarge: "Muito grande",
      feedTitle: "Nos seus calendários",
      feedDesc: "Para iPhone e iCloud: seus compromissos do MeMo, dentro do Calendário Apple.",
      feedCopy: "Copiar link",
      feedCopied: "Copiado!",
      feedSubscribe: "Ver no meu calendário",
      feedHint: "iPhone: Ajustes → Calendário → Contas → Adicionar → Outra → Adicionar calendário assinado. Mac: Arquivo → Nova assinatura de calendário.",
      feedConnect: "Conecte seus calendários",
      feedRegenerate: "Regenerar link",
      saved: '✓ Configurações salvas!',
      saveError: 'Erro ao salvar configurações. Tente novamente.',
      language: 'Idioma',
      telegramSection: 'Resumo Diário no Telegram',
      sendTime: 'Horário de envio',
      timezone: 'Seu fuso horário',
      tzDetected: 'Seu dispositivo está em',
      tzUseDetected: 'Usar este fuso horário',
      tzAutoDetect: 'Detectar automaticamente',
      chatId: 'Chat ID do Telegram',
      voicePitch: 'Tom',
      voicePitchLow: 'Grave',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Agudo',
      pushIntro: 'As notificações chegam ao celular mesmo com o app fechado. Toque para ativar e testar.',
      pushDenied: 'Permissão negada. Ative as notificações do MeMo nas configurações do celular/navegador.',
      pushUnsupported: 'Este dispositivo não suporta push. No iPhone, instale o MeMo na tela de início.',
      pushUnsupportedApp: 'No aplicativo instalado os lembretes vêm do próprio celular, não do navegador: só garanta que o MeMo tem permissão para mostrar notificações. Telegram e e-mail ficam como extra, se você quiser.',
      pushFailed: 'Falhou. Tente novamente ou verifique as configurações de notificações.',
      pushSending: 'Enviando…',
      pushTestButton: '🔔 Ativar / Enviar teste',
      pushSent: '✓ Enviada! Verifique seu dispositivo.',
      pushError: 'Erro — ative as notificações no navegador.',
      pushNoDevice: 'Nenhum dispositivo alcançado. Tente novamente ou desative e reative as notificações do MeMo nas configurações do navegador.',
      chatIdPlaceholder: 'ex. 123456789',
      chatIdHelp: (bot) => `Como obter seu Chat ID: abra o Telegram, pesquise @${bot}, envie uma mensagem, depois toque no link do guia de configuração.`,
      testSending: 'Enviando...',
      testButton: '📨 Enviar mensagem de teste agora',
      testSuccess: '✓ Mensagem enviada! Verifique o Telegram.',
      testError: 'Falha ao enviar. Certifique-se de que seu Chat ID está salvo.',
      saving: 'Salvando...',
      saveButton: 'Salvar configurações',
      telegramSetupTitle: 'Como conectar o Telegram:',
      telegramStep1: (bot) => `Abra o Telegram e pesquise @${bot}`,
      telegramStep2: 'Envie qualquer mensagem para começar',
      telegramStep3: 'Abra @userinfobot → envie /start → copie seu Chat ID',
      telegramStep4: 'Cole no campo abaixo e salve',
      supportTitle: "Ajuda e suporte",
      supportDesc: "Precisa de ajuda ou quer relatar um problema? Escreva para nós, respondemos assim que possível.",
      emailSupport: "Enviar e-mail",
      contactSupport: 'Falar com o suporte no Telegram',
      themeSection: 'Aparência',
      themeLabel: 'Tema',
      themeLight: 'Claro',
      themeDark: 'Escuro',
      countryLabel: 'País',
      subCalendarsSection: 'Etiquetas',
      subCalendarName: 'Nome',
      subCalendarAdd: 'Adicionar etiqueta',
      subCalendarOwn: 'Colorir os meus eventos',
      subCalendarPickColour: 'Escolha uma cor',
      subCalendarAutoHint: 'As etiquetas dos calendários que não são seus recebem uma cor provisória: toque nelas para escolher a sua.',
      subCalendarDeleteConfirm: 'Excluir esta etiqueta? Os eventos não serão excluídos.',
      summarySection: 'Resumo Matinal',
      summaryNowhere: "O resumo não sai: o MeMo não sabe para onde enviar. Escreva seu e-mail aqui embaixo, ou conecte o Telegram.",
      summaryChannels: 'Para onde enviar',
      viaTelegram: 'Enviar pelo Telegram',
      viaEmail: 'Enviar por email',
      emailAnyProvider: 'Funciona com qualquer endereço — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Resumo por e-mail',
      emailLabel: 'Seu endereço de e-mail',
      emailHelp: 'Qualquer e-mail — Gmail, Outlook, iCloud, trabalho, etc.',
      emailTestSending: '⏳ Enviando...',
      emailTestButton: '📧 Enviar e-mail de teste',
      emailTestSuccess: '✓ E-mail enviado! Verifique sua caixa de entrada.',
      emailTestError: 'Falha ao enviar e-mail. Verifique a configuração do servidor.',
      voiceLabel: 'Voz de leitura',
      voiceDefault: 'Padrão do sistema',
      voiceTest: '▶ Testar voz',
      voiceSection: "Voz e leitura",
      voiceSpeed: "Velocidade",
      voiceSlow: "Lenta",
      voiceFast: "Rápida",
      voiceNoVoices: 'Nenhuma voz disponível para este idioma neste dispositivo.',
      voiceFemale: '♀ Feminina',
      voiceMale: '♂ Masculino',
      voiceNoMatch: 'Nenhuma voz correspondente — usando o padrão do sistema.',
      alarmSection: 'Configurações de notificação',
      alarmVolume: 'Volume',
      alarmSnooze: 'Duração do soneca',
      alarmMaxSnooze: 'Máx. sonecas',
      alarmForever: 'Sempre',
      alarmMin: 'min',
      alarmSound: 'Som',
      weatherReset: 'Clima',
      weatherActive: 'Clima ativo',
      weatherDisable: 'Desativar',
      weatherDesc: 'Ative para ver a temperatura e a previsão na tela principal.',
      weatherActivating: 'Aguardando…',
      weatherEnable: 'Ativar clima',
      weatherReEnable: 'Reativar clima',
      weatherDeniedHint: 'Acesso à localização bloqueado pelo navegador. Acesse as configurações do navegador para desbloquear e tente novamente.',
      weatherDeniedHintApp: "O acesso à localização foi negado. Permita para o MeMo nas configurações do celular e tente de novo.",
      weatherUnavailable: 'GPS não disponível neste dispositivo.',
      exportLabel: 'Conectar ao Apple Calendar, Outlook ou outro app não Google',
      exportButton: 'Baixar .ics',
      exportHint: 'Importar no Apple Calendar, Outlook ou qualquer outro app',
      exporting: 'Exportando…',
    },
    tasks: {
      emptyTitle: 'Ainda sem notas',
      emptySub: 'ex. Comprar pão · Ligar para o dentista',
      addPlaceholder: 'Escreva uma nota…',
      doneSection: "Concluídas",
      deleteNoteConfirm: "Excluir esta nota definitivamente?",
      restoreNote: "Toque para restaurar",
      completed: 'concluídas',
      noScope: 'Saia e entre novamente para ativar as notas',
      deleteConfirm: 'Excluir esta nota?',
    },
    form: {
      allDay: "Dia todo",
      timeMissing: "Escolha um horário, ou ative “Dia todo”.",
      pageTitle: 'Novo Evento',
      required: 'Preencha todos os campos obrigatórios.',
      saveError: 'Erro ao salvar o evento. Tente novamente.',
      titleLabel: 'Evento',
      titlePlaceholder: 'ex. Consulta médica',
      cancelButton: 'Cancelar',
      recurringEditTitle: 'Editar evento recorrente',
      recurringThisOnly: 'Apenas este evento',
      recurringAllSeries: 'Todos os eventos da série',
      recurringNote: 'Evento recorrente — as alterações se aplicam apenas a esta ocorrência',
      photoLabel: 'Foto',
      openInMaps: 'Abrir no Google Maps',
      recurringEvent: 'Evento recorrente',
      typeLabel: 'Tipo *',
      typeDefault: 'Selecionar tipo...',
      datetimeLabel: 'Data e hora',
      dateFromLabel: 'De',
      dateToLabel: 'Até',
      locationLabel: 'Local (opcional)',
      locationPlaceholder: 'ex. Av. Paulista 100, São Paulo',
      priorityLabel: 'Prioridade',
      priorities: [
        { value: 'red',    label: 'Alta',    sublabel: 'Precisa ser feito' },
        { value: 'yellow', label: 'Média',   sublabel: 'Deve ser feito' },
        { value: 'green',  label: 'Baixa',   sublabel: 'Se tiver tempo' },
      ],
      notesLabel: 'Notas (opcional)',
      notesPlaceholder: 'Adicione notas ou use a voz...',
      attLabel: 'Anexos',
      attAdd: '📎 Anexar foto ou PDF',
      attHint: 'Máx. 5 arquivos, removidos 5 dias depois',
      attTooMany: 'Máx. 5 anexos',
      attBadType: 'Só fotos e PDF',
      attTooBig: 'Arquivo muito grande (máx. 10 MB)',
      saving: 'Salvando...',
      saveButton: '✓ Salvar Evento',
      editPageTitle: 'Editar Evento',
      editButton: 'Editar',
      updateButton: '✓ Atualizar',
      dictateLabel: 'DITE',
      deleteButton: 'Excluir',
      deleteConfirm: 'Excluir este evento do Google Agenda?',
      deleteError: 'Erro ao excluir. Tente novamente.',
      recurrenceLabel: 'Repetição',
      recurrenceNone: 'Não repete',
      recurrenceDaily: 'Todo dia',
      recurrenceWeekly: 'Toda semana',
      recurrenceMonthly: 'Todo mês',
      recurrenceYearly: 'Todo ano',
      recurrenceEnds: 'Lembrar',
      recurrenceNever: 'Para sempre',
      recurrenceAfter: 'Após',
      recurrenceTimes: 'vezes',
      recurrenceOnDate: 'Até',
      recurrenceEvery: 'A cada',
      recurrenceLast: 'último',
      recurrenceMonthlyDay: 'Mesmo dia do mês',
      recUnitDays: 'dias',
      recUnitWeeks: 'semanas',
      recUnitMonths: 'meses',
      recUnitYears: 'anos',
      recurrenceDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      deleteRecurring: 'Este é um evento recorrente.',
      deleteOnlyThis: 'Apenas este evento',
      deleteAllSeries: 'Todos os eventos da série',
      voiceHint: 'TOQUE E DITE: Evento · Data\nHora · Local · Prioridade · Notas',
      parsing: 'Preenchendo formulário com a voz...',
      parseError: 'Não foi possível entender a gravação. Preencha os campos manualmente.',
      imageHint: 'ESCANEAR IMAGEM OU FOTO',
      imageParsing: 'Lendo imagem...',
      imageError: 'Não foi possível ler a imagem. Preencha os campos manualmente.',
      multiTitle: "Eventos encontrados",
      multiSubtitle: "Confira, edite e crie.",
      multiCreate: "Criar todos",
      multiNoLabel: "Nenhuma",
      multiCreating: "Criando…",
      multiDetails: "Local e notas",
      overlapTitle: "Sobreposição",
      overlapText: "Sobrepõe-se a «{title}» ({time}). Você pode salvar mesmo assim.",
      subCalendarLabel: 'Etiqueta',
      subCalendarNone: 'Nenhum',
      visibilityLabel: 'Calendários',
      visibilityPrivate: 'Só eu',
      visibilityGroup: 'Calendário compartilhado',
      visibilitySelectGroup: 'Selecionar grupo',
      conflictTitle: 'Conflito de horário',
      conflictHint: 'Ocupados neste horário:',
      reminderLabel: 'Lembrete',
      reminderNone: 'Sem lembrete',
      reminderOn: '✓ Ativo',
      reminderOff: 'Ativar',
      reminderOptions: [
        { value: 0,    label: 'No início' },
        { value: 5,    label: '5 min antes' },
        { value: 10,   label: '10 min antes' },
        { value: 15,   label: '15 min antes' },
        { value: 20,   label: '20 min antes' },
        { value: 25,   label: '25 min antes' },
        { value: 30,   label: '30 min antes' },
        { value: 45,   label: '45 min antes' },
        { value: 60,   label: '1 hora antes' },
        { value: 120,  label: '2 horas antes' },
        { value: 180,  label: '3 horas antes' },
        { value: 720,  label: '12 horas antes' },
        { value: 1440, label: '1 dia antes' },
        { value: 2880, label: '2 dias antes' },
        { value: 10080, label: '1 semana antes' },
      ],
    },
    groups: {
      pageTitle: 'Calendários compartilhados',
      noGroups: 'Sem calendários ainda',
      noGroupsSub: 'Crie um calendário ou entre com um código de convite',
      createGroup: 'Criar calendário',
      joinGroup: 'Entrar com código',
      planLimit: 'Você atingiu o limite de calendários compartilhados do seu plano. Faça upgrade para criar mais.',
      seePlans: 'Ver planos',
      calendarFull: 'Este calendário compartilhado está cheio para o plano do proprietário.',
      groupNameLabel: 'Nome do calendário',
      groupNamePlaceholder: 'ex. Família Souza',
      inviteCode: 'Código de convite',
      inviteCodePlaceholder: 'ex. A3F9B2',
      join: 'Entrar',
      create: 'Criar',
      creating: 'Criando…',
      joining: 'Entrando…',
      members: 'Membros',
      you: '(você)',
      admin: 'Admin',
      member: 'Membro',
      leave: 'Sair do grupo',
      leaveConfirm: 'Sair deste grupo?',
      delete: 'Excluir grupo',
      deleteConfirm: 'Excluir este grupo? Todos os membros perderão o acesso.',
      shareInvite: 'Compartilhar código',
      copied: '✓ Código copiado!',
      errorCreate: 'Não foi possível criar o grupo. Tente novamente.',
      errorJoin: 'Código inválido. Verifique e tente novamente.',
      errorAlready: 'Você já é membro deste grupo.',
      cancel: 'Cancelar',
      saving: 'Salvando…',
    },
    install: {
      title: '📲 Instalar MeMo',
      subtitle: 'Instale como um app de verdade — sem App Store.',
      installBtn: '📲 Instalar neste dispositivo',
      installed: '✓ Instalado!',
      oneLogin: '🔐 Entre uma vez só — o Memo te lembra para sempre.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Abra este link no Safari', 'Toque em Compartilhar □↑ na parte de baixo', 'Toque em "Adicionar à Tela de Início"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Abra este link no Chrome', 'Toque em ⋮ no canto superior direito', 'Toque em "Adicionar à tela inicial"'],
      },
      desktop: {
        title: '💻 Computador (Chrome)',
        steps: ['Abra este link no Chrome', 'Clique no ícone 📲 na barra de endereços', 'Clique em "Instalar"'],
      },
    },
  },

  ru: {
    nav: { today: 'Сегодня', add: 'Добавить', tasks: 'Заметки', search: 'Поиск', settings: 'Настройки', groups: 'Календари' },
    search: {
      placeholder: 'Поиск событий…',
      searching: 'Поиск…',
      noResults: 'Нет результатов для',
      noResultsHint: 'Попробуйте другие ключевые слова',
      hint: 'напр. «дантист», «встреча», «день рождения»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Ошибка входа',
      subtitle: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
      back: 'Назад',
    },
    login: {
      title: 'Memo',
      subtitle: 'Ваш личный планировщик событий с голосовым вводом',
      features: [
        'Добавляйте события голосом за рулём',
        'Расставляйте приоритеты по цветам',
        'Синхронизация с Google Календарём',
        'Ежедневный Telegram-дайджест в 7 утра',
      ],
      signIn: 'Войти через Google',
    },
    onboarding: {
      demo: {
        views: ["День", "Нед.", "Месяц", "Год"],
        allDay: "Весь день",
        prio: ["Высокий", "Средний", "Низкий"],
        labels: ["РАБОТА", "СПОРТ", "ДОМ"],
        shared: "Общий",
        create: "Создать",
        join: "Войти по коду",
        sharedTitle: "Общие календари",
        searchTitle: "Поиск событий",
        searchHint: "напр. стоматолог, встреча…",
        notePh: "Написать заметку…",
        settingsTitle: "Настройки",
        light: "Светлая",
        dark: "Тёмная",
        signout: "Выйти",
        ics: "Скачать .ics",
        notif: "Уведомления",
        summaryLbl: "Сводка",
        time: "Время",
        telegram: "Отправить в Telegram",
        reminder: "за 30 мин",
        greeting: "Доброе утро",
        repeat: ["Ежедневно", "Еженедельно"],
        ev: ["Встреча", "Спортзал", "Покупки", "Стоматолог", "Ужин", "Доставка"],
      },
      skip: "Пропустить",
      next: "Далее →",
      start: "Начать",
      slides: [
        { key: "calendar", title: "Календарь, который говорит с вами", text: "День, неделя, месяц и год: смотрите планы как удобно. А одним касанием он зачитает вслух дела на день и на неделю." },
        { key: "add", title: "Добавляйте как удобно", text: "Создавайте событие печатая, голосом или по фото: сфотографируйте листок, письмо или афишу — ИИ всё заполнит. Можно также прикрепить документы к событию." },
        { key: "planner", title: "Планируйте свой день", text: "Планер на день и неделю показывает свободное и занятое время: всё укладывается с одного взгляда." },
        { key: "multi", title: "Ищите, записывайте, делитесь", text: "Находите любое событие за секунду, держите заметки под рукой и делитесь календарём с семьёй или командой — каждый своим цветом." },
        { key: "summary", title: "Каждое утро — ваша сводка", text: "Получайте дела дня в Telegram и на почту, по времени и приоритету. Начинайте день, ничего не забывая." },
        { key: "settings", title: "Настройте MeMo под себя", text: "Выберите язык, время сводки, уведомления, мелодию и тему: MeMo подстроится под ваш ритм." },
      ],
    },

    welcome: {
      greeting: 'Добро пожаловать',
      subtitle: 'Выберите язык для начала',
      saving: 'Сохранение...',
      continue: 'Продолжить →',
      countryLabel: 'Ваша страна',
      countryPlaceholder: 'Выберите страну...',
      themeLabel: 'Выберите тему',
      timezoneLabel: 'Часовой пояс',
      timezoneHint: 'Меняйте при поездках',
    },
    eventGenericType: 'Событие',
    today: {
      noEvents: 'Нет событий',
      eventsCount: (n) => {
        const mod10 = n % 10, mod100 = n % 100;
        if (mod10 === 1 && mod100 !== 11) return `${n} событие сегодня`;
        if ([2,3,4].includes(mod10) && ![12,13,14].includes(mod100)) return `${n} события сегодня`;
        return `${n} событий сегодня`;
      },
      emptyTitle: 'На сегодня всё свободно!',
      emptySub: 'Нажмите + чтобы добавить событие',
      error: 'Не удалось загрузить события. Пожалуйста, попробуйте ещё раз.',
      retry: 'Повторить',
      signOutHint: 'Если проблема не исчезает, попробуйте выйти и войти снова.',
      slowConnection: 'Медленное соединение… загрузка продолжается.',
      aiLimitReached: 'Вы использовали все 30 ИИ-записей (голос и фото) плана Free в этом месяце. Лимит обновится в следующем месяце.',
      weekList: 'Список',
      weekGrid: 'Сетка',
      conflictWarning: 'Внимание: пересекается с «{title}» ({time}). Всё равно сохранить?',
      tomorrow: 'Завтра',
      today: 'Сегодня',
      yesterday: 'Вчера',
      snooze: 'Отложить',
      stop: 'Стоп',
      prevWeek: 'Предыдущая неделя',
      nextWeek: 'Следующая неделя',
      microphone: 'Микрофон',
      noEventsDay: 'Нет событий',
      emptyTitleDay: 'Здесь пусто',
      eventsCountDay: (n) => {
        const mod10 = n % 10, mod100 = n % 100;
        if (mod10 === 1 && mod100 !== 11) return `${n} событие`;
        if ([2,3,4].includes(mod10) && ![12,13,14].includes(mod100)) return `${n} события`;
        return `${n} событий`;
      },
      prevDay: 'Предыдущий день',
      nextDay: 'Следующий день',
      filterAll: 'Все',
      filterMine: 'Только я',
      ttsButton: 'Слушать',
      ttsStop: 'Стоп',
      ttsNoEvents: 'Сегодня событий нет.',
      ttsIntro: 'Сегодня у вас {n} событий',
      viewDay: 'День',
      viewWeek: 'Неделя',
      viewMonth: 'Месяц',
      viewYear: 'Год',
      viewSlots: 'Plan',
      slotsHint: 'Нажмите на свободный слот для добавления события',
      slotsTapDay: 'Нажмите на день, чтобы открыть его',
      slotsFree: 'Свободно',
      slotsBusy: 'Занято',
      weekOf: 'Неделя',
    },
    settings: {
      pageTitle: 'Настройки',
      account: 'Аккаунт',
      signOut: 'Выйти',
      themeColor: "Цвет темы приложения",
      themeColorBtn: "Цвет прил.",
      a11yReminders: "Доступные напоминания (вибрация + голос)",
      a11yRemindersHint: "Во время напоминания телефон вибрирует и зачитывает событие вслух.",
      a11yMode: "Режим доступности",
      a11yModeHint: "Одновременно включает высокий контраст и напоминания с вибрацией и голосом.",
      a11yVibration: "Вибрация",
      a11ySpeak: "Читать событие вслух",
      a11yContrast: "Высокий контраст",
      usageTab: "Использование данных",
      a11yTab: "Доступность",
      updateAvailable: "Доступна новая версия.",
      updateButton: "Обновить",
      bigText: "Крупный текст",
      bigTextOn: "вкл",
      bigTextOff: "выкл",
      bigLarge: "Крупный",
      bigXLarge: "Очень крупный",
      feedTitle: "MeMo в Календаре Apple",
      feedDesc: "Для iPhone и iCloud: ваши дела из MeMo — в Календаре Apple.",
      feedCopy: "Копировать ссылку",
      feedCopied: "Скопировано!",
      feedSubscribe: "Показать в моём календаре",
      feedHint: "iPhone: Настройки → Календарь → Учётные записи → Добавить → Другое → Подписной календарь. Mac: Файл → Новая подписка на календарь.",
      feedConnect: "Подключить календари",
      feedRegenerate: "Обновить ссылку",
      saved: '✓ Настройки сохранены!',
      saveError: 'Не удалось сохранить настройки. Попробуйте ещё раз.',
      language: 'Язык',
      telegramSection: 'Ежедневный Telegram-дайджест',
      sendTime: 'Время отправки',
      timezone: 'Ваш часовой пояс',
      tzDetected: 'Ваше устройство в зоне',
      tzUseDetected: 'Использовать этот пояс',
      tzAutoDetect: 'Определить автоматически',
      chatId: 'Chat ID Telegram',
      voicePitch: 'Высота',
      voicePitchLow: 'Низкий',
      voicePitchMid: 'Обычный',
      voicePitchHigh: 'Высокий',
      pushIntro: 'Уведомления приходят на телефон, даже когда приложение закрыто. Нажмите, чтобы включить и проверить.',
      pushDenied: 'Доступ запрещён. Включите уведомления MeMo в настройках телефона/браузера.',
      pushUnsupported: 'Это устройство не поддерживает push-уведомления. На iPhone добавьте MeMo на экран «Домой».',
      pushUnsupportedApp: 'В установленном приложении напоминания приходят от самого телефона, а не от браузера: достаточно разрешить MeMo показывать уведомления. Telegram и почта — дополнение, если они вам нужны.',
      pushFailed: 'Не удалось. Повторите попытку или проверьте настройки уведомлений.',
      pushSending: 'Отправка…',
      pushTestButton: '🔔 Включить / Отправить тест',
      pushSent: '✓ Отправлено! Проверьте устройство.',
      pushError: 'Ошибка — включите уведомления в браузере.',
      pushNoDevice: 'Ни одно устройство не получило уведомление. Повторите попытку или выключите и снова включите уведомления MeMo в настройках браузера.',
      chatIdPlaceholder: 'например 123456789',
      chatIdHelp: (bot) => `Как получить Chat ID: откройте Telegram, найдите @${bot}, отправьте любое сообщение, затем перейдите по ссылке в руководстве.`,
      testSending: 'Отправка...',
      testButton: '📨 Отправить тестовое сообщение',
      testSuccess: '✓ Сообщение отправлено! Проверьте Telegram.',
      testError: 'Не удалось отправить. Убедитесь, что Chat ID сохранён.',
      saving: 'Сохранение...',
      saveButton: 'Сохранить настройки',
      telegramSetupTitle: 'Как подключить Telegram:',
      telegramStep1: (bot) => `Откройте Telegram и найдите @${bot}`,
      telegramStep2: 'Отправьте боту любое сообщение',
      telegramStep3: 'Откройте @userinfobot → отправьте /start → скопируйте Chat ID',
      telegramStep4: 'Вставьте его в поле ниже и сохраните',
      supportTitle: "Помощь и поддержка",
      supportDesc: "Нужна помощь или хотите сообщить о проблеме? Напишите нам — мы ответим как можно скорее.",
      emailSupport: "Написать на почту",
      contactSupport: 'Написать в поддержку в Telegram',
      themeSection: 'Внешний вид',
      themeLabel: 'Тема',
      themeLight: 'Светлая',
      themeDark: 'Тёмная',
      countryLabel: 'Страна',
      subCalendarsSection: 'Метки',
      subCalendarName: 'Название',
      subCalendarAdd: 'Добавить метку',
      subCalendarOwn: 'Раскрасить мои события',
      subCalendarPickColour: 'Выберите цвет',
      subCalendarAutoHint: 'Метки для чужих календарей получают временный цвет — нажмите на метку, чтобы выбрать свой.',
      subCalendarDeleteConfirm: 'Удалить эту метку? События удалены не будут.',
      summarySection: 'Утренний дайджест',
      summaryNowhere: "Сводка никуда не уходит: MeMo не знает, куда её отправить. Укажите почту ниже или подключите Telegram.",
      summaryChannels: 'Куда отправлять',
      viaTelegram: 'Отправлять через Telegram',
      viaEmail: 'Отправлять по email',
      emailAnyProvider: 'Работает с любым адресом — Gmail, Outlook, iCloud, Yahoo...',
      emailSection: 'Email-дайджест',
      emailLabel: 'Ваш email',
      emailHelp: 'Укажите любой email — Gmail, Outlook, iCloud, рабочий и т.д.',
      emailTestSending: '⏳ Отправка...',
      emailTestButton: '📧 Отправить тестовый email',
      emailTestSuccess: '✓ Email отправлен! Проверьте почту.',
      emailTestError: 'Не удалось отправить email. Проверьте настройки сервера.',
      voiceLabel: 'Голос для чтения',
      voiceDefault: 'Системный по умолчанию',
      voiceTest: '▶ Проверить голос',
      voiceSection: "Голос и озвучивание",
      voiceSpeed: "Скорость",
      voiceSlow: "Медленно",
      voiceFast: "Быстро",
      voiceNoVoices: 'Нет голосов для этого языка на этом устройстве.',
      voiceFemale: '♀ Женский',
      voiceMale: '♂ Мужской',
      voiceNoMatch: 'Подходящий голос не найден — используется системный по умолчанию.',
      alarmSection: 'Настройки уведомлений',
      alarmVolume: 'Громкость',
      alarmSnooze: 'Длительность отсрочки',
      alarmMaxSnooze: 'Макс. отсрочек',
      alarmForever: 'Всегда',
      alarmMin: 'мин',
      alarmSound: 'Звук',
      weatherReset: 'Погода',
      weatherActive: 'Погода включена',
      weatherDisable: 'Отключить',
      weatherDesc: 'Включите, чтобы видеть температуру и прогноз на главном экране.',
      weatherActivating: 'Запрашиваем…',
      weatherEnable: 'Включить погоду',
      weatherReEnable: 'Повторно включить погоду',
      weatherDeniedHint: 'Доступ к местоположению заблокирован браузером. Перейдите в настройки браузера, чтобы разблокировать его, и повторите попытку.',
      weatherDeniedHintApp: "Доступ к местоположению отклонён. Разрешите его для MeMo в настройках телефона и попробуйте снова.",
      weatherUnavailable: 'GPS недоступен на этом устройстве.',
      exportLabel: 'Подключить Apple Calendar, Outlook или другое приложение (не Google)',
      exportButton: 'Скачать .ics',
      exportHint: 'Импортировать в Apple Calendar, Outlook или другое приложение',
      exporting: 'Экспорт…',
    },
    tasks: {
      emptyTitle: 'Пока нет заметок',
      emptySub: 'напр. Купить хлеб · Позвонить дантисту',
      addPlaceholder: 'Напишите заметку…',
      doneSection: "Выполнено",
      deleteNoteConfirm: "Удалить эту заметку навсегда?",
      restoreNote: "Нажмите, чтобы восстановить",
      completed: 'выполнено',
      noScope: 'Выйдите и снова войдите, чтобы включить заметки',
      deleteConfirm: 'Удалить эту заметку?',
    },
    form: {
      allDay: "Весь день",
      timeMissing: "Выберите время или включите «Весь день».",
      pageTitle: 'Новое событие',
      required: 'Пожалуйста, заполните все обязательные поля.',
      saveError: 'Не удалось сохранить событие. Попробуйте ещё раз.',
      titleLabel: 'Событие',
      titlePlaceholder: 'например Приём у врача',
      cancelButton: 'Отмена',
      recurringEditTitle: 'Изменить повторяющееся событие',
      recurringThisOnly: 'Только это событие',
      recurringAllSeries: 'Все события серии',
      recurringNote: 'Повторяющееся событие — изменения применяются только к этому случаю',
      photoLabel: 'Фото',
      openInMaps: 'Открыть в Google Maps',
      recurringEvent: 'Повторяющееся событие',
      typeLabel: 'Тип *',
      typeDefault: 'Выберите тип...',
      datetimeLabel: 'Дата и время',
      dateFromLabel: 'С',
      dateToLabel: 'До',
      locationLabel: 'Место (необязательно)',
      locationPlaceholder: 'например ул. Ленина 10, Москва',
      priorityLabel: 'Приоритет',
      priorities: [
        { value: 'red',    label: 'Высокий',  sublabel: 'Обязательно' },
        { value: 'yellow', label: 'Средний',  sublabel: 'По возможности' },
        { value: 'green',  label: 'Низкий',   sublabel: 'Когда будет время' },
      ],
      notesLabel: 'Заметки (необязательно)',
      notesPlaceholder: 'Добавьте заметку или продиктуйте...',
      attLabel: 'Вложения',
      attAdd: '📎 Прикрепить фото или PDF',
      attHint: 'Макс. 5 файлов, удаляются через 5 дней',
      attTooMany: 'Максимум 5 вложений',
      attBadType: 'Только фото и PDF',
      attTooBig: 'Файл слишком большой (макс. 10 МБ)',
      saving: 'Сохранение...',
      saveButton: '✓ Сохранить',
      editPageTitle: 'Редактировать событие',
      editButton: 'Изменить',
      updateButton: '✓ Обновить',
      dictateLabel: 'ДИ',
      deleteButton: 'Удалить',
      deleteConfirm: 'Удалить это событие из Google Календаря?',
      deleteError: 'Ошибка удаления. Попробуйте ещё раз.',
      recurrenceLabel: 'Повторение',
      recurrenceNone: 'Не повторять',
      recurrenceDaily: 'Каждый день',
      recurrenceWeekly: 'Каждую неделю',
      recurrenceMonthly: 'Каждый месяц',
      recurrenceYearly: 'Каждый год',
      recurrenceEnds: 'Запомнить',
      recurrenceNever: 'Навсегда',
      recurrenceAfter: 'После',
      recurrenceTimes: 'раз',
      recurrenceOnDate: 'До',
      recurrenceEvery: 'Каждые',
      recurrenceLast: 'последний',
      recurrenceMonthlyDay: 'Тот же день месяца',
      recUnitDays: 'дней',
      recUnitWeeks: 'недель',
      recUnitMonths: 'месяцев',
      recUnitYears: 'лет',
      recurrenceDays: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
      deleteRecurring: 'Это повторяющееся событие.',
      deleteOnlyThis: 'Только это событие',
      deleteAllSeries: 'Все события серии',
      voiceHint: 'НАЖМИТЕ И ДИКТУЙТЕ: Событие · Дата\nВремя · Место · Приоритет · Заметки',
      parsing: 'Заполнение формы по голосу...',
      parseError: 'Не удалось распознать запись. Заполните поля вручную.',
      imageHint: 'СКАНИРОВАТЬ ИЗОБРАЖЕНИЕ ИЛИ ФОТО',
      imageParsing: 'Чтение изображения...',
      imageError: 'Не удалось прочитать изображение. Заполните поля вручную.',
      multiTitle: "Найдены события",
      multiSubtitle: "Проверьте, исправьте и создайте.",
      multiCreate: "Создать все",
      multiNoLabel: "Без метки",
      multiCreating: "Создание…",
      multiDetails: "Место и заметки",
      overlapTitle: "Наложение",
      overlapText: "Пересекается с «{title}» ({time}). Можно сохранить всё равно.",
      subCalendarLabel: 'Метка',
      subCalendarNone: 'Нет',
      visibilityLabel: 'Календари',
      visibilityPrivate: 'Только я',
      visibilityGroup: 'Общий календарь',
      visibilitySelectGroup: 'Выбрать группу',
      conflictTitle: 'Конфликт расписания',
      conflictHint: 'Заняты в это время:',
      reminderLabel: 'Напоминание',
      reminderNone: 'Без напоминания',
      reminderOn: '✓ Активно',
      reminderOff: 'Включить',
      reminderOptions: [
        { value: 0,    label: 'В начале' },
        { value: 5,    label: 'За 5 мин' },
        { value: 10,   label: 'За 10 мин' },
        { value: 15,   label: 'За 15 мин' },
        { value: 20,   label: 'За 20 мин' },
        { value: 25,   label: 'За 25 мин' },
        { value: 30,   label: 'За 30 мин' },
        { value: 45,   label: 'За 45 мин' },
        { value: 60,   label: 'За 1 час' },
        { value: 120,  label: 'За 2 часа' },
        { value: 180,  label: 'за 3 часа' },
        { value: 720,  label: 'за 12 часов' },
        { value: 1440, label: 'За 1 день' },
        { value: 2880, label: 'за 2 дня' },
        { value: 10080, label: 'за 1 неделю' },
      ],
    },
    groups: {
      pageTitle: 'Общие календари',
      noGroups: 'Пока нет календарей',
      noGroupsSub: 'Создайте календарь или присоединитесь по коду',
      createGroup: 'Создать календарь',
      joinGroup: 'Войти по коду',
      planLimit: 'Вы достигли лимита общих календарей вашего тарифа. Перейдите на более высокий тариф, чтобы создать ещё.',
      seePlans: 'Посмотреть тарифы',
      calendarFull: 'Этот общий календарь заполнен для тарифа владельца.',
      groupNameLabel: 'Название календаря',
      groupNamePlaceholder: 'напр. Семья Ивановых',
      inviteCode: 'Код приглашения',
      inviteCodePlaceholder: 'напр. A3F9B2',
      join: 'Войти',
      create: 'Создать',
      creating: 'Создание…',
      joining: 'Вход…',
      members: 'Участники',
      you: '(вы)',
      admin: 'Админ',
      member: 'Участник',
      leave: 'Покинуть группу',
      leaveConfirm: 'Покинуть эту группу?',
      delete: 'Удалить группу',
      deleteConfirm: 'Удалить группу? Все участники потеряют доступ.',
      shareInvite: 'Поделиться кодом',
      copied: '✓ Код скопирован!',
      errorCreate: 'Не удалось создать группу. Попробуйте ещё раз.',
      errorJoin: 'Неверный код. Проверьте и попробуйте ещё раз.',
      errorAlready: 'Вы уже являетесь участником этой группы.',
      cancel: 'Отмена',
      saving: 'Сохранение…',
    },
    install: {
      title: '📲 Установить MeMo',
      subtitle: 'Установите как настоящее приложение — без App Store.',
      installBtn: '📲 Установить на это устройство',
      installed: '✓ Установлено!',
      oneLogin: '🔐 Войдите один раз — Memo запомнит вас навсегда.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Откройте эту ссылку в Safari', 'Нажмите кнопку «Поделиться» □↑ внизу', 'Нажмите «На экран «Домой»»'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Откройте эту ссылку в Chrome', 'Нажмите ⋮ в правом верхнем углу', 'Нажмите «Добавить на главный экран»'],
      },
      desktop: {
        title: '💻 Компьютер (Chrome)',
        steps: ['Откройте эту ссылку в Chrome', 'Нажмите значок 📲 в адресной строке', 'Нажмите «Установить»'],
      },
    },
  },

  pl: {
    nav: { today: 'Dziś', add: 'Dodaj', tasks: 'Notatki', search: 'Szukaj', settings: 'Opcje', groups: 'Grupy' },
    search: {
      placeholder: 'Szukaj wydarzeń…',
      searching: 'Szukanie…',
      noResults: 'Brak wyników dla',
      noResultsHint: 'Spróbuj innych słów kluczowych',
      hint: 'np. „dentysta", „spotkanie", „urodziny"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Błąd logowania',
      subtitle: 'Coś poszło nie tak. Spróbuj ponownie.',
      back: 'Wróć',
    },
    login: {
      title: 'Memo',
      subtitle: 'Twój osobisty menedżer notatek i wydarzeń z wprowadzaniem głosowym',
      features: [
        'Dodawaj wydarzenia głosem podczas jazdy',
        'Ustalaj priorytety kolorami',
        'Synchronizacja z Kalendarzem Google',
        'Codzienny skrót Telegram o 7:00',
      ],
      signIn: 'Zaloguj się przez Google',
    },
    onboarding: {
      demo: {
        views: ["Dzień", "Tydz.", "Mies.", "Rok"],
        allDay: "Cały dzień",
        prio: ["Wysoki", "Średni", "Niski"],
        labels: ["PRACA", "SPORT", "DOM"],
        shared: "Wspólny",
        create: "Utwórz",
        join: "Dołącz kodem",
        sharedTitle: "Wspólne kalendarze",
        searchTitle: "Szukaj wydarzeń",
        searchHint: "np. dentysta, spotkanie…",
        notePh: "Napisz notatkę…",
        settingsTitle: "Ustawienia",
        light: "Jasny",
        dark: "Ciemny",
        signout: "Wyloguj",
        ics: "Pobierz .ics",
        notif: "Powiadomienia",
        summaryLbl: "Podsumowanie",
        time: "Godzina",
        telegram: "Wyślij przez Telegram",
        reminder: "30 min przed",
        greeting: "Dzień dobry",
        repeat: ["Codziennie", "Co tydzień"],
        ev: ["Spotkanie", "Siłownia", "Zakupy", "Dentysta", "Kolacja", "Dostawa"],
      },
      skip: "Pomiń",
      next: "Dalej →",
      start: "Zaczynamy",
      slides: [
        { key: "calendar", title: "Kalendarz, który do ciebie mówi", text: "Dzień, tydzień, miesiąc i rok: patrz na plany jak chcesz. A jednym dotknięciem odczyta na głos plan dnia i tygodnia." },
        { key: "add", title: "Dodawaj jak chcesz", text: "Utwórz wydarzenie pisząc, głosem lub zdjęciem: sfotografuj kartkę, e-mail lub plakat, a AI wszystko uzupełni. Możesz też dołączyć dokumenty do wydarzenia." },
        { key: "planner", title: "Zaplanuj swój dzień", text: "Planer dzienny i tygodniowy pokazuje wolne i zajęte terminy: wszystko układasz jednym rzutem oka." },
        { key: "multi", title: "Szukaj, notuj, udostępniaj", text: "Znajdź każde wydarzenie w sekundę, miej notatki zawsze pod ręką i udostępnij kalendarz rodzinie lub zespołowi — każdy w swoim kolorze." },
        { key: "summary", title: "Każdego ranka twoje podsumowanie", text: "Otrzymuj plany dnia na Telegramie i e-mailu, według godziny i priorytetu. Zaczynaj dzień, o niczym nie zapominając." },
        { key: "settings", title: "Niech MeMo pracuje dla ciebie", text: "Wybierz język, godzinę podsumowania, powiadomienia, dzwonek i motyw: MeMo dopasuje się do ciebie." },
      ],
    },

    welcome: {
      greeting: 'Witaj',
      subtitle: 'Wybierz swój język, aby zacząć',
      saving: 'Zapisywanie...',
      continue: 'Dalej →',
      countryLabel: 'Twój kraj',
      countryPlaceholder: 'Wybierz kraj...',
      themeLabel: 'Wybierz motyw',
      timezoneLabel: 'Strefa czasowa',
      timezoneHint: 'Zmień podczas podróży',
    },
    eventGenericType: 'Wydarzenie',
    today: {
      noEvents: 'Brak zaplanowanych wydarzeń',
      eventsCount: (n) => {
        if (n === 1) return `${n} wydarzenie dzisiaj`;
        const mod10 = n % 10, mod100 = n % 100;
        if ([2,3,4].includes(mod10) && ![12,13,14].includes(mod100)) return `${n} wydarzenia dzisiaj`;
        return `${n} wydarzeń dzisiaj`;
      },
      emptyTitle: 'Dziś nic zaplanowanego!',
      emptySub: 'Dotknij + aby dodać wydarzenie',
      error: 'Nie można załadować wydarzeń. Spróbuj ponownie.',
      retry: 'Spróbuj ponownie',
      signOutHint: 'Jeśli problem się utrzymuje, wyloguj się i zaloguj ponownie.',
      slowConnection: 'Wolne połączenie… wciąż ładuję.',
      aiLimitReached: 'Wykorzystano 30 wpisów AI (głos i zdjęcia) planu Free w tym miesiącu. Limit odnowi się w przyszłym miesiącu.',
      weekList: 'Lista',
      weekGrid: 'Siatka',
      conflictWarning: 'Uwaga: nakłada się na „{title}” ({time}). Zapisać mimo to?',
      tomorrow: 'Jutro',
      today: 'Dziś',
      yesterday: 'Wczoraj',
      snooze: 'Drzemka',
      stop: 'Zatrzymaj',
      prevWeek: 'Poprzedni tydzień',
      nextWeek: 'Następny tydzień',
      microphone: 'Mikrofon',
      noEventsDay: 'Brak wydarzeń',
      emptyTitleDay: 'Nic tutaj',
      eventsCountDay: (n) => {
        if (n === 1) return `${n} wydarzenie`;
        const mod10 = n % 10, mod100 = n % 100;
        if ([2,3,4].includes(mod10) && ![12,13,14].includes(mod100)) return `${n} wydarzenia`;
        return `${n} wydarzeń`;
      },
      prevDay: 'Poprzedni dzień',
      nextDay: 'Następny dzień',
      filterAll: 'Wszystkie',
      filterMine: 'Tylko ja',
      ttsButton: 'Słuchaj',
      ttsStop: 'Stop',
      ttsNoEvents: 'Brak wydarzeń dzisiaj.',
      ttsIntro: 'Dziś masz {n} wydarzeń',
      viewDay: 'Dzień',
      viewWeek: 'Tydzień',
      viewMonth: 'Miesiąc',
      viewYear: 'Rok',
      viewSlots: 'Plan',
      slotsHint: 'Dotknij wolny slot, aby dodać wydarzenie',
      slotsTapDay: 'Dotknij dnia, aby go otworzyć',
      slotsFree: 'Wolny',
      slotsBusy: 'Zajęty',
      weekOf: 'Tydzień',
    },
    settings: {
      pageTitle: 'Ustawienia',
      account: 'Konto',
      signOut: 'Wyloguj',
      themeColor: "Kolor motywu aplikacji",
      themeColorBtn: "Kolor apl.",
      a11yReminders: "Dostępne przypomnienia (wibracja + głos)",
      a11yRemindersHint: "O czasie przypomnienia telefon wibruje i odczytuje wydarzenie na głos.",
      a11yMode: "Tryb dostępności",
      a11yModeHint: "Włącza jednocześnie wysoki kontrast i przypomnienia z wibracją i głosem.",
      a11yVibration: "Wibracja",
      a11ySpeak: "Odczytaj wydarzenie",
      a11yContrast: "Wysoki kontrast",
      usageTab: "Zużycie danych",
      a11yTab: "Dostępność",
      updateAvailable: "Dostępna jest nowa wersja.",
      updateButton: "Aktualizuj",
      bigText: "Duży tekst",
      bigTextOn: "wł.",
      bigTextOff: "wył.",
      bigLarge: "Duży",
      bigXLarge: "Bardzo duży",
      feedTitle: "MeMo w Kalendarzu Apple",
      feedDesc: "Dla iPhone’a i iCloud: Twoje plany z MeMo, w Kalendarzu Apple.",
      feedCopy: "Kopiuj link",
      feedCopied: "Skopiowano!",
      feedSubscribe: "Pokaż w moim kalendarzu",
      feedHint: "iPhone: Ustawienia → Kalendarz → Konta → Dodaj → Inne → Dodaj subskrybowany kalendarz. Mac: Plik → Nowa subskrypcja kalendarza.",
      feedConnect: "Połącz kalendarze",
      feedRegenerate: "Nowy link",
      saved: '✓ Ustawienia zapisane!',
      saveError: 'Błąd przy zapisywaniu. Spróbuj ponownie.',
      language: 'Język',
      telegramSection: 'Codzienny skrót Telegram',
      sendTime: 'Godzina wysyłania',
      timezone: 'Twoja strefa czasowa',
      tzDetected: 'Twoje urządzenie jest w strefie',
      tzUseDetected: 'Użyj tej strefy',
      tzAutoDetect: 'Wykryj automatycznie',
      chatId: 'ID czatu Telegram',
      voicePitch: 'Wysokość',
      voicePitchLow: 'Niski',
      voicePitchMid: 'Normalny',
      voicePitchHigh: 'Wysoki',
      pushIntro: 'Powiadomienia docierają na telefon nawet przy zamkniętej aplikacji. Dotknij, aby włączyć i przetestować.',
      pushDenied: 'Odmowa uprawnień. Włącz powiadomienia MeMo w ustawieniach telefonu/przeglądarki.',
      pushUnsupported: 'To urządzenie nie obsługuje powiadomień push. Na iPhonie zainstaluj MeMo na ekranie głównym.',
      pushUnsupportedApp: 'W zainstalowanej aplikacji przypomnienia pochodzą od samego telefonu, nie z przeglądarki: wystarczy pozwolić MeMo pokazywać powiadomienia. Telegram i e-mail są dodatkiem, jeśli ich chcesz.',
      pushFailed: 'Nie udało się. Spróbuj ponownie lub sprawdź ustawienia powiadomień.',
      pushSending: 'Wysyłanie…',
      pushTestButton: '🔔 Włącz / Wyślij test',
      pushSent: '✓ Wysłano! Sprawdź urządzenie.',
      pushError: 'Błąd — włącz powiadomienia w przeglądarce.',
      pushNoDevice: 'Nie osiągnięto żadnego urządzenia. Spróbuj ponownie lub wyłącz i włącz powiadomienia MeMo w ustawieniach przeglądarki.',
      chatIdPlaceholder: 'np. 123456789',
      chatIdHelp: (bot) => `Jak uzyskać Chat ID: otwórz Telegram, znajdź @${bot}, wyślij wiadomość, kliknij link w przewodniku.`,
      testSending: 'Wysyłanie...',
      testButton: '📨 Wyślij wiadomość testową',
      testSuccess: '✓ Wiadomość wysłana! Sprawdź Telegram.',
      testError: 'Nie udało się wysłać. Upewnij się, że Chat ID jest zapisany.',
      saving: 'Zapisywanie...',
      saveButton: 'Zapisz ustawienia',
      telegramSetupTitle: 'Jak połączyć Telegram:',
      telegramStep1: (bot) => `Otwórz Telegram i znajdź @${bot}`,
      telegramStep2: 'Wyślij dowolną wiadomość, aby rozpocząć',
      telegramStep3: 'Otwórz @userinfobot → wyślij /start → skopiuj Chat ID',
      telegramStep4: 'Wklej w polu poniżej i zapisz',
      supportTitle: "Pomoc i wsparcie",
      supportDesc: "Potrzebujesz pomocy lub chcesz zgłosić problem? Napisz do nas — odpowiemy najszybciej, jak to możliwe.",
      emailSupport: "Napisz e-mail",
      contactSupport: 'Skontaktuj się z pomocą na Telegramie',
      themeSection: 'Wygląd',
      themeLabel: 'Motyw',
      themeLight: 'Jasny',
      themeDark: 'Ciemny',
      countryLabel: 'Kraj',
      subCalendarsSection: 'Etykiety',
      subCalendarName: 'Nazwa',
      subCalendarAdd: 'Dodaj etykietę',
      subCalendarOwn: 'Pokoloruj moje wydarzenia',
      subCalendarPickColour: 'Wybierz kolor',
      subCalendarAutoHint: 'Etykiety kalendarzy, które nie są Twoje, dostają tymczasowy kolor — dotknij, żeby wybrać własny.',
      subCalendarDeleteConfirm: 'Usunąć tę etykietę? Wydarzenia nie zostaną usunięte.',
      summarySection: 'Poranny skrót',
      summaryNowhere: "Podsumowanie nigdzie nie trafia: MeMo nie wie dokąd. Wpisz poniżej swój e-mail albo połącz Telegram.",
      summaryChannels: 'Gdzie wysyłać',
      viaTelegram: 'Wyślij przez Telegram',
      viaEmail: 'Wyślij przez email',
      emailAnyProvider: 'Działa z każdym adresem e-mail — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Skrót e-mail',
      emailLabel: 'Twój adres e-mail',
      emailHelp: 'Dowolny adres e-mail — Gmail, Outlook, iCloud, praca itp.',
      emailTestSending: '⏳ Wysyłanie...',
      emailTestButton: '📧 Wyślij testowy e-mail',
      emailTestSuccess: '✓ E-mail wysłany! Sprawdź skrzynkę odbiorczą.',
      emailTestError: 'Błąd wysyłania e-maila. Sprawdź konfigurację serwera.',
      voiceLabel: 'Głos do czytania',
      voiceDefault: 'Domyślny systemowy',
      voiceTest: '▶ Sprawdź głos',
      voiceSection: "Głos i czytanie",
      voiceSpeed: "Prędkość",
      voiceSlow: "Wolno",
      voiceFast: "Szybko",
      voiceNoVoices: 'Brak głosów dla tego języka na tym urządzeniu.',
      voiceFemale: '♀ Żeński',
      voiceMale: '♂ Męski',
      voiceNoMatch: 'Nie znaleziono pasującego głosu — używany jest domyślny systemowy.',
      alarmSection: 'Ustawienia powiadomień',
      alarmVolume: 'Głośność',
      alarmSnooze: 'Czas drzemki',
      alarmMaxSnooze: 'Maks. drzemek',
      alarmForever: 'Zawsze',
      alarmMin: 'min',
      alarmSound: 'Dźwīk',
      weatherReset: 'Pogoda',
      weatherActive: 'Pogoda aktywna',
      weatherDisable: 'Wyłącz',
      weatherDesc: 'Włącz, aby widzieć temperaturę i prognozę na głównym ekranie.',
      weatherActivating: 'Czekam…',
      weatherEnable: 'Włącz pogodę',
      weatherReEnable: 'Włącz ponownie pogodę',
      weatherDeniedHint: 'Dostęp do lokalizacji zablokowany przez przeglądarkę. Przejdź do ustawień przeglądarki, aby go odblokować, i spróbuj ponownie.',
      weatherDeniedHintApp: "Odmówiono dostępu do lokalizacji. Zezwól na to dla MeMo w ustawieniach telefonu i spróbuj ponownie.",
      weatherUnavailable: 'GPS niedostępny na tym urządzeniu.',
      exportLabel: 'Połącz z Apple Calendar, Outlook lub inną aplikacją (nie Google)',
      exportButton: 'Pobierz .ics',
      exportHint: 'Zaimportuj do Apple Calendar, Outlook lub innej aplikacji',
      exporting: 'Eksportowanie…',
    },
    tasks: {
      emptyTitle: 'Brak notatek',
      emptySub: 'np. Kupić chleb · Zadzwonić do dentysty',
      addPlaceholder: 'Napisz notatkę…',
      doneSection: "Ukończone",
      deleteNoteConfirm: "Usunąć tę notatkę na stałe?",
      restoreNote: "Dotknij, aby przywrócić",
      completed: 'ukończone',
      noScope: 'Wyloguj się i zaloguj ponownie, aby włączyć notatki',
      deleteConfirm: 'Usunąć tę notatkę?',
    },
    form: {
      allDay: "Cały dzień",
      timeMissing: "Wybierz godzinę albo włącz „Cały dzień”.",
      pageTitle: 'Nowe wydarzenie',
      required: 'Wypełnij wszystkie wymagane pola.',
      saveError: 'Błąd zapisu. Spróbuj ponownie.',
      titleLabel: 'Wydarzenie',
      titlePlaceholder: 'np. Wizyta u lekarza',
      cancelButton: 'Anuluj',
      recurringEditTitle: 'Edytuj wydarzenie cykliczne',
      recurringThisOnly: 'Tylko to wydarzenie',
      recurringAllSeries: 'Wszystkie wydarzenia z serii',
      recurringNote: 'Wydarzenie cykliczne — zmiany dotyczą tylko tego wystąpienia',
      photoLabel: 'Zdjęcie',
      openInMaps: 'Otwórz w Mapach Google',
      recurringEvent: 'Wydarzenie cykliczne',
      typeLabel: 'Typ *',
      typeDefault: 'Wybierz typ...',
      datetimeLabel: 'Data i godzina',
      dateFromLabel: 'Od',
      dateToLabel: 'Do',
      locationLabel: 'Miejsce (opcjonalne)',
      locationPlaceholder: 'np. ul. Marszałkowska 10, Warszawa',
      priorityLabel: 'Priorytet',
      priorities: [
        { value: 'red',    label: 'Wysoki',  sublabel: 'Musi być zrobione' },
        { value: 'yellow', label: 'Średni',  sublabel: 'Powinno być zrobione' },
        { value: 'green',  label: 'Niski',   sublabel: 'Gdy będzie czas' },
      ],
      notesLabel: 'Notatki (opcjonalne)',
      notesPlaceholder: 'Dodaj notatki lub użyj głosu...',
      attLabel: 'Załączniki',
      attAdd: '📎 Dołącz zdjęcie lub PDF',
      attHint: 'Maks. 5 plików, usuwane 5 dni po wydarzeniu',
      attTooMany: 'Maks. 5 załączników',
      attBadType: 'Tylko zdjęcia i PDF',
      attTooBig: 'Plik za duży (maks. 10 MB)',
      saving: 'Zapisywanie...',
      saveButton: '✓ Zapisz wydarzenie',
      editPageTitle: 'Edytuj wydarzenie',
      editButton: 'Edytuj',
      updateButton: '✓ Zaktualizuj',
      dictateLabel: 'MÓW',
      deleteButton: 'Usuń',
      deleteConfirm: 'Usunąć to wydarzenie z Kalendarza Google?',
      deleteError: 'Błąd usuwania. Spróbuj ponownie.',
      recurrenceLabel: 'Powtarzanie',
      recurrenceNone: 'Nie powtarzaj',
      recurrenceDaily: 'Codziennie',
      recurrenceWeekly: 'Co tydzień',
      recurrenceMonthly: 'Co miesiąc',
      recurrenceYearly: 'Co rok',
      recurrenceEnds: 'Pamiętaj',
      recurrenceNever: 'Na zawsze',
      recurrenceAfter: 'Po',
      recurrenceTimes: 'razy',
      recurrenceOnDate: 'Do',
      recurrenceEvery: 'Co',
      recurrenceLast: 'ostatni',
      recurrenceMonthlyDay: 'Ten sam dzień miesiąca',
      recUnitDays: 'dni',
      recUnitWeeks: 'tygodni',
      recUnitMonths: 'miesięcy',
      recUnitYears: 'lat',
      recurrenceDays: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
      deleteRecurring: 'To jest wydarzenie cykliczne.',
      deleteOnlyThis: 'Tylko to wydarzenie',
      deleteAllSeries: 'Wszystkie wydarzenia z serii',
      voiceHint: 'DOTKNIJ I DYKTUJ: Wydarzenie · Data\nGodzina · Miejsce · Priorytet · Notatki',
      parsing: 'Wypełnianie formularza z głosu...',
      parseError: 'Nie udało się zrozumieć nagrania. Wypełnij pola ręcznie.',
      imageHint: 'SKANUJ OBRAZ LUB ZDJĘCIE',
      imageParsing: 'Odczytywanie obrazu...',
      imageError: 'Nie udało się odczytać obrazu. Wypełnij pola ręcznie.',
      multiTitle: "Znalezione wydarzenia",
      multiSubtitle: "Sprawdź, popraw i utwórz.",
      multiCreate: "Utwórz wszystkie",
      multiNoLabel: "Brak",
      multiCreating: "Tworzenie…",
      multiDetails: "Miejsce i notatki",
      overlapTitle: "Nakładanie się",
      overlapText: "Nakłada się na «{title}» ({time}). Możesz i tak zapisać.",
      subCalendarLabel: 'Etykieta',
      subCalendarNone: 'Brak',
      visibilityLabel: 'Kalendarze',
      visibilityPrivate: 'Tylko ja',
      visibilityGroup: 'Współdzielony kalendarz',
      visibilitySelectGroup: 'Wybierz grupę',
      conflictTitle: 'Konflikt harmonogramu',
      conflictHint: 'Zajęci w tym czasie:',
      reminderLabel: 'Przypomnienie',
      reminderNone: 'Bez przypomnienia',
      reminderOn: '✓ Aktywne',
      reminderOff: 'Włącz',
      reminderOptions: [
        { value: 0,    label: 'Na początku' },
        { value: 5,    label: '5 min wcześniej' },
        { value: 10,   label: '10 min wcześniej' },
        { value: 15,   label: '15 min wcześniej' },
        { value: 20,   label: '20 min wcześniej' },
        { value: 25,   label: '25 min wcześniej' },
        { value: 30,   label: '30 min wcześniej' },
        { value: 45,   label: '45 min wcześniej' },
        { value: 60,   label: '1 godz. wcześniej' },
        { value: 120,  label: '2 godz. wcześniej' },
        { value: 180,  label: '3 godz. wcześniej' },
        { value: 720,  label: '12 godz. wcześniej' },
        { value: 1440, label: '1 dzień wcześniej' },
        { value: 2880, label: '2 dni wcześniej' },
        { value: 10080, label: '1 tydzień wcześniej' },
      ],
    },
    groups: {
      pageTitle: 'Kalendarze współdzielone',
      noGroups: 'Brak kalendarzy',
      noGroupsSub: 'Utwórz kalendarz lub dołącz za pomocą kodu zaproszenia',
      createGroup: 'Utwórz kalendarz',
      joinGroup: 'Dołącz z kodem',
      planLimit: 'Osiągnąłeś limit wspólnych kalendarzy w swoim planie. Przejdź na wyższy plan, aby utworzyć więcej.',
      seePlans: 'Zobacz plany',
      calendarFull: 'Ten wspólny kalendarz jest pełny dla planu właściciela.',
      groupNameLabel: 'Nazwa kalendarza',
      groupNamePlaceholder: 'np. Rodzina Kowalskich',
      inviteCode: 'Kod zaproszenia',
      inviteCodePlaceholder: 'np. A3F9B2',
      join: 'Dołącz',
      create: 'Utwórz',
      creating: 'Tworzenie…',
      joining: 'Dołączanie…',
      members: 'Członkowie',
      you: '(ty)',
      admin: 'Admin',
      member: 'Członek',
      leave: 'Opuść grupę',
      leaveConfirm: 'Opuścić tę grupę?',
      delete: 'Usuń grupę',
      deleteConfirm: 'Usunąć tę grupę? Wszyscy członkowie utracą dostęp.',
      shareInvite: 'Udostępnij kod',
      copied: '✓ Kod skopiowany!',
      errorCreate: 'Nie udało się utworzyć grupy. Spróbuj ponownie.',
      errorJoin: 'Nieprawidłowy kod. Sprawdź i spróbuj ponownie.',
      errorAlready: 'Jesteś już członkiem tej grupy.',
      cancel: 'Anuluj',
      saving: 'Zapisywanie…',
    },
    install: {
      title: '📲 Zainstaluj MeMo',
      subtitle: 'Zainstaluj jak prawdziwą aplikację — bez App Store.',
      installBtn: '📲 Zainstaluj na tym urządzeniu',
      installed: '✓ Zainstalowano!',
      oneLogin: '🔐 Zaloguj się raz — Memo pamięta cię na zawsze.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Otwórz ten link w Safari', 'Dotknij przycisku Udostępnij □↑ na dole', 'Dotknij "Dodaj do ekranu głównego"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Otwórz ten link w Chrome', 'Dotknij ⋮ w prawym górnym rogu', 'Dotknij "Dodaj do ekranu głównego"'],
      },
      desktop: {
        title: '💻 Komputer (Chrome)',
        steps: ['Otwórz ten link w Chrome', 'Kliknij ikonę 📲 w pasku adresu', 'Kliknij "Zainstaluj"'],
      },
    },
  },

  ro: {
    nav: { today: 'Azi', add: 'Adaugă', tasks: 'Notițe', search: 'Caută', settings: 'Setări', groups: 'Calendare' },
    search: {
      placeholder: 'Caută evenimente…',
      searching: 'Se caută…',
      noResults: 'Niciun rezultat pentru',
      noResultsHint: 'Încearcă alte cuvinte cheie',
      hint: 'ex. „dentist", „ședință", „aniversare"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'Eroare la autentificare',
      subtitle: 'Ceva a mers greșit. Încearcă din nou.',
      back: 'Înapoi',
    },
    login: {
      title: 'Memo',
      subtitle: 'Managerul tău personal de notițe și evenimente cu introducere vocală',
      features: [
        'Adaugă evenimente prin voce în timp ce conduci',
        'Prioritizează cu culori',
        'Sincronizare cu Google Calendar',
        'Rezumat zilnic Telegram la 7:00',
      ],
      signIn: 'Conectează-te cu Google',
    },
    onboarding: {
      demo: {
        views: ["Zi", "Săpt.", "Lună", "An"],
        allDay: "Toată ziua",
        prio: ["Ridicată", "Medie", "Scăzută"],
        labels: ["MUNCĂ", "SPORT", "ACASĂ"],
        shared: "Partajat",
        create: "Creează",
        join: "Intră cu cod",
        sharedTitle: "Calendare partajate",
        searchTitle: "Caută evenimente",
        searchHint: "ex. dentist, ședință…",
        notePh: "Scrie o notiță…",
        settingsTitle: "Setări",
        light: "Luminos",
        dark: "Întunecat",
        signout: "Ieși",
        ics: "Descarcă .ics",
        notif: "Notificări",
        summaryLbl: "Rezumat",
        time: "Ora",
        telegram: "Trimite prin Telegram",
        reminder: "cu 30 min înainte",
        greeting: "Bună dimineața",
        repeat: ["Zilnic", "Săptămânal"],
        ev: ["Ședință", "Sală", "Cumpărături", "Dentist", "Cină", "Livrare"],
      },
      skip: "Sari peste",
      next: "Următorul →",
      start: "Începe",
      slides: [
        { key: "calendar", title: "Calendarul care îți vorbește", text: "Zi, săptămână, lună și an: vezi-ți programul cum preferi. Și cu o atingere îți citește cu voce tare ziua și săptămâna." },
        { key: "add", title: "Adaugă cum vrei", text: "Creează un eveniment scriind, cu vocea sau cu o poză: fotografiază o foaie, un e-mail sau un afiș, iar AI completează tot. Poți atașa și documente la eveniment." },
        { key: "planner", title: "Organizează-ți ziua", text: "Planificatorul zilnic și săptămânal arată intervalele libere și ocupate: potrivești totul dintr-o privire." },
        { key: "multi", title: "Caută, notează, partajează", text: "Găsești orice eveniment într-o secundă, ții notițele mereu la îndemână și partajezi calendarul cu familia sau echipa, fiecare cu culoarea lui." },
        { key: "summary", title: "În fiecare dimineață, rezumatul tău", text: "Primești programul zilei pe Telegram și pe e-mail, ordonat după oră și prioritate. Începe ziua fără să uiți nimic." },
        { key: "settings", title: "Pune MeMo să lucreze pentru tine", text: "Alege limba, ora rezumatului, notificările, sunetul și tema: MeMo se adaptează la felul tău de a lucra." },
      ],
    },

    welcome: {
      greeting: 'Bun venit',
      subtitle: 'Alege limba ta pentru a începe',
      saving: 'Se salvează...',
      continue: 'Continuă →',
      countryLabel: 'Țara ta',
      countryPlaceholder: 'Selectează țara...',
      themeLabel: 'Alege tema',
      timezoneLabel: 'Fus orar',
      timezoneHint: 'Schimbă când călătorești',
    },
    eventGenericType: 'Eveniment',
    today: {
      noEvents: 'Niciun eveniment planificat',
      eventsCount: (n) => `${n} eveniment${n !== 1 ? 'e' : ''} azi`,
      emptyTitle: 'Astăzi ești liber!',
      emptySub: 'Apasă + pentru a adăuga un eveniment',
      error: 'Nu s-au putut încărca evenimentele. Încearcă din nou.',
      retry: 'Reîncearcă',
      signOutHint: 'Dacă problema persistă, deconectează-te și reconectează-te.',
      slowConnection: 'Conexiune lentă… încă se încarcă.',
      aiLimitReached: 'Ai folosit cele 30 de intrări AI (voce și foto) ale planului Free luna aceasta. Se resetează luna viitoare.',
      weekList: 'Listă',
      weekGrid: 'Grilă',
      conflictWarning: 'Atenție: se suprapune cu „{title}” ({time}). Salvezi oricum?',
      tomorrow: 'Mâine',
      today: 'Azi',
      yesterday: 'Ieri',
      snooze: 'Amână',
      stop: 'Oprește',
      prevWeek: 'Săptămâna anterioară',
      nextWeek: 'Săptămâna următoare',
      microphone: 'Microfon',
      noEventsDay: 'Niciun eveniment',
      emptyTitleDay: 'Nimic aici',
      eventsCountDay: (n) => `${n} eveniment${n !== 1 ? 'e' : ''}`,
      prevDay: 'Ziua anterioară',
      nextDay: 'Ziua următoare',
      filterAll: 'Toate',
      filterMine: 'Doar eu',
      ttsButton: 'Ascultă',
      ttsStop: 'Stop',
      ttsNoEvents: 'Niciun eveniment astăzi.',
      ttsIntro: 'Astăzi ai {n} evenimente',
      viewDay: 'Zi',
      viewWeek: 'Săptămână',
      viewMonth: 'Lună',
      viewYear: 'An',
      viewSlots: 'Plan',
      slotsHint: 'Atingeți un slot liber pentru a adăuga un eveniment',
      slotsTapDay: 'Atingeți o zi pentru a o deschide',
      slotsFree: 'Liber',
      slotsBusy: 'Ocupat',
      weekOf: 'Săptămâna',
    },
    settings: {
      pageTitle: 'Setări',
      account: 'Cont',
      signOut: 'Deconectare',
      themeColor: "Culoarea temei aplicației",
      themeColorBtn: "Culoare app",
      a11yReminders: "Memento accesibile (vibrație + voce)",
      a11yRemindersHint: "La ora mementoului telefonul vibrează și citește evenimentul cu voce tare.",
      a11yMode: "Mod accesibilitate",
      a11yModeHint: "Activează împreună contrast ridicat și memento cu vibrație și voce.",
      a11yVibration: "Vibrație",
      a11ySpeak: "Citește evenimentul cu voce",
      a11yContrast: "Contrast ridicat",
      usageTab: "Utilizare date",
      a11yTab: "Accesibilitate",
      updateAvailable: "Este disponibilă o versiune nouă.",
      updateButton: "Actualizează",
      bigText: "Text mare",
      bigTextOn: "activat",
      bigTextOff: "dezactivat",
      bigLarge: "Mare",
      bigXLarge: "Foarte mare",
      feedTitle: "MeMo în Calendarul Apple",
      feedDesc: "Pentru iPhone și iCloud: programul tău din MeMo, în Calendarul Apple.",
      feedCopy: "Copiază linkul",
      feedCopied: "Copiat!",
      feedSubscribe: "Vezi în calendarul meu",
      feedHint: "iPhone: Setări → Calendar → Conturi → Adaugă → Altul → Adaugă calendar abonat. Mac: Fișier → Abonament nou la calendar.",
      feedConnect: "Conectează-ți calendarele",
      feedRegenerate: "Regenerează linkul",
      saved: '✓ Setări salvate!',
      saveError: 'Eroare la salvare. Încearcă din nou.',
      language: 'Limbă',
      telegramSection: 'Rezumat zilnic Telegram',
      sendTime: 'Ora de trimitere',
      timezone: 'Fusul tău orar',
      tzDetected: 'Dispozitivul tău este în',
      tzUseDetected: 'Folosește acest fus orar',
      tzAutoDetect: 'Detectare automată',
      chatId: 'Chat ID Telegram',
      voicePitch: 'Ton',
      voicePitchLow: 'Grav',
      voicePitchMid: 'Normal',
      voicePitchHigh: 'Înalt',
      pushIntro: 'Notificările ajung pe telefon chiar și cu aplicația închisă. Atinge pentru a activa și testa.',
      pushDenied: 'Permisiune refuzată. Activează notificările MeMo din setările telefonului/browserului.',
      pushUnsupported: 'Acest dispozitiv nu acceptă notificări push. Pe iPhone, instalează MeMo pe ecranul principal.',
      pushUnsupportedApp: 'În aplicația instalată mementourile vin de la telefonul însuși, nu din browser: asigură-te doar că i-ai dat MeMo permisiunea de a afișa notificări. Telegram și emailul rămân în plus, dacă le vrei.',
      pushFailed: 'A eșuat. Încearcă din nou sau verifică setările notificărilor.',
      pushSending: 'Se trimite…',
      pushTestButton: '🔔 Activează / Trimite test',
      pushSent: '✓ Trimisă! Verifică dispozitivul.',
      pushError: 'Eroare — activează notificările în browser.',
      pushNoDevice: 'Niciun dispozitiv contactat. Încearcă din nou sau dezactivează și reactivează notificările MeMo din setările browserului.',
      chatIdPlaceholder: 'ex. 123456789',
      chatIdHelp: (bot) => `Cum să obții Chat ID: deschide Telegram, caută @${bot}, trimite un mesaj, apoi apasă linkul din ghid.`,
      testSending: 'Se trimite...',
      testButton: '📨 Trimite mesaj de test acum',
      testSuccess: '✓ Mesaj trimis! Verifică Telegram.',
      testError: 'Trimitere eșuată. Asigură-te că Chat ID-ul este salvat.',
      saving: 'Se salvează...',
      saveButton: 'Salvează setările',
      telegramSetupTitle: 'Cum să conectezi Telegram:',
      telegramStep1: (bot) => `Deschide Telegram și caută @${bot}`,
      telegramStep2: 'Trimite orice mesaj pentru a începe',
      telegramStep3: 'Deschide @userinfobot → trimite /start → copiază Chat ID',
      telegramStep4: 'Lipește-l în câmpul de mai jos și salvează',
      supportTitle: "Ajutor și asistență",
      supportDesc: "Ai nevoie de ajutor sau vrei să raportezi o problemă? Scrie-ne, îți răspundem cât mai curând.",
      emailSupport: "Scrie-ne un e-mail",
      contactSupport: 'Contactează asistența pe Telegram',
      themeSection: 'Aspect',
      themeLabel: 'Temă',
      themeLight: 'Deschis',
      themeDark: 'Întunecat',
      countryLabel: 'Țară',
      subCalendarsSection: 'Etichete',
      subCalendarName: 'Nume',
      subCalendarAdd: 'Adaugă etichetă',
      subCalendarOwn: 'Colorează evenimentele mele',
      subCalendarPickColour: 'Alege o culoare',
      subCalendarAutoHint: 'Etichetele calendarelor care nu sunt ale tale primesc o culoare provizorie — atinge-le ca să alegi tu.',
      subCalendarDeleteConfirm: 'Ștergi această etichetă? Evenimentele nu vor fi șterse.',
      summarySection: 'Rezumat de dimineață',
      summaryNowhere: "Rezumatul nu pleacă: MeMo nu știe unde să-l trimită. Scrie-ți emailul mai jos, sau conectează Telegram.",
      summaryChannels: 'Unde să trimiți',
      viaTelegram: 'Trimite prin Telegram',
      viaEmail: 'Trimite prin email',
      emailAnyProvider: 'Funcționează cu orice adresă — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'Rezumat prin e-mail',
      emailLabel: 'Adresa ta de e-mail',
      emailHelp: 'Orice adresă de e-mail — Gmail, Outlook, iCloud, serviciu, etc.',
      emailTestSending: '⏳ Se trimite...',
      emailTestButton: '📧 Trimite e-mail de test',
      emailTestSuccess: '✓ E-mail trimis! Verifică inbox-ul.',
      emailTestError: 'Eroare la trimiterea e-mailului. Verifică configurația serverului.',
      voiceLabel: 'Voce de citire',
      voiceDefault: 'Implicit al sistemului',
      voiceTest: '▶ Testează vocea',
      voiceSection: "Voce și citire",
      voiceSpeed: "Viteză",
      voiceSlow: "Lentă",
      voiceFast: "Rapidă",
      voiceNoVoices: 'Nu există voci disponibile pentru această limbă pe acest dispozitiv.',
      voiceFemale: '♀ Feminină',
      voiceMale: '♂ Masculin',
      voiceNoMatch: 'Nicio voce corespunzătoare — se folosește cea implicită a sistemului.',
      alarmSection: 'Setări notificări',
      alarmVolume: 'Volum',
      alarmSnooze: 'Durată amânare',
      alarmMaxSnooze: 'Max. amânări',
      alarmForever: 'Mereu',
      alarmMin: 'min',
      alarmSound: 'Sunet',
      weatherReset: 'Vreme',
      weatherActive: 'Vremea activă',
      weatherDisable: 'Dezactivează',
      weatherDesc: 'Activează pentru a vedea temperatura și prognoza pe ecranul principal.',
      weatherActivating: 'Se solicită…',
      weatherEnable: 'Activează vremea',
      weatherReEnable: 'Reactivează vremea',
      weatherDeniedHint: 'Accesul la locație blocat de browser. Mergi la setările browserului pentru a-l debloca, apoi încearcă din nou.',
      weatherDeniedHintApp: "Accesul la locație a fost refuzat. Permite-l pentru MeMo din setările telefonului, apoi încearcă din nou.",
      weatherUnavailable: 'GPS indisponibil pe acest dispozitiv.',
      exportLabel: 'Conectează la Apple Calendar, Outlook sau orice altă aplicație non-Google',
      exportButton: 'Descarcă .ics',
      exportHint: 'Importă în Apple Calendar, Outlook sau orice altă aplicație',
      exporting: 'Se exportă…',
    },
    tasks: {
      emptyTitle: 'Încă nicio notă',
      emptySub: 'ex. Cumpără pâine · Sună la dentist',
      addPlaceholder: 'Scrie o notă…',
      doneSection: "Finalizate",
      deleteNoteConfirm: "Ștergi definitiv această notă?",
      restoreNote: "Atinge pentru a restabili",
      completed: 'finalizate',
      noScope: 'Deconectează-te și reconectează-te pentru a activa notele',
      deleteConfirm: 'Ștergeți această notiță?',
    },
    form: {
      allDay: "Toată ziua",
      timeMissing: "Alege o oră, sau activează «Toată ziua».",
      pageTitle: 'Eveniment nou',
      required: 'Completează toate câmpurile obligatorii.',
      saveError: 'Eroare la salvare. Încearcă din nou.',
      titleLabel: 'Eveniment',
      titlePlaceholder: 'ex. Vizită la doctor',
      cancelButton: 'Anulează',
      recurringEditTitle: 'Editează evenimentul recurent',
      recurringThisOnly: 'Doar acest eveniment',
      recurringAllSeries: 'Toate evenimentele din serie',
      recurringNote: 'Eveniment recurent — modificările se aplică doar acestei apariții',
      photoLabel: 'Fotografie',
      openInMaps: 'Deschide în Google Maps',
      recurringEvent: 'Eveniment recurent',
      typeLabel: 'Tip *',
      typeDefault: 'Selectează tipul...',
      datetimeLabel: 'Dată și oră',
      dateFromLabel: 'De la',
      dateToLabel: 'Până la',
      locationLabel: 'Locație (opțional)',
      locationPlaceholder: 'ex. Str. Victoriei 10, București',
      priorityLabel: 'Prioritate',
      priorities: [
        { value: 'red',    label: 'Înaltă',  sublabel: 'Trebuie făcut' },
        { value: 'yellow', label: 'Mediu',   sublabel: 'Ar trebui făcut' },
        { value: 'green',  label: 'Scăzut',  sublabel: 'Dacă este timp' },
      ],
      notesLabel: 'Note (opțional)',
      notesPlaceholder: 'Adaugă note sau folosește vocea...',
      attLabel: 'Atașamente',
      attAdd: '📎 Atașează foto sau PDF',
      attHint: 'Max 5 fișiere, șterse după 5 zile',
      attTooMany: 'Max 5 atașamente',
      attBadType: 'Doar foto și PDF',
      attTooBig: 'Fișier prea mare (max 10 MB)',
      saving: 'Se salvează...',
      saveButton: '✓ Salvează evenimentul',
      editPageTitle: 'Editează evenimentul',
      editButton: 'Editează',
      updateButton: '✓ Actualizează',
      dictateLabel: 'ZI',
      deleteButton: 'Șterge',
      deleteConfirm: 'Ștergi acest eveniment din Google Calendar?',
      deleteError: 'Eroare la ștergere. Încearcă din nou.',
      recurrenceLabel: 'Recurență',
      recurrenceNone: 'Nu se repetă',
      recurrenceDaily: 'În fiecare zi',
      recurrenceWeekly: 'În fiecare săptămână',
      recurrenceMonthly: 'În fiecare lună',
      recurrenceYearly: 'În fiecare an',
      recurrenceEnds: 'Reamintește-mi',
      recurrenceNever: 'Pentru totdeauna',
      recurrenceAfter: 'După',
      recurrenceTimes: 'apariții',
      recurrenceOnDate: 'Până la',
      recurrenceEvery: 'La fiecare',
      recurrenceLast: 'ultima',
      recurrenceMonthlyDay: 'Aceeași zi a lunii',
      recUnitDays: 'zile',
      recUnitWeeks: 'săptămâni',
      recUnitMonths: 'luni',
      recUnitYears: 'ani',
      recurrenceDays: ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ'],
      deleteRecurring: 'Acesta este un eveniment recurent.',
      deleteOnlyThis: 'Doar acest eveniment',
      deleteAllSeries: 'Toate evenimentele din serie',
      voiceHint: 'APASĂ ȘI DICTEAZĂ: Eveniment · Dată\nOră · Locație · Prioritate · Note',
      parsing: 'Se completează formularul din voce...',
      parseError: 'Nu s-a putut înțelege înregistrarea. Completează câmpurile manual.',
      imageHint: 'SCANEAZĂ IMAGINE SAU FOTOGRAFIE',
      imageParsing: 'Se citește imaginea...',
      imageError: 'Nu s-a putut citi imaginea. Completează câmpurile manual.',
      multiTitle: "Evenimente găsite",
      multiSubtitle: "Verifică, corectează și creează.",
      multiCreate: "Creează toate",
      multiNoLabel: "Fără",
      multiCreating: "Se creează…",
      multiDetails: "Locație și notițe",
      overlapTitle: "Suprapunere",
      overlapText: "Se suprapune cu «{title}» ({time}). Poți salva oricum.",
      subCalendarLabel: 'Etichetă',
      subCalendarNone: 'Niciunul',
      visibilityLabel: 'Calendare',
      visibilityPrivate: 'Doar eu',
      visibilityGroup: 'Calendar partajat',
      visibilitySelectGroup: 'Selectează grupul',
      conflictTitle: 'Conflict de program',
      conflictHint: 'Ocupați la această oră:',
      reminderLabel: 'Memento',
      reminderNone: 'Fără memento',
      reminderOn: '✓ Activ',
      reminderOff: 'Activați',
      reminderOptions: [
        { value: 0,    label: 'La început' },
        { value: 5,    label: 'Cu 5 min înainte' },
        { value: 10,   label: 'Cu 10 min înainte' },
        { value: 15,   label: 'Cu 15 min înainte' },
        { value: 20,   label: 'Cu 20 min înainte' },
        { value: 25,   label: 'Cu 25 min înainte' },
        { value: 30,   label: 'Cu 30 min înainte' },
        { value: 45,   label: 'Cu 45 min înainte' },
        { value: 60,   label: 'Cu 1 oră înainte' },
        { value: 120,  label: 'Cu 2 ore înainte' },
        { value: 180,  label: 'cu 3 ore înainte' },
        { value: 720,  label: 'cu 12 ore înainte' },
        { value: 1440, label: 'Cu 1 zi înainte' },
        { value: 2880, label: 'cu 2 zile înainte' },
        { value: 10080, label: 'cu 1 săptămână înainte' },
      ],
    },
    groups: {
      pageTitle: 'Calendare partajate',
      noGroups: 'Niciun calendar încă',
      noGroupsSub: 'Creează un calendar sau alătură-te cu un cod de invitație',
      createGroup: 'Creează calendar',
      joinGroup: 'Alătură-te cu cod',
      planLimit: 'Ai atins limita de calendare partajate a planului tău. Treci la un plan superior pentru a crea mai multe.',
      seePlans: 'Vezi planurile',
      calendarFull: 'Acest calendar partajat este plin pentru planul proprietarului.',
      groupNameLabel: 'Numele calendarului',
      groupNamePlaceholder: 'ex. Familia Popescu',
      inviteCode: 'Cod de invitație',
      inviteCodePlaceholder: 'ex. A3F9B2',
      join: 'Alătură-te',
      create: 'Creează',
      creating: 'Se creează…',
      joining: 'Se alătură…',
      members: 'Membri',
      you: '(tu)',
      admin: 'Admin',
      member: 'Membru',
      leave: 'Părăsește grupul',
      leaveConfirm: 'Să părăsești acest grup?',
      delete: 'Șterge grupul',
      deleteConfirm: 'Șterge acest grup? Toți membrii vor pierde accesul.',
      shareInvite: 'Distribuie codul',
      copied: '✓ Cod copiat!',
      errorCreate: 'Nu s-a putut crea grupul. Încearcă din nou.',
      errorJoin: 'Cod invalid. Verifică și încearcă din nou.',
      errorAlready: 'Ești deja membru al acestui grup.',
      cancel: 'Anulează',
      saving: 'Se salvează…',
    },
    install: {
      title: '📲 Instalează MeMo',
      subtitle: 'Instalează-l ca o aplicație reală — fără App Store.',
      installBtn: '📲 Instalează pe acest dispozitiv',
      installed: '✓ Instalat!',
      oneLogin: '🔐 Conectează-te o singură dată — Memo te ține minte pentru totdeauna.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['Deschide acest link în Safari', 'Apasă butonul Partajare □↑ în jos', 'Apasă "Adaugă pe ecranul de start"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['Deschide acest link în Chrome', 'Apasă ⋮ în colțul din dreapta sus', 'Apasă "Adaugă pe ecranul de start"'],
      },
      desktop: {
        title: '💻 Calculator (Chrome)',
        steps: ['Deschide acest link în Chrome', 'Fă clic pe pictograma 📲 din bara de adrese', 'Fă clic pe "Instalează"'],
      },
    },
  },

  zh: {
    nav: { today: '今天', add: '添加', tasks: '笔记', search: '搜索', settings: '设置', groups: '日历' },
    search: {
      placeholder: '搜索事件…',
      searching: '搜索中…',
      noResults: '没有结果：',
      noResultsHint: '换个关键词试试',
      hint: '例如 "牙医"、"会议"、"生日"',
    },
    header: { title: 'Memo' },
    authError: {
      title: '登录失败',
      subtitle: '出了点问题，请重试。',
      back: '返回',
    },
    login: {
      title: 'Memo',
      subtitle: '带语音输入的个人笔记与事件管理器',
      features: [
        '开车时用语音添加事件',
        '用颜色区分优先级',
        '同步到 Google 日历',
        '每天早上 7 点 Telegram 摘要',
      ],
      signIn: '通过 Google 登录',
    },
    onboarding: {
      demo: {
        views: ["日", "周", "月", "年"],
        allDay: "全天",
        prio: ["高", "中", "低"],
        labels: ["工作", "运动", "家庭"],
        shared: "共享",
        create: "创建",
        join: "用代码加入",
        sharedTitle: "共享日历",
        searchTitle: "搜索事件",
        searchHint: "如 牙医、会议…",
        notePh: "写一条笔记…",
        settingsTitle: "设置",
        light: "浅色",
        dark: "深色",
        signout: "退出",
        ics: "下载 .ics",
        notif: "通知",
        summaryLbl: "摘要",
        time: "时间",
        telegram: "通过 Telegram 发送",
        reminder: "提前 30 分钟",
        greeting: "早上好",
        repeat: ["每天", "每周"],
        ev: ["会议", "健身", "购物", "牙医", "晚餐", "快递"],
      },
      skip: "跳过",
      next: "下一步 →",
      start: "开始",
      slides: [
        { key: "calendar", title: "会对你说话的日历", text: "日、周、月、年：随你喜欢查看安排。轻轻一点，还能把当天和本周的日程读给你听。" },
        { key: "add", title: "随你的方式添加", text: "用打字、语音或拍照创建事件：拍下一张纸、一封邮件或一张海报，AI 自动填好。还能给事件添加附件。" },
        { key: "planner", title: "规划你的一天", text: "每日和每周计划表显示空闲与占用的时段：一眼就能安排妥当。" },
        { key: "multi", title: "搜索、记录、共享", text: "一秒找到任何事件，笔记随时在手，把日历与家人或团队共享——每人一种颜色。" },
        { key: "summary", title: "每天早晨，你的摘要", text: "通过 Telegram 和邮件收到当天安排，按时间和优先级排序。开启新的一天，什么都不会忘。" },
        { key: "settings", title: "让 MeMo 为你服务", text: "选择语言、摘要时间、通知、铃声和主题：MeMo 会适应你的工作方式。" },
      ],
    },

    welcome: {
      greeting: '欢迎',
      subtitle: '选择你的语言开始使用',
      saving: '保存中...',
      continue: '继续 →',
      countryLabel: '你的国家',
      countryPlaceholder: '选择你的国家...',
      themeLabel: '选择主题',
      timezoneLabel: '时区',
      timezoneHint: '出行时记得修改',
    },
    eventGenericType: '事件',
    today: {
      noEvents: '没有安排的事件',
      eventsCount: (n) => `今天有 ${n} 个事件`,
      emptyTitle: '今天很清闲！',
      emptySub: '点击 + 添加事件',
      error: '无法加载事件，请重试。',
      retry: '重试',
      signOutHint: '如果问题持续，请尝试退出后重新登录。',
      slowConnection: '网络较慢……仍在加载。',
      aiLimitReached: '本月 Free 计划的 30 次 AI 输入（语音和照片）已用完。下个月重置。',
      weekList: '列表',
      weekGrid: '网格',
      conflictWarning: '注意：与“{title}”（{time}）时间重叠。仍要保存吗？',
      tomorrow: '明天',
      today: '今天',
      yesterday: '昨天',
      snooze: '稍后提醒',
      stop: '停止',
      prevWeek: '上一周',
      nextWeek: '下一周',
      microphone: '麦克风',
      noEventsDay: '没有安排的事件',
      emptyTitleDay: '这里什么都没有',
      eventsCountDay: (n) => `${n} 个事件`,
      prevDay: '前一天',
      nextDay: '后一天',
      filterAll: '全部',
      filterMine: '仅我',
      ttsButton: '朗读',
      ttsStop: '停止',
      ttsNoEvents: '今天没有事件。',
      ttsIntro: '今天你有 {n} 个事件',
      viewDay: '日',
      viewWeek: '周',
      viewMonth: '月',
      viewYear: '年',
      viewSlots: 'Plan',
      slotsHint: '点击空闲时段添加事件',
      slotsTapDay: '点击某天打开它',
      slotsFree: '空闲',
      slotsBusy: '占用',
      weekOf: '周',
    },
    tasks: {
      emptyTitle: '还没有笔记',
      emptySub: '例如 买面包 · 给牙医打电话',
      addPlaceholder: '写一条笔记…',
      doneSection: "已完成",
      deleteNoteConfirm: "永久删除这条笔记？",
      restoreNote: "点按以恢复",
      completed: '已完成',
      noScope: '退出并重新登录以启用笔记',
      deleteConfirm: '删除这个笔记？',
    },
    settings: {
      pageTitle: '设置',
      account: '账户',
      signOut: '退出登录',
      themeColor: "应用主题颜色",
      themeColorBtn: "应用颜色",
      a11yReminders: "无障碍提醒（振动+语音）",
      a11yRemindersHint: "到提醒时间时，手机会振动并朗读事件。",
      a11yMode: "无障碍模式",
      a11yModeHint: "同时开启高对比度，以及带振动和语音的提醒。",
      a11yVibration: "振动",
      a11ySpeak: "朗读事件",
      a11yContrast: "高对比度",
      usageTab: "数据用量",
      a11yTab: "无障碍",
      updateAvailable: "有新版本可用。",
      updateButton: "更新",
      bigText: "大字体",
      bigTextOn: "开",
      bigTextOff: "关",
      bigLarge: "大",
      bigXLarge: "特大",
      feedTitle: "在 Apple 日历中查看 MeMo",
      feedDesc: "适用于 iPhone 和 iCloud：把 MeMo 的安排放进 Apple 日历。",
      feedCopy: "复制链接",
      feedCopied: "已复制！",
      feedSubscribe: "在我的日历中显示",
      feedHint: "iPhone：设置 → 日历 → 账户 → 添加 → 其他 → 添加已订阅日历。Mac：文件 → 新建日历订阅。",
      feedConnect: "连接你的日历",
      feedRegenerate: "重新生成链接",
      saved: '✓ 设置已保存！',
      saveError: '保存设置失败，请重试。',
      language: '语言',
      telegramSection: '每日 Telegram 摘要',
      sendTime: '发送时间',
      timezone: '你的时区',
      tzDetected: '你的设备位于',
      tzUseDetected: '使用这个时区',
      tzAutoDetect: '从我的设备自动检测',
      chatId: 'Telegram 聊天 ID',
      voicePitch: '音高',
      voicePitchLow: '低',
      voicePitchMid: '正常',
      voicePitchHigh: '高',
      pushIntro: '即使应用关闭，通知也会到达手机。点按以启用并测试。',
      pushDenied: '权限被拒绝。请在手机/浏览器设置中启用 MeMo 的通知。',
      pushUnsupported: '此设备不支持推送通知。在 iPhone 上，请将 MeMo 添加到主屏幕。',
      pushUnsupportedApp: '在已安装的应用中，提醒来自手机本身，而不是浏览器：只需确认已允许 MeMo 显示通知。Telegram 和邮件是额外的选择。',
      pushFailed: '失败。请重试或检查通知设置。',
      pushSending: '发送中…',
      pushTestButton: '🔔 启用 / 发送测试',
      pushSent: '✓ 已发送！请检查你的设备。',
      pushError: '出错 — 请在浏览器中启用通知。',
      pushNoDevice: '未送达任何设备。请重试，或在浏览器设置中关闭再开启 MeMo 的通知。',
      chatIdPlaceholder: '例如 123456789',
      chatIdHelp: (bot) => `如何获取 Chat ID：打开 Telegram，找到 @${bot}，给它发送任意消息，然后点击设置指南中的链接。`,
      testSending: '发送中...',
      testButton: '📨 立即发送测试消息',
      testSuccess: '✓ 消息已发送！请查看你的 Telegram。',
      testError: '发送失败，请确认 Chat ID 已保存。',
      saving: '保存中...',
      saveButton: '保存设置',
      telegramSetupTitle: '如何连接 Telegram：',
      telegramStep1: (bot) => `打开 Telegram 并搜索 @${bot}`,
      telegramStep2: '给它发送任意消息以开始',
      telegramStep3: '打开 @userinfobot → 发送 /start → 复制你的 Chat ID',
      telegramStep4: '粘贴到下面的字段并保存',
      supportTitle: "帮助与支持",
      supportDesc: "需要帮助或想报告问题吗？给我们留言，我们会尽快回复。",
      emailSupport: "给我们发邮件",
      contactSupport: '在 Telegram 上联系客服',
      themeSection: '外观',
      themeLabel: '主题',
      themeLight: '浅色',
      themeDark: '深色',
      countryLabel: '国家',
      subCalendarsSection: '标签',
      subCalendarName: '名称',
      subCalendarAdd: '添加标签',
      subCalendarOwn: '给我的日程上色',
      subCalendarPickColour: '选择一个颜色',
      subCalendarAutoHint: '不属于你的日历的标签会获得一个临时颜色 — 点一下即可自己选择。',
      subCalendarDeleteConfirm: '删除这个标签？事件不会被删除。',
      summarySection: '早晨摘要',
      summaryNowhere: "摘要发不出去：MeMo 不知道要发到哪里。请在下面填写邮箱，或连接 Telegram。",
      summaryChannels: '发送到哪里',
      viaTelegram: '通过 Telegram 发送',
      viaEmail: '通过邮件发送',
      emailAnyProvider: '适用于任意邮箱地址 — Gmail、Outlook、iCloud、Yahoo…',
      emailSection: '邮件摘要',
      emailLabel: '你的邮箱地址',
      emailHelp: '输入任意邮箱地址 — Gmail、Outlook、iCloud、工作邮箱等。',
      emailTestSending: '⏳ 发送中...',
      emailTestButton: '📧 发送测试邮件',
      emailTestSuccess: '✓ 邮件已发送！请查看收件箱。',
      emailTestError: '发送邮件失败，请检查服务器配置。',
      voiceLabel: '朗读语音',
      voiceDefault: '系统默认',
      voiceTest: '▶ 试听语音',
      voiceSection: "语音与朗读",
      voiceSpeed: "速度",
      voiceSlow: "慢",
      voiceFast: "快",
      voiceNoVoices: '此设备上没有适用于该语言的语音。',
      voiceFemale: '♀ 女声',
      voiceMale: '♂ 男声',
      voiceNoMatch: '未找到匹配的语音 — 使用系统默认。',
      alarmSection: '通知设置',
      alarmVolume: '音量',
      alarmSnooze: '稍后提醒间隔',
      alarmMaxSnooze: '最多稍后提醒次数',
      alarmForever: '一直',
      alarmMin: '分钟',
      alarmSound: '声音',
      weatherReset: '天气',
      weatherActive: '天气已开启',
      weatherDisable: '关闭',
      weatherDesc: '开启后可在主屏幕查看温度和天气预报。',
      weatherActivating: '请求中…',
      weatherEnable: '开启天气',
      weatherReEnable: '重新开启天气',
      weatherDeniedHint: '浏览器已阻止定位访问。请到浏览器设置中解除阻止，然后重试。',
      weatherDeniedHintApp: "位置权限被拒绝。请在手机设置中允许 MeMo 使用位置，然后重试。",
      weatherUnavailable: '此设备无法使用 GPS。',
      exportLabel: '连接 Apple 日历、Outlook 或其他非 Google 应用',
      exportButton: '下载 .ics',
      exportHint: '导入到 Apple 日历、Outlook 或任意其他应用',
      exporting: '导出中…',
    },
    form: {
      allDay: "全天",
      timeMissing: "请选择时间，或打开「全天」。",
      pageTitle: '新事件',
      required: '请填写所有必填字段。',
      saveError: '保存事件失败，请重试。',
      titleLabel: '事件',
      titlePlaceholder: '例如 看医生',
      cancelButton: '取消',
      recurringEditTitle: '编辑重复事件',
      recurringThisOnly: '仅此事件',
      recurringAllSeries: '系列中的所有事件',
      recurringNote: '重复事件 — 更改仅适用于此次',
      photoLabel: '照片',
      openInMaps: '在 Google 地图中打开',
      recurringEvent: '重复事件',
      typeLabel: '类型 *',
      typeDefault: '选择类型...',
      datetimeLabel: '日期和时间',
      dateFromLabel: '从',
      dateToLabel: '到',
      locationLabel: '地点（可选）',
      locationPlaceholder: '例如 罗马街 10 号，米兰',
      priorityLabel: '优先级',
      priorities: [
        { value: 'red',    label: '高', sublabel: '必须做' },
        { value: 'yellow', label: '中', sublabel: '应该做' },
        { value: 'green',  label: '低', sublabel: '可以做' },
      ],
      notesLabel: '备注（可选）',
      notesPlaceholder: '添加备注，或使用语音...',
      attLabel: '附件',
      attAdd: '📎 附加照片或PDF',
      attHint: '最多5个文件，活动5天后删除',
      attTooMany: '最多5个附件',
      attBadType: '仅照片和PDF',
      attTooBig: '文件过大（最大10 MB）',
      saving: '保存中...',
      saveButton: '✓ 保存事件',
      editPageTitle: '编辑事件',
      editButton: '编辑',
      updateButton: '✓ 更新',
      dictateLabel: '说',
      deleteButton: '删除',
      deleteConfirm: '从 Google 日历删除这个事件？',
      deleteError: '删除事件出错，请重试。',
      recurrenceLabel: '重复',
      recurrenceNone: '不重复',
      recurrenceDaily: '每天',
      recurrenceWeekly: '每周',
      recurrenceMonthly: '每月',
      recurrenceYearly: '每年',
      recurrenceEnds: '保留',
      recurrenceNever: '永远',
      recurrenceAfter: '之后',
      recurrenceTimes: '次',
      recurrenceOnDate: '截止到',
      recurrenceEvery: '每',
      recurrenceLast: '最后一个',
      recurrenceMonthlyDay: '每月同一天',
      recUnitDays: '天',
      recUnitWeeks: '周',
      recUnitMonths: '个月',
      recUnitYears: '年',
      recurrenceDays: ['日', '一', '二', '三', '四', '五', '六'],
      deleteRecurring: '这是一个重复事件。',
      deleteOnlyThis: '仅此事件',
      deleteAllSeries: '该系列的所有事件',
      voiceHint: '点击并口述：事件 · 日期\n时间 · 地点 · 优先级 · 备注',
      parsing: '正在用语音填写表单...',
      parseError: '无法识别录音，请手动填写字段。',
      imageHint: '扫描图片或照片',
      imageParsing: '正在读取图片...',
      imageError: '无法读取图片，请手动填写字段。',
      multiTitle: "找到的事件",
      multiSubtitle: "检查、修改并创建。",
      multiCreate: "全部创建",
      multiNoLabel: "无",
      multiCreating: "创建中…",
      multiDetails: "地点和备注",
      overlapTitle: "时间重叠",
      overlapText: "与《{title}》（{time}）重叠。仍可保存。",
      subCalendarLabel: '标签',
      subCalendarNone: '无',
      visibilityLabel: '日历',
      visibilityPrivate: '仅我',
      visibilityGroup: '共享日历',
      visibilitySelectGroup: '选择群组',
      conflictTitle: '时间冲突',
      conflictHint: '此时间已占用：',
      reminderLabel: '提醒',
      reminderNone: '无提醒',
      reminderOn: '✓ 已开启',
      reminderOff: '开启',
      reminderOptions: [
        { value: 0,    label: '事件开始时' },
        { value: 5,    label: '提前 5 分钟' },
        { value: 10,   label: '提前 10 分钟' },
        { value: 15,   label: '提前 15 分钟' },
        { value: 20,   label: '提前 20 分钟' },
        { value: 25,   label: '提前 25 分钟' },
        { value: 30,   label: '提前 30 分钟' },
        { value: 45,   label: '提前 45 分钟' },
        { value: 60,   label: '提前 1 小时' },
        { value: 120,  label: '提前 2 小时' },
        { value: 180,  label: '提前 3 小时' },
        { value: 720,  label: '提前 12 小时' },
        { value: 1440, label: '提前 1 天' },
        { value: 2880, label: '提前 2 天' },
        { value: 10080, label: '提前 1 周' },
      ],
    },
    groups: {
      pageTitle: '共享日历',
      noGroups: '还没有日历',
      noGroupsSub: '创建日历或用邀请码加入',
      createGroup: '创建日历',
      joinGroup: '用邀请码加入',
      planLimit: '你已达到当前套餐的共享日历上限。升级套餐以创建更多。',
      seePlans: '查看套餐',
      calendarFull: '该共享日历已达到所有者套餐的人数上限。',
      groupNameLabel: '日历名称',
      groupNamePlaceholder: '例如 张家日历',
      inviteCode: '邀请码',
      inviteCodePlaceholder: '例如 A3F9B2',
      join: '加入',
      create: '创建',
      creating: '创建中…',
      joining: '加入中…',
      members: '成员',
      you: '（你）',
      admin: '管理员',
      member: '成员',
      leave: '退出日历',
      leaveConfirm: '退出这个日历？',
      delete: '删除日历',
      deleteConfirm: '删除这个日历？所有成员将失去访问权限。',
      shareInvite: '分享邀请码',
      copied: '✓ 邀请码已复制！',
      errorCreate: '无法创建日历，请重试。',
      errorJoin: '邀请码无效，请检查后重试。',
      errorAlready: '你已经是这个日历的成员。',
      cancel: '取消',
      saving: '保存中…',
    },
    install: {
      title: '📲 安装 MeMo',
      subtitle: '像真正的应用一样安装它 — 无需应用商店。',
      installBtn: '📲 安装到此设备',
      installed: '✓ 已安装！',
      oneLogin: '🔐 登录一次 — Memo 会永远记住你。',
      ios: {
        title: '🍎 iPhone / iPad（Safari）',
        steps: ['在 Safari 中打开此链接', '点击底部的分享按钮 □↑', '点击“添加到主屏幕”'],
      },
      android: {
        title: '🤖 Android（Chrome）',
        steps: ['在 Chrome 中打开此链接', '点击右上角的 ⋮', '点击“添加到主屏幕”'],
      },
      desktop: {
        title: '💻 电脑（Chrome）',
        steps: ['在 Chrome 中打开此链接', '点击地址栏中的 📲 图标', '点击“安装”'],
      },
    },
  },

  ja: {
    nav: { today: '今日', add: '追加', tasks: 'メモ', search: '検索', settings: '設定', groups: 'カレンダー' },
    search: {
      placeholder: '予定を検索…',
      searching: '検索中…',
      noResults: '結果がありません：',
      noResultsHint: '別のキーワードでお試しください',
      hint: '例：「歯医者」「会議」「誕生日」',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'ログインに失敗しました',
      subtitle: '問題が発生しました。もう一度お試しください。',
      back: '戻る',
    },
    login: {
      title: 'Memo',
      subtitle: '音声入力できる、あなたのメモ＆予定マネージャー',
      features: [
        '運転中でも音声で予定を追加',
        '色で優先度をつける',
        'Google カレンダーと同期',
        '毎朝7時に Telegram でまとめ',
      ],
      signIn: 'Google でログイン',
    },
    onboarding: {
      demo: {
        views: ["日", "週", "月", "年"],
        allDay: "終日",
        prio: ["高", "中", "低"],
        labels: ["仕事", "運動", "家庭"],
        shared: "共有",
        create: "作成",
        join: "コードで参加",
        sharedTitle: "共有カレンダー",
        searchTitle: "予定を検索",
        searchHint: "例：歯医者、会議…",
        notePh: "メモを書く…",
        settingsTitle: "設定",
        light: "ライト",
        dark: "ダーク",
        signout: "ログアウト",
        ics: ".ics をダウンロード",
        notif: "通知",
        summaryLbl: "まとめ",
        time: "時刻",
        telegram: "Telegram で送信",
        reminder: "30分前",
        greeting: "おはよう",
        repeat: ["毎日", "毎週"],
        ev: ["会議", "ジム", "買い物", "歯医者", "夕食", "配達"],
      },
      skip: "スキップ",
      next: "次へ →",
      start: "はじめる",
      slides: [
        { key: "calendar", title: "話しかけてくるカレンダー", text: "日・週・月・年、好きな形で予定を確認。ワンタップで、その日と週の予定を読み上げます。" },
        { key: "add", title: "好きな方法で追加", text: "入力・音声・写真で予定を作成：紙やメール、ポスターを撮ればAIが自動入力。予定に書類も添付できます。" },
        { key: "planner", title: "一日を組み立てる", text: "日次・週次のプランナーが空き時間と埋まった時間を表示。ひと目で予定を組めます。" },
        { key: "multi", title: "検索・メモ・共有", text: "どんな予定も一瞬で見つけ、メモはいつでも手元に。家族やチームとカレンダーを共有、色分けで一目瞭然。" },
        { key: "summary", title: "毎朝、あなたのまとめ", text: "その日の予定を Telegram とメールで、時刻と優先度順にお届け。何も忘れずに一日を始められます。" },
        { key: "settings", title: "MeMo をあなた仕様に", text: "言語、まとめの時刻、通知、着信音、テーマを選択。MeMo があなたの働き方に合わせます。" },
      ],
    },

    welcome: {
      greeting: 'ようこそ',
      subtitle: '言語を選んで始めましょう',
      saving: '保存中...',
      continue: '続ける →',
      countryLabel: 'お住まいの国',
      countryPlaceholder: '国を選択...',
      themeLabel: 'テーマを選択',
      timezoneLabel: 'タイムゾーン',
      timezoneHint: '旅行のときは変更してください',
    },
    eventGenericType: '予定',
    today: {
      noEvents: '予定はありません',
      eventsCount: (n) => `今日は ${n} 件の予定`,
      emptyTitle: '今日は予定なし！',
      emptySub: '+ をタップして予定を追加',
      error: '予定を読み込めませんでした。もう一度お試しください。',
      retry: '再試行',
      signOutHint: '問題が続く場合は、ログアウトして再度ログインしてください。',
      slowConnection: '接続が遅いようです…読み込み中です。',
      aiLimitReached: '今月の Free プランの AI 入力（音声・写真）30回を使い切りました。来月リセットされます。',
      weekList: 'リスト',
      weekGrid: 'グリッド',
      conflictWarning: '注意：「{title}」（{time}）と重なっています。保存しますか？',
      tomorrow: '明日',
      today: '今日',
      yesterday: '昨日',
      snooze: 'スヌーズ',
      stop: '停止',
      prevWeek: '前の週',
      nextWeek: '次の週',
      microphone: 'マイク',
      noEventsDay: '予定はありません',
      emptyTitleDay: '何もありません',
      eventsCountDay: (n) => `${n} 件の予定`,
      prevDay: '前の日',
      nextDay: '次の日',
      filterAll: 'すべて',
      filterMine: '自分のみ',
      ttsButton: '読み上げ',
      ttsStop: '停止',
      ttsNoEvents: '今日は予定がありません。',
      ttsIntro: '今日は {n} 件の予定があります',
      viewDay: '日',
      viewWeek: '週',
      viewMonth: '月',
      viewYear: '年',
      viewSlots: 'Plan',
      slotsHint: '空き時間をタップして予定を追加',
      slotsTapDay: '日をタップして開く',
      slotsFree: '空き',
      slotsBusy: '予定あり',
      weekOf: '週',
    },
    tasks: {
      emptyTitle: 'メモはまだありません',
      emptySub: '例：パンを買う · 歯医者に電話',
      addPlaceholder: 'メモを書く…',
      doneSection: "完了",
      deleteNoteConfirm: "このメモを完全に削除しますか？",
      restoreNote: "タップして元に戻す",
      completed: '完了',
      noScope: '一度サインアウトして再度サインインするとメモが有効になります',
      deleteConfirm: 'このメモを削除しますか？',
    },
    settings: {
      pageTitle: '設定',
      account: 'アカウント',
      signOut: 'ログアウト',
      themeColor: "アプリのテーマカラー",
      themeColorBtn: "アプリの色",
      a11yReminders: "アクセシブルなリマインダー（振動＋音声）",
      a11yRemindersHint: "リマインダーの時刻に、電話が振動してイベントを読み上げます。",
      a11yMode: "アクセシビリティモード",
      a11yModeHint: "ハイコントラストと、振動と音声のリマインダーをまとめてオンにします。",
      a11yVibration: "バイブレーション",
      a11ySpeak: "イベントを読み上げ",
      a11yContrast: "ハイコントラスト",
      usageTab: "データ使用量",
      a11yTab: "アクセシビリティ",
      updateAvailable: "新しいバージョンが利用可能です。",
      updateButton: "更新",
      bigText: "大きな文字",
      bigTextOn: "オン",
      bigTextOff: "オフ",
      bigLarge: "大",
      bigXLarge: "特大",
      feedTitle: "Apple カレンダーで MeMo を見る",
      feedDesc: "iPhone と iCloud 向け：MeMo の予定を Apple カレンダーに。",
      feedCopy: "リンクをコピー",
      feedCopied: "コピーしました！",
      feedSubscribe: "マイカレンダーに表示",
      feedHint: "iPhone：設定 → カレンダー → アカウント → 追加 → その他 → 照会するカレンダーを追加。Mac：ファイル → 新規照会カレンダー。",
      feedConnect: "カレンダーを連携",
      feedRegenerate: "リンクを再生成",
      saved: '✓ 設定を保存しました！',
      saveError: '設定の保存に失敗しました。もう一度お試しください。',
      language: '言語',
      telegramSection: '毎日の Telegram まとめ',
      sendTime: '送信時刻',
      timezone: 'タイムゾーン',
      tzDetected: 'お使いのデバイスの地域：',
      tzUseDetected: 'このタイムゾーンを使う',
      tzAutoDetect: 'デバイスから自動検出',
      chatId: 'Telegram チャットID',
      voicePitch: '音の高さ',
      voicePitchLow: '低い',
      voicePitchMid: '標準',
      voicePitchHigh: '高い',
      pushIntro: 'アプリを閉じていても通知は届きます。タップして有効化・テストできます。',
      pushDenied: '許可が拒否されました。端末／ブラウザの設定で MeMo の通知を有効にしてください。',
      pushUnsupported: 'この端末はプッシュ通知に対応していません。iPhone では MeMo をホーム画面に追加してください。',
      pushUnsupportedApp: 'インストール版では、リマインダーはブラウザーではなく端末そのものから届きます。MeMo に通知の表示を許可してあるかだけ確認してください。Telegram とメールは必要なら追加で使えます。',
      pushFailed: '失敗しました。もう一度試すか、通知設定を確認してください。',
      pushSending: '送信中…',
      pushTestButton: '🔔 有効化 / テスト送信',
      pushSent: '✓ 送信しました！端末を確認してください。',
      pushError: 'エラー — ブラウザで通知を有効にしてください。',
      pushNoDevice: '端末に届きませんでした。もう一度試すか、ブラウザ設定で MeMo の通知をオフ／オンしてください。',
      chatIdPlaceholder: '例 123456789',
      chatIdHelp: (bot) => `Chat ID の取得方法：Telegram を開き @${bot} を探して、何かメッセージを送り、セットアップガイドのリンクをタップします。`,
      testSending: '送信中...',
      testButton: '📨 今すぐテストメッセージを送る',
      testSuccess: '✓ メッセージを送信しました！Telegram を確認してください。',
      testError: '送信に失敗しました。Chat ID が保存されているか確認してください。',
      saving: '保存中...',
      saveButton: '設定を保存',
      telegramSetupTitle: 'Telegram の接続方法：',
      telegramStep1: (bot) => `Telegram を開いて @${bot} を検索`,
      telegramStep2: '何かメッセージを送って開始',
      telegramStep3: '@userinfobot を開く → /start を送る → Chat ID をコピー',
      telegramStep4: '下の欄に貼り付けて保存',
      supportTitle: "ヘルプとサポート",
      supportDesc: "サポートが必要ですか、または問題を報告したいですか？ご連絡ください。できるだけ早く返信します。",
      emailSupport: "メールで問い合わせ",
      contactSupport: 'Telegram でサポートに問い合わせる',
      themeSection: '外観',
      themeLabel: 'テーマ',
      themeLight: 'ライト',
      themeDark: 'ダーク',
      countryLabel: '国',
      subCalendarsSection: 'ラベル',
      subCalendarName: '名前',
      subCalendarAdd: 'ラベルを追加',
      subCalendarOwn: '自分の予定に色をつける',
      subCalendarPickColour: '色を選んでください',
      subCalendarAutoHint: '自分のものではないカレンダーのラベルには仮の色がつきます。タップすれば自分で選べます。',
      subCalendarDeleteConfirm: 'このラベルを削除しますか？予定は削除されません。',
      summarySection: '朝のまとめ',
      summaryNowhere: "まとめが送れません。MeMo に送り先がありません。下にメールを入れるか、Telegram を連携してください。",
      summaryChannels: '送信先',
      viaTelegram: 'Telegram で送信',
      viaEmail: 'メールで送信',
      emailAnyProvider: 'どのメールアドレスでも使えます — Gmail、Outlook、iCloud、Yahoo…',
      emailSection: 'メールのまとめ',
      emailLabel: 'メールアドレス',
      emailHelp: '任意のメールアドレスを入力 — Gmail、Outlook、iCloud、仕事用など。',
      emailTestSending: '⏳ 送信中...',
      emailTestButton: '📧 テストメールを送る',
      emailTestSuccess: '✓ メールを送信しました！受信箱を確認してください。',
      emailTestError: 'メール送信に失敗しました。サーバー設定を確認してください。',
      voiceLabel: '読み上げの声',
      voiceDefault: 'システム既定',
      voiceTest: '▶ 音声を試す',
      voiceSection: "音声と読み上げ",
      voiceSpeed: "速度",
      voiceSlow: "遅い",
      voiceFast: "速い",
      voiceNoVoices: 'この端末にはこの言語の音声がありません。',
      voiceFemale: '♀ 女性',
      voiceMale: '♂ 男性',
      voiceNoMatch: '一致する音声がありません — システム既定を使用します。',
      alarmSection: '通知設定',
      alarmVolume: '音量',
      alarmSnooze: 'スヌーズ間隔',
      alarmMaxSnooze: 'スヌーズ最大回数',
      alarmForever: 'ずっと',
      alarmMin: '分',
      alarmSound: '音',
      weatherReset: '天気',
      weatherActive: '天気オン',
      weatherDisable: 'オフにする',
      weatherDesc: 'オンにするとメイン画面に気温と予報が表示されます。',
      weatherActivating: 'リクエスト中…',
      weatherEnable: '天気をオンにする',
      weatherReEnable: '天気を再びオンにする',
      weatherDeniedHint: 'ブラウザが位置情報をブロックしています。ブラウザ設定で解除して再試行してください。',
      weatherDeniedHintApp: "位置情報の利用が拒否されました。端末の設定で MeMo に許可してから、もう一度お試しください。",
      weatherUnavailable: 'この端末では GPS が使えません。',
      exportLabel: 'Apple カレンダー、Outlook、その他 Google 以外のアプリと連携',
      exportButton: '.ics をダウンロード',
      exportHint: 'Apple カレンダー、Outlook、その他のアプリにインポート',
      exporting: 'エクスポート中…',
    },
    form: {
      allDay: "終日",
      timeMissing: "時刻を選ぶか、「終日」をオンにしてください。",
      pageTitle: '新しい予定',
      required: '必須項目をすべて入力してください。',
      saveError: '予定の保存に失敗しました。もう一度お試しください。',
      titleLabel: '予定',
      titlePlaceholder: '例 通院',
      cancelButton: 'キャンセル',
      recurringEditTitle: '繰り返しの予定を編集',
      recurringThisOnly: 'この予定のみ',
      recurringAllSeries: 'シリーズのすべての予定',
      recurringNote: '繰り返しの予定 — 変更はこの回のみに適用されます',
      photoLabel: '写真',
      openInMaps: 'Google マップで開く',
      recurringEvent: '繰り返しの予定',
      typeLabel: '種類 *',
      typeDefault: '種類を選択...',
      datetimeLabel: '日付と時刻',
      dateFromLabel: '開始',
      dateToLabel: '終了',
      locationLabel: '場所（任意）',
      locationPlaceholder: '例 ローマ通り10番地、ミラノ',
      priorityLabel: '優先度',
      priorities: [
        { value: 'red',    label: '高', sublabel: '必須' },
        { value: 'yellow', label: '中', sublabel: 'やるべき' },
        { value: 'green',  label: '低', sublabel: 'できれば' },
      ],
      notesLabel: 'メモ（任意）',
      notesPlaceholder: 'メモを追加、または音声で...',
      attLabel: '添付',
      attAdd: '📎 写真やPDFを添付',
      attHint: '最大5件、予定の5日後に削除',
      attTooMany: '添付は最大5件',
      attBadType: '写真とPDFのみ',
      attTooBig: 'ファイルが大きすぎます（最大10 MB）',
      saving: '保存中...',
      saveButton: '✓ 予定を保存',
      editPageTitle: '予定を編集',
      editButton: '編集',
      updateButton: '✓ 更新',
      dictateLabel: '話す',
      deleteButton: '削除',
      deleteConfirm: 'この予定を Google カレンダーから削除しますか？',
      deleteError: '予定の削除中にエラーが発生しました。もう一度お試しください。',
      recurrenceLabel: '繰り返し',
      recurrenceNone: '繰り返さない',
      recurrenceDaily: '毎日',
      recurrenceWeekly: '毎週',
      recurrenceMonthly: '毎月',
      recurrenceYearly: '毎年',
      recurrenceEnds: '保持',
      recurrenceNever: 'ずっと',
      recurrenceAfter: '次の回数後',
      recurrenceTimes: '回',
      recurrenceOnDate: 'まで',
      recurrenceEvery: '毎',
      recurrenceLast: '最終',
      recurrenceMonthlyDay: '毎月同じ日',
      recUnitDays: '日',
      recUnitWeeks: '週間',
      recUnitMonths: 'か月',
      recUnitYears: '年',
      recurrenceDays: ['日', '月', '火', '水', '木', '金', '土'],
      deleteRecurring: 'これは繰り返しの予定です。',
      deleteOnlyThis: 'この予定のみ',
      deleteAllSeries: 'シリーズのすべての予定',
      voiceHint: 'タップして話す：予定 ・ 日付\n時刻 ・ 場所 ・ 優先度 ・ メモ',
      parsing: '音声からフォームを入力中...',
      parseError: '録音を認識できませんでした。手動で入力してください。',
      imageHint: '画像または写真をスキャン',
      imageParsing: '画像を読み取り中...',
      imageError: '画像を読み取れませんでした。手動で入力してください。',
      multiTitle: "見つかった予定",
      multiSubtitle: "確認・修正して作成。",
      multiCreate: "すべて作成",
      multiNoLabel: "なし",
      multiCreating: "作成中…",
      multiDetails: "場所とメモ",
      overlapTitle: "重複",
      overlapText: "「{title}」（{time}）と重複しています。そのまま保存できます。",
      subCalendarLabel: 'ラベル',
      subCalendarNone: 'なし',
      visibilityLabel: 'カレンダー',
      visibilityPrivate: '自分のみ',
      visibilityGroup: '共有カレンダー',
      visibilitySelectGroup: 'グループを選択',
      conflictTitle: '予定の重複',
      conflictHint: 'この時間は予定あり：',
      reminderLabel: 'リマインダー',
      reminderNone: 'リマインダーなし',
      reminderOn: '✓ オン',
      reminderOff: 'オンにする',
      reminderOptions: [
        { value: 0,    label: '開始時刻に' },
        { value: 5,    label: '5分前' },
        { value: 10,   label: '10分前' },
        { value: 15,   label: '15分前' },
        { value: 20,   label: '20分前' },
        { value: 25,   label: '25分前' },
        { value: 30,   label: '30分前' },
        { value: 45,   label: '45分前' },
        { value: 60,   label: '1時間前' },
        { value: 120,  label: '2時間前' },
        { value: 180,  label: '3時間前' },
        { value: 720,  label: '12時間前' },
        { value: 1440, label: '1日前' },
        { value: 2880, label: '2日前' },
        { value: 10080, label: '1週間前' },
      ],
    },
    groups: {
      pageTitle: '共有カレンダー',
      noGroups: 'まだカレンダーがありません',
      noGroupsSub: 'カレンダーを作成するか招待コードで参加',
      createGroup: 'カレンダーを作成',
      joinGroup: 'コードで参加',
      planLimit: 'プランの共有カレンダー上限に達しました。さらに作成するにはアップグレードしてください。',
      seePlans: 'プランを見る',
      calendarFull: 'この共有カレンダーは所有者のプランの上限に達しています。',
      groupNameLabel: 'カレンダー名',
      groupNamePlaceholder: '例 田中家のカレンダー',
      inviteCode: '招待コード',
      inviteCodePlaceholder: '例 A3F9B2',
      join: '参加',
      create: '作成',
      creating: '作成中…',
      joining: '参加中…',
      members: 'メンバー',
      you: '（あなた）',
      admin: '管理者',
      member: 'メンバー',
      leave: 'カレンダーを退出',
      leaveConfirm: 'このカレンダーを退出しますか？',
      delete: 'カレンダーを削除',
      deleteConfirm: 'このカレンダーを削除しますか？全メンバーがアクセスできなくなります。',
      shareInvite: 'コードを共有',
      copied: '✓ コードをコピーしました！',
      errorCreate: 'カレンダーを作成できませんでした。もう一度お試しください。',
      errorJoin: 'コードが無効です。確認してもう一度お試しください。',
      errorAlready: 'すでにこのカレンダーのメンバーです。',
      cancel: 'キャンセル',
      saving: '保存中…',
    },
    install: {
      title: '📲 MeMo をインストール',
      subtitle: '本物のアプリのようにインストール — App Store 不要。',
      installBtn: '📲 この端末にインストール',
      installed: '✓ インストール済み！',
      oneLogin: '🔐 一度ログインすれば、Memo がずっと覚えています。',
      ios: {
        title: '🍎 iPhone / iPad（Safari）',
        steps: ['このリンクを Safari で開く', '下の共有ボタン □↑ をタップ', '「ホーム画面に追加」をタップ'],
      },
      android: {
        title: '🤖 Android（Chrome）',
        steps: ['このリンクを Chrome で開く', '右上の ⋮ をタップ', '「ホーム画面に追加」をタップ'],
      },
      desktop: {
        title: '💻 パソコン（Chrome）',
        steps: ['このリンクを Chrome で開く', 'アドレスバーの 📲 アイコンをクリック', '「インストール」をクリック'],
      },
    },
  },

  hi: {
    nav: { today: 'आज', add: 'जोड़ें', tasks: 'नोट्स', search: 'खोज', settings: 'सेटिंग्स', groups: 'कैलेंडर' },
    search: {
      placeholder: 'इवेंट खोजें…',
      searching: 'खोज रहे हैं…',
      noResults: 'कोई परिणाम नहीं:',
      noResultsHint: 'अलग कीवर्ड आज़माएं',
      hint: 'उदा. "डॉक्टर", "मीटिंग", "जन्मदिन"',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'लॉगिन विफल',
      subtitle: 'कुछ गलत हुआ। कृपया फिर से प्रयास करें।',
      back: 'वापस जाएं',
    },
    login: {
      title: 'Memo',
      subtitle: 'आवाज़ इनपुट के साथ आपका निजी नोट और इवेंट मैनेजर',
      features: [
        'गाड़ी चलाते समय आवाज़ से इवेंट जोड़ें',
        'रंगों से प्राथमिकता तय करें',
        'Google Calendar के साथ सिंक',
        'हर सुबह 7 बजे Telegram सारांश',
      ],
      signIn: 'Google से साइन इन करें',
    },
    onboarding: {
      demo: {
        views: ["दिन", "सप्ताह", "माह", "वर्ष"],
        allDay: "पूरे दिन",
        prio: ["उच्च", "मध्यम", "कम"],
        labels: ["काम", "खेल", "घर"],
        shared: "साझा",
        create: "बनाएं",
        join: "कोड से जुड़ें",
        sharedTitle: "साझा कैलेंडर",
        searchTitle: "इवेंट खोजें",
        searchHint: "जैसे डेंटिस्ट, मीटिंग…",
        notePh: "एक नोट लिखें…",
        settingsTitle: "सेटिंग्स",
        light: "हल्का",
        dark: "गहरा",
        signout: "साइन आउट",
        ics: ".ics डाउनलोड",
        notif: "सूचनाएं",
        summaryLbl: "सारांश",
        time: "समय",
        telegram: "Telegram से भेजें",
        reminder: "30 मिनट पहले",
        greeting: "सुप्रभात",
        repeat: ["हर दिन", "हर सप्ताह"],
        ev: ["मीटिंग", "जिम", "खरीदारी", "डेंटिस्ट", "रात का खाना", "डिलीवरी"],
      },
      skip: "छोड़ें",
      next: "आगे →",
      start: "शुरू करें",
      slides: [
        { key: "calendar", title: "वह कैलेंडर जो आपसे बात करता है", text: "दिन, सप्ताह, महीना और साल: अपने कार्यक्रम जैसे चाहें देखें। और एक टैप से दिन और सप्ताह के काम ज़ोर से पढ़कर सुनाता है।" },
        { key: "add", title: "जैसे चाहें जोड़ें", text: "इवेंट लिखकर, आवाज़ से या फोटो से बनाएं: किसी कागज़, ईमेल या पोस्टर की फोटो लें और AI सब भर देता है। इवेंट में दस्तावेज़ भी जोड़ सकते हैं।" },
        { key: "planner", title: "अपना दिन व्यवस्थित करें", text: "दैनिक और साप्ताहिक प्लानर खाली और भरे समय दिखाता है: एक नज़र में सब कुछ बैठा लें।" },
        { key: "multi", title: "खोजें, नोट करें, साझा करें", text: "कोई भी इवेंट पल में खोजें, नोट्स हमेशा पास रखें और कैलेंडर परिवार या टीम के साथ साझा करें — हर कोई अपने रंग में।" },
        { key: "summary", title: "हर सुबह, आपका सारांश", text: "दिन के काम Telegram और ईमेल पर पाएं, समय और प्राथमिकता के अनुसार। दिन की शुरुआत कुछ भी भूले बिना करें।" },
        { key: "settings", title: "MeMo को अपने हिसाब से चलाएं", text: "भाषा, सारांश का समय, सूचनाएं, रिंगटोन और थीम चुनें: MeMo आपके काम के तरीके के अनुसार ढल जाता है।" },
      ],
    },

    welcome: {
      greeting: 'स्वागत है',
      subtitle: 'शुरू करने के लिए अपनी भाषा चुनें',
      saving: 'सहेज रहे हैं...',
      continue: 'जारी रखें →',
      countryLabel: 'आपका देश',
      countryPlaceholder: 'अपना देश चुनें...',
      themeLabel: 'अपनी थीम चुनें',
      timezoneLabel: 'समय क्षेत्र',
      timezoneHint: 'यात्रा करते समय इसे बदलें',
    },
    eventGenericType: 'इवेंट',
    today: {
      noEvents: 'कोई इवेंट नहीं',
      eventsCount: (n) => `आज ${n} इवेंट`,
      emptyTitle: 'आज सब साफ़ है!',
      emptySub: 'इवेंट जोड़ने के लिए + दबाएं',
      error: 'इवेंट लोड नहीं हो सके। कृपया फिर से प्रयास करें।',
      retry: 'पुनः प्रयास',
      signOutHint: 'अगर समस्या बनी रहे, तो साइन आउट करके फिर साइन इन करें।',
      slowConnection: 'धीमा कनेक्शन… अभी लोड हो रहा है।',
      aiLimitReached: 'आपने इस महीने Free प्लान की 30 AI प्रविष्टियाँ (आवाज़ और फ़ोटो) उपयोग कर ली हैं। अगले महीने रीसेट होंगी।',
      weekList: 'सूची',
      weekGrid: 'ग्रिड',
      conflictWarning: 'ध्यान दें: यह “{title}” ({time}) से टकरा रहा है। फिर भी सहेजें?',
      tomorrow: 'कल',
      today: 'आज',
      yesterday: 'बीता कल',
      snooze: 'याद दिलाएँ',
      stop: 'रोकें',
      prevWeek: 'पिछला सप्ताह',
      nextWeek: 'अगला सप्ताह',
      microphone: 'माइक्रोफ़ोन',
      noEventsDay: 'कोई इवेंट नहीं',
      emptyTitleDay: 'यहाँ कुछ नहीं',
      eventsCountDay: (n) => `${n} इवेंट`,
      prevDay: 'पिछला दिन',
      nextDay: 'अगला दिन',
      filterAll: 'सभी',
      filterMine: 'केवल मैं',
      ttsButton: 'सुनें',
      ttsStop: 'रोकें',
      ttsNoEvents: 'आज कोई इवेंट नहीं।',
      ttsIntro: 'आज आपके पास {n} इवेंट हैं',
      viewDay: 'दिन',
      viewWeek: 'सप्ताह',
      viewMonth: 'महीना',
      viewYear: 'साल',
      viewSlots: 'Plan',
      slotsHint: 'इवेंट जोड़ने के लिए खाली स्लॉट दबाएं',
      slotsTapDay: 'खोलने के लिए किसी दिन को दबाएं',
      slotsFree: 'खाली',
      slotsBusy: 'व्यस्त',
      weekOf: 'सप्ताह',
    },
    tasks: {
      emptyTitle: 'अभी कोई नोट नहीं',
      emptySub: 'उदा. ब्रेड खरीदें · डेंटिस्ट को कॉल करें',
      addPlaceholder: 'एक नोट लिखें…',
      doneSection: "पूर्ण",
      deleteNoteConfirm: "इस नोट को स्थायी रूप से हटाएं?",
      restoreNote: "पुनर्स्थापित करने के लिए टैप करें",
      completed: 'पूर्ण',
      noScope: 'नोट्स सक्षम करने के लिए साइन आउट करके फिर साइन इन करें',
      deleteConfirm: 'यह नोट हटाएं?',
    },
    settings: {
      pageTitle: 'सेटिंग्स',
      account: 'खाता',
      signOut: 'साइन आउट',
      themeColor: "ऐप थीम रंग",
      themeColorBtn: "ऐप रंग",
      a11yReminders: "सुलभ रिमाइंडर (कंपन + आवाज़)",
      a11yRemindersHint: "रिमाइंडर के समय फ़ोन कंपन करता है और इवेंट को ज़ोर से पढ़ता है।",
      a11yMode: "एक्सेसिबिलिटी मोड",
      a11yModeHint: "एक साथ हाई कॉन्ट्रास्ट और कंपन व आवाज़ वाले रिमाइंडर चालू करता है।",
      a11yVibration: "कंपन",
      a11ySpeak: "इवेंट पढ़ें",
      a11yContrast: "हाई कॉन्ट्रास्ट",
      usageTab: "डेटा उपयोग",
      a11yTab: "पहुँच",
      updateAvailable: "एक नया संस्करण उपलब्ध है।",
      updateButton: "अपडेट करें",
      bigText: "बड़ा टेक्स्ट",
      bigTextOn: "चालू",
      bigTextOff: "बंद",
      bigLarge: "बड़ा",
      bigXLarge: "बहुत बड़ा",
      feedTitle: "Apple Calendar में MeMo देखें",
      feedDesc: "iPhone और iCloud के लिए: MeMo के आपके काम, Apple Calendar में।",
      feedCopy: "लिंक कॉपी करें",
      feedCopied: "कॉपी हो गया!",
      feedSubscribe: "मेरे कैलेंडर में दिखाएँ",
      feedHint: "iPhone: सेटिंग्स → कैलेंडर → खाते → जोड़ें → अन्य → सब्सक्राइब्ड कैलेंडर जोड़ें। Mac: फ़ाइल → नई कैलेंडर सदस्यता।",
      feedConnect: "अपने कैलेंडर कनेक्ट करें",
      feedRegenerate: "लिंक फिर से बनाएं",
      saved: '✓ सेटिंग्स सहेजी गईं!',
      saveError: 'सेटिंग्स सहेजने में विफल। कृपया फिर से प्रयास करें।',
      language: 'भाषा',
      telegramSection: 'दैनिक Telegram सारांश',
      sendTime: 'भेजने का समय',
      timezone: 'आपका समय क्षेत्र',
      tzDetected: 'आपका डिवाइस यहाँ है:',
      tzUseDetected: 'यह समय क्षेत्र उपयोग करें',
      tzAutoDetect: 'मेरे डिवाइस से स्वतः पहचानें',
      chatId: 'Telegram चैट ID',
      voicePitch: 'पिच',
      voicePitchLow: 'कम',
      voicePitchMid: 'सामान्य',
      voicePitchHigh: 'उच्च',
      pushIntro: 'ऐप बंद होने पर भी सूचनाएँ फ़ोन पर आती हैं. सक्षम करने और जाँचने के लिए टैप करें.',
      pushDenied: 'अनुमति अस्वीकृत. अपने फ़ोन/ब्राउज़र सेटिंग में MeMo के लिए सूचनाएँ सक्षम करें.',
      pushUnsupported: 'यह डिवाइस पुश सूचनाओं का समर्थन नहीं करता. iPhone पर MeMo को होम स्क्रीन पर इंस्टॉल करें.',
      pushUnsupportedApp: 'इंस्टॉल किए गए ऐप में रिमाइंडर ब्राउज़र से नहीं, फ़ोन से ही आते हैं: बस यह पक्का करें कि MeMo को सूचनाएँ दिखाने की अनुमति है। Telegram और ईमेल अतिरिक्त हैं, अगर आप चाहें।',
      pushFailed: 'विफल. फिर से प्रयास करें या सूचना सेटिंग जाँचें.',
      pushSending: 'भेजा जा रहा है…',
      pushTestButton: '🔔 सक्षम करें / परीक्षण भेजें',
      pushSent: '✓ भेज दी गई! अपना डिवाइस जाँचें.',
      pushError: 'त्रुटि — ब्राउज़र में सूचनाएँ सक्षम करें.',
      pushNoDevice: 'कोई डिवाइस तक नहीं पहुँचा. फिर से प्रयास करें, या ब्राउज़र सेटिंग में MeMo की सूचनाएँ बंद करके फिर से चालू करें.',
      chatIdPlaceholder: 'जैसे 123456789',
      chatIdHelp: (bot) => `Chat ID कैसे पाएं: Telegram खोलें, @${bot} खोजें, उसे कोई संदेश भेजें, फिर सेटअप गाइड में दिए लिंक पर टैप करें।`,
      testSending: 'भेज रहे हैं...',
      testButton: '📨 अभी टेस्ट संदेश भेजें',
      testSuccess: '✓ संदेश भेजा गया! अपना Telegram देखें।',
      testError: 'भेजने में विफल। सुनिश्चित करें कि Chat ID सहेजा गया है।',
      saving: 'सहेज रहे हैं...',
      saveButton: 'सेटिंग्स सहेजें',
      telegramSetupTitle: 'Telegram कैसे जोड़ें:',
      telegramStep1: (bot) => `Telegram खोलें और @${bot} खोजें`,
      telegramStep2: 'शुरू करने के लिए कोई संदेश भेजें',
      telegramStep3: '@userinfobot खोलें → /start भेजें → अपना Chat ID कॉपी करें',
      telegramStep4: 'नीचे के बॉक्स में पेस्ट करें और सहेजें',
      supportTitle: "सहायता और समर्थन",
      supportDesc: "मदद चाहिए या कोई समस्या बतानी है? हमें लिखें, हम जल्द से जल्द जवाब देंगे।",
      emailSupport: "ईमेल करें",
      contactSupport: 'Telegram पर सहायता से संपर्क करें',
      themeSection: 'रूप',
      themeLabel: 'थीम',
      themeLight: 'हल्की',
      themeDark: 'गहरी',
      countryLabel: 'देश',
      subCalendarsSection: 'लेबल',
      subCalendarName: 'नाम',
      subCalendarAdd: 'लेबल जोड़ें',
      subCalendarOwn: 'मेरे इवेंट को रंग दें',
      subCalendarPickColour: 'एक रंग चुनें',
      subCalendarAutoHint: 'जो कैलेंडर आपके नहीं हैं, उनके लेबल को अस्थायी रंग मिलता है — अपना रंग चुनने के लिए उन पर टैप करें।',
      subCalendarDeleteConfirm: 'यह लेबल हटाएं? इवेंट नहीं हटेंगे।',
      summarySection: 'सुबह का सारांश',
      summaryNowhere: "सारांश कहीं नहीं जा रहा: MeMo को पता नहीं कहाँ भेजे। नीचे अपना ईमेल लिखें, या Telegram जोड़ें।",
      summaryChannels: 'कहाँ भेजें',
      viaTelegram: 'Telegram से भेजें',
      viaEmail: 'ईमेल से भेजें',
      emailAnyProvider: 'किसी भी ईमेल पते के साथ काम करता है — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: 'ईमेल सारांश',
      emailLabel: 'आपका ईमेल पता',
      emailHelp: 'कोई भी ईमेल पता दर्ज करें — Gmail, Outlook, iCloud, काम का ईमेल आदि।',
      emailTestSending: '⏳ भेज रहे हैं...',
      emailTestButton: '📧 टेस्ट ईमेल भेजें',
      emailTestSuccess: '✓ ईमेल भेजा गया! अपना इनबॉक्स देखें।',
      emailTestError: 'ईमेल भेजने में विफल। सर्वर कॉन्फ़िगरेशन जांचें।',
      voiceLabel: 'पढ़ने की आवाज़',
      voiceDefault: 'सिस्टम डिफ़ॉल्ट',
      voiceTest: '▶ आवाज़ जाँचें',
      voiceSection: "आवाज़ और वाचन",
      voiceSpeed: "गति",
      voiceSlow: "धीमी",
      voiceFast: "तेज़",
      voiceNoVoices: 'इस डिवाइस पर इस भाषा के लिए कोई आवाज़ उपलब्ध नहीं।',
      voiceFemale: '♀ महिला',
      voiceMale: '♂ पुरुष',
      voiceNoMatch: 'कोई मेल खाती आवाज़ नहीं मिली — सिस्टम डिफ़ॉल्ट उपयोग कर रहे हैं।',
      alarmSection: 'सूचना सेटिंग्स',
      alarmVolume: 'आवाज़',
      alarmSnooze: 'स्नूज़ अवधि',
      alarmMaxSnooze: 'अधिकतम स्नूज़',
      alarmForever: 'हमेशा',
      alarmMin: 'मिनट',
      alarmSound: 'ध्वनि',
      weatherReset: 'मौसम',
      weatherActive: 'मौसम चालू',
      weatherDisable: 'बंद करें',
      weatherDesc: 'मुख्य स्क्रीन पर तापमान और पूर्वानुमान देखने के लिए चालू करें।',
      weatherActivating: 'अनुरोध कर रहे हैं…',
      weatherEnable: 'मौसम चालू करें',
      weatherReEnable: 'मौसम फिर चालू करें',
      weatherDeniedHint: 'ब्राउज़र ने स्थान पहुँच रोक दी है। ब्राउज़र सेटिंग्स में जाकर अनुमति दें, फिर पुनः प्रयास करें।',
      weatherDeniedHintApp: "स्थान की अनुमति अस्वीकार कर दी गई। फ़ोन की सेटिंग में MeMo को अनुमति दें और फिर कोशिश करें।",
      weatherUnavailable: 'इस डिवाइस पर GPS उपलब्ध नहीं।',
      exportLabel: 'Apple Calendar, Outlook या किसी अन्य गैर-Google ऐप से कनेक्ट करें',
      exportButton: '.ics डाउनलोड करें',
      exportHint: 'Apple Calendar, Outlook या किसी अन्य ऐप में इम्पोर्ट करें',
      exporting: 'एक्सपोर्ट कर रहे हैं…',
    },
    form: {
      allDay: "पूरे दिन",
      timeMissing: "समय चुनें, या «पूरे दिन» चालू करें।",
      pageTitle: 'नया इवेंट',
      required: 'कृपया सभी आवश्यक फ़ील्ड भरें।',
      saveError: 'इवेंट सहेजने में विफल। कृपया फिर से प्रयास करें।',
      titleLabel: 'इवेंट',
      titlePlaceholder: 'जैसे डॉक्टर अपॉइंटमेंट',
      cancelButton: 'रद्द करें',
      recurringEditTitle: 'दोहराए जाने वाले इवेंट को संपादित करें',
      recurringThisOnly: 'केवल यह इवेंट',
      recurringAllSeries: 'श्रृंखला के सभी इवेंट',
      recurringNote: 'दोहराया इवेंट — बदलाव केवल इसी बार पर लागू होंगे',
      photoLabel: 'फ़ोटो',
      openInMaps: 'Google Maps में खोलें',
      recurringEvent: 'दोहराया इवेंट',
      typeLabel: 'प्रकार *',
      typeDefault: 'प्रकार चुनें...',
      datetimeLabel: 'तारीख और समय',
      dateFromLabel: 'से',
      dateToLabel: 'तक',
      locationLabel: 'स्थान (वैकल्पिक)',
      locationPlaceholder: 'जैसे रोमा स्ट्रीट 10, मिलान',
      priorityLabel: 'प्राथमिकता',
      priorities: [
        { value: 'red',    label: 'उच्च', sublabel: 'ज़रूरी' },
        { value: 'yellow', label: 'मध्यम', sublabel: 'करना चाहिए' },
        { value: 'green',  label: 'निम्न', sublabel: 'कर सकें तो' },
      ],
      notesLabel: 'नोट्स (वैकल्पिक)',
      notesPlaceholder: 'नोट जोड़ें, या आवाज़ का उपयोग करें...',
      attLabel: 'अटैचमेंट',
      attAdd: '📎 फ़ोटो या PDF जोड़ें',
      attHint: 'अधिकतम 5 फ़ाइलें, इवेंट के 5 दिन बाद हटेंगी',
      attTooMany: 'अधिकतम 5 अटैचमेंट',
      attBadType: 'केवल फ़ोटो और PDF',
      attTooBig: 'फ़ाइल बहुत बड़ी (अधिकतम 10 MB)',
      saving: 'सहेज रहे हैं...',
      saveButton: '✓ इवेंट सहेजें',
      editPageTitle: 'इवेंट संपादित करें',
      editButton: 'संपादित करें',
      updateButton: '✓ अपडेट',
      dictateLabel: 'बोलें',
      deleteButton: 'हटाएं',
      deleteConfirm: 'इस इवेंट को Google Calendar से हटाएं?',
      deleteError: 'इवेंट हटाने में त्रुटि। कृपया फिर से प्रयास करें।',
      recurrenceLabel: 'दोहराव',
      recurrenceNone: 'दोहराएं नहीं',
      recurrenceDaily: 'हर दिन',
      recurrenceWeekly: 'हर सप्ताह',
      recurrenceMonthly: 'हर महीने',
      recurrenceYearly: 'हर साल',
      recurrenceEnds: 'रखें',
      recurrenceNever: 'हमेशा',
      recurrenceAfter: 'के बाद',
      recurrenceTimes: 'बार',
      recurrenceOnDate: 'तक',
      recurrenceEvery: 'हर',
      recurrenceLast: 'आखिरी',
      recurrenceMonthlyDay: 'महीने का वही दिन',
      recUnitDays: 'दिन',
      recUnitWeeks: 'सप्ताह',
      recUnitMonths: 'महीने',
      recUnitYears: 'साल',
      recurrenceDays: ['र', 'सो', 'मं', 'बु', 'गु', 'शु', 'श'],
      deleteRecurring: 'यह एक दोहराने वाला इवेंट है।',
      deleteOnlyThis: 'केवल यह इवेंट',
      deleteAllSeries: 'श्रृंखला के सभी इवेंट',
      voiceHint: 'दबाएं और बोलें: इवेंट · तारीख\nसमय · स्थान · प्राथमिकता · नोट्स',
      parsing: 'आवाज़ से फ़ॉर्म भर रहे हैं...',
      parseError: 'रिकॉर्डिंग समझ नहीं आई। कृपया फ़ील्ड मैन्युअल रूप से भरें।',
      imageHint: 'छवि या फ़ोटो स्कैन करें',
      imageParsing: 'छवि पढ़ रहे हैं...',
      imageError: 'छवि पढ़ नहीं सके। कृपया फ़ील्ड मैन्युअल रूप से भरें।',
      multiTitle: "मिले इवेंट",
      multiSubtitle: "जांचें, ठीक करें और बनाएं।",
      multiCreate: "सभी बनाएं",
      multiNoLabel: "कोई नहीं",
      multiCreating: "बना रहे हैं…",
      multiDetails: "स्थान और नोट्स",
      overlapTitle: "ओवरलैप",
      overlapText: "«{title}» ({time}) से ओवरलैप हो रहा है। फिर भी सहेज सकते हैं।",
      subCalendarLabel: 'लेबल',
      subCalendarNone: 'कोई नहीं',
      visibilityLabel: 'कैलेंडर',
      visibilityPrivate: 'केवल मैं',
      visibilityGroup: 'साझा कैलेंडर',
      visibilitySelectGroup: 'समूह चुनें',
      conflictTitle: 'समय टकराव',
      conflictHint: 'इस समय व्यस्त:',
      reminderLabel: 'रिमाइंडर',
      reminderNone: 'कोई रिमाइंडर नहीं',
      reminderOn: '✓ चालू',
      reminderOff: 'चालू करें',
      reminderOptions: [
        { value: 0,    label: 'शुरू होने पर' },
        { value: 5,    label: '5 मिनट पहले' },
        { value: 10,   label: '10 मिनट पहले' },
        { value: 15,   label: '15 मिनट पहले' },
        { value: 20,   label: '20 मिनट पहले' },
        { value: 25,   label: '25 मिनट पहले' },
        { value: 30,   label: '30 मिनट पहले' },
        { value: 45,   label: '45 मिनट पहले' },
        { value: 60,   label: '1 घंटा पहले' },
        { value: 120,  label: '2 घंटे पहले' },
        { value: 180,  label: '3 घंटे पहले' },
        { value: 720,  label: '12 घंटे पहले' },
        { value: 1440, label: '1 दिन पहले' },
        { value: 2880, label: '2 दिन पहले' },
        { value: 10080, label: '1 सप्ताह पहले' },
      ],
    },
    groups: {
      pageTitle: 'साझा कैलेंडर',
      noGroups: 'अभी कोई कैलेंडर नहीं',
      noGroupsSub: 'कैलेंडर बनाएं या आमंत्रण कोड से जुड़ें',
      createGroup: 'कैलेंडर बनाएं',
      joinGroup: 'कोड से जुड़ें',
      planLimit: 'आप अपने प्लान की साझा कैलेंडर सीमा तक पहुँच गए हैं। और बनाने के लिए अपग्रेड करें।',
      seePlans: 'प्लान देखें',
      calendarFull: 'यह साझा कैलेंडर मालिक के प्लान के लिए भर चुका है।',
      groupNameLabel: 'कैलेंडर का नाम',
      groupNamePlaceholder: 'जैसे शर्मा परिवार कैलेंडर',
      inviteCode: 'आमंत्रण कोड',
      inviteCodePlaceholder: 'जैसे A3F9B2',
      join: 'जुड़ें',
      create: 'बनाएं',
      creating: 'बना रहे हैं…',
      joining: 'जुड़ रहे हैं…',
      members: 'सदस्य',
      you: '(आप)',
      admin: 'व्यवस्थापक',
      member: 'सदस्य',
      leave: 'कैलेंडर छोड़ें',
      leaveConfirm: 'यह कैलेंडर छोड़ें?',
      delete: 'कैलेंडर हटाएं',
      deleteConfirm: 'यह कैलेंडर हटाएं? सभी सदस्यों की पहुँच खत्म हो जाएगी।',
      shareInvite: 'कोड साझा करें',
      copied: '✓ कोड कॉपी हुआ!',
      errorCreate: 'कैलेंडर नहीं बना सके। कृपया फिर से प्रयास करें।',
      errorJoin: 'अमान्य कोड। जांचें और फिर से प्रयास करें।',
      errorAlready: 'आप पहले से ही इस कैलेंडर के सदस्य हैं।',
      cancel: 'रद्द करें',
      saving: 'सहेज रहे हैं…',
    },
    install: {
      title: '📲 MeMo इंस्टॉल करें',
      subtitle: 'असली ऐप की तरह इंस्टॉल करें — App Store की ज़रूरत नहीं।',
      installBtn: '📲 इस डिवाइस पर इंस्टॉल करें',
      installed: '✓ इंस्टॉल हो गया!',
      oneLogin: '🔐 एक बार लॉगिन करें — Memo आपको हमेशा याद रखता है।',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['यह लिंक Safari में खोलें', 'नीचे शेयर बटन □↑ दबाएं', '"Add to Home Screen" दबाएं'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['यह लिंक Chrome में खोलें', 'ऊपर दाईं ओर ⋮ दबाएं', '"Add to Home Screen" दबाएं'],
      },
      desktop: {
        title: '💻 कंप्यूटर (Chrome)',
        steps: ['यह लिंक Chrome में खोलें', 'एड्रेस बार में 📲 आइकन क्लिक करें', '"Install" क्लिक करें'],
      },
    },
  },

  ko: {
    nav: { today: '오늘', add: '추가', tasks: '메모', search: '검색', settings: '설정', groups: '캘린더' },
    search: {
      placeholder: '이벤트 검색…',
      searching: '검색 중…',
      noResults: '검색 결과 없음:',
      noResultsHint: '다른 키워드를 사용해 보세요',
      hint: '예: "치과", "회의", "생일"',
    },
    header: { title: 'Memo' },
    authError: {
      title: '로그인 실패',
      subtitle: '문제가 발생했습니다. 다시 시도해 주세요.',
      back: '돌아가기',
    },
    login: {
      title: 'Memo',
      subtitle: '음성 입력으로 관리하는 나만의 메모·일정 매니저',
      features: [
        '운전 중에도 음성으로 일정 추가',
        '색상으로 우선순위 정하기',
        'Google Calendar와 동기화',
        '매일 아침 7시 Telegram 요약',
      ],
      signIn: 'Google로 로그인',
    },
    onboarding: {
      demo: {
        views: ["일", "주", "월", "년"],
        allDay: "종일",
        prio: ["높음", "중간", "낮음"],
        labels: ["업무", "운동", "집"],
        shared: "공유",
        create: "만들기",
        join: "코드로 참여",
        sharedTitle: "공유 캘린더",
        searchTitle: "일정 검색",
        searchHint: "예: 치과, 회의…",
        notePh: "메모 작성…",
        settingsTitle: "설정",
        light: "라이트",
        dark: "다크",
        signout: "로그아웃",
        ics: ".ics 다운로드",
        notif: "알림",
        summaryLbl: "요약",
        time: "시간",
        telegram: "텔레그램으로 전송",
        reminder: "30분 전",
        greeting: "좋은 아침",
        repeat: ["매일", "매주"],
        ev: ["회의", "헬스장", "장보기", "치과", "저녁", "배송"],
      },
      skip: "건너뛰기",
      next: "다음 →",
      start: "시작하기",
      slides: [
        { key: "calendar", title: "당신에게 말을 거는 캘린더", text: "일·주·월·년, 원하는 방식으로 일정을 보세요. 한 번의 탭으로 오늘과 이번 주 일정을 소리 내어 읽어 줍니다." },
        { key: "add", title: "원하는 방식으로 추가", text: "입력, 음성, 사진으로 일정 생성: 종이나 이메일, 포스터를 찍으면 AI가 다 채웁니다. 일정에 문서도 첨부할 수 있어요." },
        { key: "planner", title: "하루를 계획하세요", text: "일간·주간 플래너가 비어 있는 시간과 찬 시간을 보여 줍니다. 한눈에 일정을 맞춰 넣으세요." },
        { key: "multi", title: "검색하고, 메모하고, 공유하고", text: "어떤 일정이든 순식간에 찾고, 메모는 늘 손안에. 가족이나 팀과 캘린더를 공유하세요 — 각자의 색으로." },
        { key: "summary", title: "매일 아침, 당신의 요약", text: "그날의 일정을 텔레그램과 이메일로, 시간과 우선순위 순으로 받아보세요. 아무것도 잊지 않고 하루를 시작하세요." },
        { key: "settings", title: "MeMo를 당신에 맞게", text: "언어, 요약 시간, 알림, 벨소리, 테마를 선택하세요. MeMo가 당신의 방식에 맞춰집니다." },
      ],
    },

    welcome: {
      greeting: '환영합니다',
      subtitle: '시작하려면 언어를 선택하세요',
      saving: '저장 중...',
      continue: '계속 →',
      countryLabel: '국가',
      countryPlaceholder: '국가를 선택하세요...',
      themeLabel: '테마 선택',
      timezoneLabel: '시간대',
      timezoneHint: '여행 중에는 변경하세요',
    },
    eventGenericType: '이벤트',
    today: {
      noEvents: '예정된 이벤트 없음',
      eventsCount: (n) => `오늘 이벤트 ${n}개`,
      emptyTitle: '오늘은 일정이 없어요!',
      emptySub: '+ 를 눌러 이벤트를 추가하세요',
      error: '이벤트를 불러오지 못했습니다. 다시 시도해 주세요.',
      retry: '다시 시도',
      signOutHint: '문제가 계속되면 로그아웃 후 다시 로그인해 보세요.',
      slowConnection: '연결이 느립니다… 계속 불러오는 중입니다.',
      aiLimitReached: '이번 달 Free 플랜의 AI 입력(음성·사진) 30회를 모두 사용했습니다. 다음 달에 초기화됩니다.',
      weekList: '목록',
      weekGrid: '그리드',
      conflictWarning: '주의: “{title}”({time})과 겹칩니다. 그래도 저장할까요?',
      tomorrow: '내일',
      today: '오늘',
      yesterday: '어제',
      snooze: '다시 알림',
      stop: '중지',
      prevWeek: '지난 주',
      nextWeek: '다음 주',
      microphone: '마이크',
      noEventsDay: '예정된 이벤트 없음',
      emptyTitleDay: '여기엔 아무것도 없어요',
      eventsCountDay: (n) => `이벤트 ${n}개`,
      prevDay: '이전 날',
      nextDay: '다음 날',
      filterAll: '전체',
      filterMine: '나만',
      ttsButton: '듣기',
      ttsStop: '정지',
      ttsNoEvents: '오늘은 이벤트가 없습니다.',
      ttsIntro: '오늘 {n}개의 이벤트가 있습니다',
      viewDay: '일',
      viewWeek: '주',
      viewMonth: '월',
      viewYear: '년',
      viewSlots: 'Plan',
      slotsHint: '빈 시간대를 눌러 이벤트를 추가하세요',
      slotsTapDay: '날짜를 눌러 여세요',
      slotsFree: '비어 있음',
      slotsBusy: '바쁨',
      weekOf: '주',
    },
    tasks: {
      emptyTitle: '아직 메모가 없습니다',
      emptySub: '예: 빵 사기 · 치과 전화하기',
      addPlaceholder: '메모 작성…',
      doneSection: "완료",
      deleteNoteConfirm: "이 메모를 영구적으로 삭제할까요?",
      restoreNote: "탭하여 복원",
      completed: '완료',
      noScope: '메모를 사용하려면 로그아웃 후 다시 로그인하세요',
      deleteConfirm: '이 메모를 삭제할까요?',
    },
    settings: {
      pageTitle: '설정',
      account: '계정',
      signOut: '로그아웃',
      themeColor: "앱 테마 색상",
      themeColorBtn: "앱 색상",
      a11yReminders: "접근성 알림 (진동 + 음성)",
      a11yRemindersHint: "알림 시간에 휴대폰이 진동하고 일정을 소리 내어 읽어줍니다.",
      a11yMode: "접근성 모드",
      a11yModeHint: "고대비와 진동·음성 알림을 한 번에 켭니다.",
      a11yVibration: "진동",
      a11ySpeak: "일정 읽어주기",
      a11yContrast: "고대비",
      usageTab: "데이터 사용량",
      a11yTab: "접근성",
      updateAvailable: "새 버전을 사용할 수 있습니다.",
      updateButton: "업데이트",
      bigText: "큰 글자",
      bigTextOn: "켜짐",
      bigTextOff: "꺼짐",
      bigLarge: "크게",
      bigXLarge: "아주 크게",
      feedTitle: "Apple 캘린더에서 MeMo 보기",
      feedDesc: "iPhone과 iCloud용: MeMo의 일정을 Apple 캘린더에.",
      feedCopy: "링크 복사",
      feedCopied: "복사됨!",
      feedSubscribe: "내 캘린더에 표시",
      feedHint: "iPhone: 설정 → 캘린더 → 계정 → 추가 → 기타 → 구독 캘린더 추가. Mac: 파일 → 새로운 캘린더 구독.",
      feedConnect: "캘린더 연결",
      feedRegenerate: "링크 재생성",
      saved: '✓ 설정이 저장되었습니다!',
      saveError: '설정을 저장하지 못했습니다. 다시 시도해 주세요.',
      language: '언어',
      telegramSection: '매일 Telegram 요약',
      sendTime: '발송 시간',
      timezone: '시간대',
      tzDetected: '기기 위치:',
      tzUseDetected: '이 시간대 사용',
      tzAutoDetect: '내 기기에서 자동 감지',
      chatId: 'Telegram 채팅 ID',
      voicePitch: '음높이',
      voicePitchLow: '낮음',
      voicePitchMid: '보통',
      voicePitchHigh: '높음',
      pushIntro: '앱이 닫혀 있어도 알림이 전화기에 도착합니다. 탭하여 켜고 테스트하세요.',
      pushDenied: '권한이 거부되었습니다. 휴대폰/브라우저 설정에서 MeMo 알림을 켜세요.',
      pushUnsupported: '이 기기는 푸시를 지원하지 않습니다. iPhone에서는 MeMo를 홈 화면에 설치하세요.',
      pushUnsupportedApp: '설치된 앱에서는 알림이 브라우저가 아니라 휴대폰 자체에서 옵니다. MeMo에 알림 표시 권한을 주었는지만 확인하세요. Telegram과 이메일은 원하면 추가로 쓸 수 있습니다.',
      pushFailed: '실패했습니다. 다시 시도하거나 알림 설정을 확인하세요.',
      pushSending: '전송 중…',
      pushTestButton: '🔔 켜기 / 테스트 전송',
      pushSent: '✓ 전송됨! 기기를 확인하세요.',
      pushError: '오류 — 브라우저에서 알림을 켜세요.',
      pushNoDevice: '도달한 기기가 없습니다. 다시 시도하거나 브라우저 설정에서 MeMo 알림을 껐다 켜세요.',
      chatIdPlaceholder: '예: 123456789',
      chatIdHelp: (bot) => `Chat ID 찾는 법: Telegram을 열고 @${bot}을(를) 검색해 아무 메시지나 보낸 다음, 설정 가이드의 링크를 누르세요.`,
      testSending: '발송 중...',
      testButton: '📨 지금 테스트 메시지 보내기',
      testSuccess: '✓ 메시지를 보냈습니다! Telegram을 확인하세요.',
      testError: '발송하지 못했습니다. Chat ID가 저장되었는지 확인하세요.',
      saving: '저장 중...',
      saveButton: '설정 저장',
      telegramSetupTitle: 'Telegram 연결 방법:',
      telegramStep1: (bot) => `Telegram을 열고 @${bot}을(를) 검색하세요`,
      telegramStep2: '시작하려면 아무 메시지나 보내세요',
      telegramStep3: '@userinfobot 열기 → /start 전송 → Chat ID 복사',
      telegramStep4: '아래 칸에 붙여넣고 저장하세요',
      supportTitle: "도움말 및 지원",
      supportDesc: "도움이 필요하거나 문제를 신고하고 싶으신가요? 메시지를 보내주시면 최대한 빨리 답변드리겠습니다.",
      emailSupport: "이메일 보내기",
      contactSupport: 'Telegram으로 고객지원에 문의하기',
      themeSection: '모양',
      themeLabel: '테마',
      themeLight: '라이트',
      themeDark: '다크',
      countryLabel: '국가',
      subCalendarsSection: '라벨',
      subCalendarName: '이름',
      subCalendarAdd: '라벨 추가',
      subCalendarOwn: '내 일정에 색상 적용',
      subCalendarPickColour: '색상을 선택하세요',
      subCalendarAutoHint: '내 것이 아닌 캘린더의 라벨에는 임시 색상이 지정됩니다. 탭해서 직접 고르세요.',
      subCalendarDeleteConfirm: '이 라벨을 삭제할까요? 이벤트는 삭제되지 않습니다.',
      summarySection: '아침 요약',
      summaryNowhere: "요약이 전송되지 않습니다. MeMo가 보낼 곳을 모릅니다. 아래에 이메일을 적거나 Telegram을 연결하세요.",
      summaryChannels: '받을 곳',
      viaTelegram: 'Telegram으로 보내기',
      viaEmail: '이메일로 보내기',
      emailAnyProvider: '모든 이메일 주소에서 작동합니다 — Gmail, Outlook, iCloud, Yahoo…',
      emailSection: '이메일 요약',
      emailLabel: '내 이메일 주소',
      emailHelp: '어떤 이메일 주소든 입력하세요 — Gmail, Outlook, iCloud, 회사 이메일 등.',
      emailTestSending: '⏳ 발송 중...',
      emailTestButton: '📧 테스트 이메일 보내기',
      emailTestSuccess: '✓ 이메일을 보냈습니다! 받은편지함을 확인하세요.',
      emailTestError: '이메일을 보내지 못했습니다. 서버 설정을 확인하세요.',
      voiceLabel: '읽어주는 음성',
      voiceDefault: '시스템 기본값',
      voiceTest: '▶ 음성 테스트',
      voiceSection: "음성 및 읽어주기",
      voiceSpeed: "속도",
      voiceSlow: "느림",
      voiceFast: "빠름",
      voiceNoVoices: '이 기기에는 이 언어의 음성이 없습니다.',
      voiceFemale: '♀ 여성',
      voiceMale: '♂ 남성',
      voiceNoMatch: '일치하는 음성이 없어 시스템 기본값을 사용합니다.',
      alarmSection: '알림 설정',
      alarmVolume: '볼륨',
      alarmSnooze: '다시 알림 간격',
      alarmMaxSnooze: '최대 다시 알림 횟수',
      alarmForever: '항상',
      alarmMin: '분',
      alarmSound: '소리',
      weatherReset: '날씨',
      weatherActive: '날씨 켜짐',
      weatherDisable: '끄기',
      weatherDesc: '메인 화면에서 기온과 예보를 보려면 켜세요.',
      weatherActivating: '요청 중…',
      weatherEnable: '날씨 켜기',
      weatherReEnable: '날씨 다시 켜기',
      weatherDeniedHint: '브라우저가 위치 접근을 차단했습니다. 브라우저 설정에서 허용한 뒤 다시 시도하세요.',
      weatherDeniedHintApp: "위치 접근이 거부되었습니다. 휴대폰 설정에서 MeMo에 허용한 뒤 다시 시도하세요.",
      weatherUnavailable: '이 기기에서는 GPS를 사용할 수 없습니다.',
      exportLabel: 'Apple Calendar, Outlook 또는 다른 비 Google 앱과 연결',
      exportButton: '.ics 다운로드',
      exportHint: 'Apple Calendar, Outlook 또는 다른 앱으로 가져오기',
      exporting: '내보내는 중…',
    },
    form: {
      allDay: "종일",
      timeMissing: "시간을 고르거나 ‘종일’을 켜세요.",
      pageTitle: '새 이벤트',
      required: '필수 항목을 모두 입력하세요.',
      saveError: '이벤트를 저장하지 못했습니다. 다시 시도해 주세요.',
      titleLabel: '이벤트',
      titlePlaceholder: '예: 병원 예약',
      cancelButton: '취소',
      recurringEditTitle: '반복 일정 편집',
      recurringThisOnly: '이 일정만',
      recurringAllSeries: '시리즈의 모든 일정',
      recurringNote: '반복 일정 — 변경사항은 이 일정에만 적용됩니다',
      photoLabel: '사진',
      openInMaps: 'Google 지도에서 열기',
      recurringEvent: '반복 일정',
      typeLabel: '유형 *',
      typeDefault: '유형 선택...',
      datetimeLabel: '날짜 및 시간',
      dateFromLabel: '시작',
      dateToLabel: '종료',
      locationLabel: '장소 (선택)',
      locationPlaceholder: '예: 로마 거리 10, 밀라노',
      priorityLabel: '우선순위',
      priorities: [
        { value: 'red',    label: '높음',  sublabel: '꼭 해야 함' },
        { value: 'yellow', label: '보통',  sublabel: '하는 게 좋음' },
        { value: 'green',  label: '낮음',  sublabel: '여유 있으면' },
      ],
      notesLabel: '메모 (선택)',
      notesPlaceholder: '메모를 추가하거나 음성을 사용하세요...',
      attLabel: '첨부',
      attAdd: '📎 사진 또는 PDF 첨부',
      attHint: '최대 5개, 일정 5일 후 삭제',
      attTooMany: '최대 5개 첨부',
      attBadType: '사진과 PDF만',
      attTooBig: '파일이 너무 큼 (최대 10 MB)',
      saving: '저장 중...',
      saveButton: '✓ 이벤트 저장',
      editPageTitle: '이벤트 편집',
      editButton: '편집',
      updateButton: '✓ 업데이트',
      dictateLabel: '말하기',
      deleteButton: '삭제',
      deleteConfirm: '이 이벤트를 Google Calendar에서 삭제할까요?',
      deleteError: '이벤트 삭제 중 오류가 발생했습니다. 다시 시도해 주세요.',
      recurrenceLabel: '반복',
      recurrenceNone: '반복 안 함',
      recurrenceDaily: '매일',
      recurrenceWeekly: '매주',
      recurrenceMonthly: '매월',
      recurrenceYearly: '매년',
      recurrenceEnds: '기억',
      recurrenceNever: '계속',
      recurrenceAfter: '다음 횟수 후',
      recurrenceTimes: '회',
      recurrenceOnDate: '까지',
      recurrenceEvery: '매',
      recurrenceLast: '마지막',
      recurrenceMonthlyDay: '매월 같은 날',
      recUnitDays: '일',
      recUnitWeeks: '주',
      recUnitMonths: '개월',
      recUnitYears: '년',
      recurrenceDays: ['일', '월', '화', '수', '목', '금', '토'],
      deleteRecurring: '반복되는 이벤트입니다.',
      deleteOnlyThis: '이 이벤트만',
      deleteAllSeries: '시리즈의 모든 이벤트',
      voiceHint: '누르고 말하세요: 이벤트 · 날짜\n시간 · 장소 · 우선순위 · 메모',
      parsing: '음성으로 양식을 채우는 중...',
      parseError: '녹음을 이해하지 못했습니다. 항목을 직접 입력해 주세요.',
      imageHint: '이미지 또는 사진 스캔',
      imageParsing: '이미지를 읽는 중...',
      imageError: '이미지를 읽지 못했습니다. 항목을 직접 입력해 주세요.',
      multiTitle: "찾은 일정",
      multiSubtitle: "확인·수정 후 만들기.",
      multiCreate: "모두 만들기",
      multiNoLabel: "없음",
      multiCreating: "생성 중…",
      multiDetails: "장소 및 메모",
      overlapTitle: "겹침",
      overlapText: "«{title}» ({time})과(와) 겹칩니다. 그래도 저장할 수 있습니다.",
      subCalendarLabel: '라벨',
      subCalendarNone: '없음',
      visibilityLabel: '캘린더',
      visibilityPrivate: '나만',
      visibilityGroup: '공유 캘린더',
      visibilitySelectGroup: '그룹 선택',
      conflictTitle: '일정 충돌',
      conflictHint: '이 시간에 바쁨:',
      reminderLabel: '리마인더',
      reminderNone: '리마인더 없음',
      reminderOn: '✓ 사용 중',
      reminderOff: '사용',
      reminderOptions: [
        { value: 0,    label: '시작 시각에' },
        { value: 5,    label: '5분 전' },
        { value: 10,   label: '10분 전' },
        { value: 15,   label: '15분 전' },
        { value: 20,   label: '20분 전' },
        { value: 25,   label: '25분 전' },
        { value: 30,   label: '30분 전' },
        { value: 45,   label: '45분 전' },
        { value: 60,   label: '1시간 전' },
        { value: 120,  label: '2시간 전' },
        { value: 180,  label: '3시간 전' },
        { value: 720,  label: '12시간 전' },
        { value: 1440, label: '1일 전' },
        { value: 2880, label: '2일 전' },
        { value: 10080, label: '1주 전' },
      ],
    },
    groups: {
      pageTitle: '공유 캘린더',
      noGroups: '아직 캘린더가 없어요',
      noGroupsSub: '캘린더를 만들거나 초대 코드로 참여하세요',
      createGroup: '캘린더 만들기',
      joinGroup: '코드로 참여',
      planLimit: '요금제의 공유 캘린더 한도에 도달했습니다. 더 만들려면 업그레이드하세요.',
      seePlans: '요금제 보기',
      calendarFull: '이 공유 캘린더는 소유자 요금제 기준으로 가득 찼습니다.',
      groupNameLabel: '캘린더 이름',
      groupNamePlaceholder: '예: 김씨 가족 캘린더',
      inviteCode: '초대 코드',
      inviteCodePlaceholder: '예: A3F9B2',
      join: '참여',
      create: '만들기',
      creating: '만드는 중…',
      joining: '참여하는 중…',
      members: '멤버',
      you: '(나)',
      admin: '관리자',
      member: '멤버',
      leave: '캘린더 나가기',
      leaveConfirm: '이 캘린더를 나갈까요?',
      delete: '캘린더 삭제',
      deleteConfirm: '이 캘린더를 삭제할까요? 모든 멤버가 접근할 수 없게 됩니다.',
      shareInvite: '코드 공유',
      copied: '✓ 코드가 복사되었습니다!',
      errorCreate: '캘린더를 만들지 못했습니다. 다시 시도해 주세요.',
      errorJoin: '잘못된 코드입니다. 확인 후 다시 시도하세요.',
      errorAlready: '이미 이 캘린더의 멤버입니다.',
      cancel: '취소',
      saving: '저장 중…',
    },
    install: {
      title: '📲 MeMo 설치',
      subtitle: '진짜 앱처럼 설치하세요 — App Store가 필요 없어요.',
      installBtn: '📲 이 기기에 설치',
      installed: '✓ 설치 완료!',
      oneLogin: '🔐 한 번만 로그인하면 — Memo가 계속 기억해요.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['이 링크를 Safari에서 열기', '하단의 공유 버튼 □↑ 누르기', '"Add to Home Screen" 누르기'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['이 링크를 Chrome에서 열기', '오른쪽 위 ⋮ 누르기', '"Add to Home Screen" 누르기'],
      },
      desktop: {
        title: '💻 컴퓨터 (Chrome)',
        steps: ['이 링크를 Chrome에서 열기', '주소창의 📲 아이콘 클릭', '"Install" 클릭'],
      },
    },
  },

  ar: {
    nav: { today: 'اليوم', add: 'إضافة', tasks: 'ملاحظات', search: 'بحث', settings: 'الإعدادات', groups: 'التقويمات' },
    search: {
      placeholder: 'ابحث عن الأحداث…',
      searching: 'جارٍ البحث…',
      noResults: 'لا نتائج لـ',
      noResultsHint: 'جرّب كلمات مفتاحية مختلفة',
      hint: 'مثال: «طبيب الأسنان»، «اجتماع»، «عيد ميلاد»',
    },
    header: { title: 'Memo' },
    authError: {
      title: 'فشل تسجيل الدخول',
      subtitle: 'حدث خطأ ما. يُرجى المحاولة مرة أخرى.',
      back: 'رجوع',
    },
    login: {
      title: 'Memo',
      subtitle: 'مدير الملاحظات والأحداث الشخصي مع الإدخال الصوتي',
      features: [
        'أضف الأحداث بصوتك أثناء القيادة',
        'رتّب الأولويات بالألوان',
        'المزامنة مع Google Calendar',
        'ملخص يومي على Telegram الساعة 7 صباحًا',
      ],
      signIn: 'تسجيل الدخول عبر Google',
    },
    onboarding: {
      demo: {
        views: ["يوم", "أسبوع", "شهر", "سنة"],
        allDay: "طوال اليوم",
        prio: ["عالية", "متوسطة", "منخفضة"],
        labels: ["عمل", "رياضة", "منزل"],
        shared: "مشترك",
        create: "إنشاء",
        join: "انضم برمز",
        sharedTitle: "تقاويم مشتركة",
        searchTitle: "ابحث عن الأحداث",
        searchHint: "مثل: طبيب أسنان، اجتماع…",
        notePh: "اكتب ملاحظة…",
        settingsTitle: "الإعدادات",
        light: "فاتح",
        dark: "داكن",
        signout: "خروج",
        ics: "تنزيل .ics",
        notif: "الإشعارات",
        summaryLbl: "الملخّص",
        time: "الوقت",
        telegram: "إرسال عبر Telegram",
        reminder: "قبل 30 دقيقة",
        greeting: "صباح الخير",
        repeat: ["كل يوم", "كل أسبوع"],
        ev: ["اجتماع", "نادٍ", "تسوّق", "طبيب أسنان", "عشاء", "توصيل"],
      },
      skip: "تخطّي",
      next: "التالي",
      start: "ابدأ",
      slides: [
        { key: "calendar", title: "التقويم الذي يحدّثك", text: "يوم، أسبوع، شهر وسنة: شاهد مواعيدك كما تحب. وبلمسة واحدة يقرأ لك مواعيد اليوم والأسبوع بصوت عالٍ." },
        { key: "add", title: "أضِفه بطريقتك", text: "أنشئ حدثًا بالكتابة أو بالصوت أو بصورة: صوّر ورقة أو بريدًا أو مُلصقًا ويملأ الذكاء الاصطناعي كل شيء. ويمكنك أيضًا إرفاق مستندات بالحدث." },
        { key: "planner", title: "نظّم يومك", text: "المخطِّط اليومي والأسبوعي يعرض الأوقات الفارغة والمشغولة: ترتّب كل شيء بنظرة واحدة." },
        { key: "multi", title: "ابحث، دوّن، شارك", text: "اعثر على أي حدث في ثانية، واحتفظ بملاحظاتك دائمًا في متناولك، وشارك التقويم مع العائلة أو الفريق، كلٌّ بلونه." },
        { key: "summary", title: "كل صباح، ملخّصك", text: "تصلك مواعيد اليوم على Telegram وبالبريد، مرتّبة حسب الوقت والأولوية. ابدأ يومك دون أن تنسى شيئًا." },
        { key: "settings", title: "اجعل MeMo يعمل من أجلك", text: "اختر اللغة ووقت الملخّص والإشعارات والنغمة والمظهر: يتكيّف MeMo مع طريقة عملك." },
      ],
    },

    welcome: {
      greeting: 'مرحبًا',
      subtitle: 'اختر لغتك للبدء',
      saving: 'جارٍ الحفظ...',
      continue: 'متابعة →',
      countryLabel: 'بلدك',
      countryPlaceholder: 'اختر بلدك...',
      themeLabel: 'اختر مظهرك',
      timezoneLabel: 'المنطقة الزمنية',
      timezoneHint: 'غيّرها عند السفر',
    },
    eventGenericType: 'حدث',
    today: {
      noEvents: 'لا أحداث مجدولة',
      eventsCount: (n) => `${n} حدث اليوم`,
      emptyTitle: 'يومك خالٍ تمامًا!',
      emptySub: 'اضغط + لإضافة حدث',
      error: 'تعذّر تحميل الأحداث. يُرجى المحاولة مرة أخرى.',
      retry: 'إعادة المحاولة',
      signOutHint: 'إذا استمرت المشكلة، جرّب تسجيل الخروج ثم الدخول من جديد.',
      slowConnection: 'الاتصال بطيء… ما زال التحميل جاريًا.',
      aiLimitReached: 'لقد استخدمت جميع إدخالات الذكاء الاصطناعي الثلاثين (الصوت والصور) لخطة Free هذا الشهر. تتجدد الشهر القادم.',
      weekList: 'قائمة',
      weekGrid: 'شبكة',
      conflictWarning: 'تنبيه: يتعارض مع «{title}» ({time}). هل تريد الحفظ رغم ذلك؟',
      tomorrow: 'غدًا',
      today: 'اليوم',
      yesterday: 'أمس',
      snooze: 'غفوة',
      stop: 'إيقاف',
      prevWeek: 'الأسبوع السابق',
      nextWeek: 'الأسبوع التالي',
      microphone: 'ميكروفون',
      noEventsDay: 'لا أحداث مجدولة',
      emptyTitleDay: 'لا شيء هنا',
      eventsCountDay: (n) => `${n} حدث`,
      prevDay: 'اليوم السابق',
      nextDay: 'اليوم التالي',
      filterAll: 'الكل',
      filterMine: 'أنا فقط',
      ttsButton: 'استماع',
      ttsStop: 'إيقاف',
      ttsNoEvents: 'لا أحداث اليوم.',
      ttsIntro: 'لديك اليوم {n} من الأحداث',
      viewDay: 'يوم',
      viewWeek: 'أسبوع',
      viewMonth: 'شهر',
      viewYear: 'سنة',
      viewSlots: 'Plan',
      slotsHint: 'اضغط على فترة فارغة لإضافة حدث',
      slotsTapDay: 'اضغط على يوم لفتحه',
      slotsFree: 'فارغ',
      slotsBusy: 'مشغول',
      weekOf: 'أسبوع',
    },
    tasks: {
      emptyTitle: 'لا توجد ملاحظات بعد',
      emptySub: 'مثال: شراء الخبز · الاتصال بطبيب الأسنان',
      addPlaceholder: 'اكتب ملاحظة…',
      doneSection: "المكتملة",
      deleteNoteConfirm: "حذف هذه الملاحظة نهائيًا؟",
      restoreNote: "اضغط للاستعادة",
      completed: 'مكتمل',
      noScope: 'سجّل الخروج ثم الدخول مجددًا لتفعيل الملاحظات',
      deleteConfirm: 'حذف هذه الملاحظة؟',
    },
    settings: {
      pageTitle: 'الإعدادات',
      account: 'الحساب',
      signOut: 'تسجيل الخروج',
      themeColor: "لون سمة التطبيق",
      themeColorBtn: "لون التطبيق",
      a11yReminders: "تذكيرات ميسّرة (اهتزاز + صوت)",
      a11yRemindersHint: "في وقت التذكير يهتز الهاتف ويقرأ الحدث بصوت عالٍ.",
      a11yMode: "وضع إمكانية الوصول",
      a11yModeHint: "يُشغّل معًا التباين العالي والتذكيرات بالاهتزاز والصوت.",
      a11yVibration: "اهتزاز",
      a11ySpeak: "قراءة الحدث بصوت",
      a11yContrast: "تباين عالٍ",
      usageTab: "استخدام البيانات",
      a11yTab: "الوصول",
      updateAvailable: "يتوفر إصدار جديد.",
      updateButton: "تحديث",
      bigText: "نص كبير",
      bigTextOn: "مُفعّل",
      bigTextOff: "مُعطّل",
      bigLarge: "كبير",
      bigXLarge: "كبير جدًا",
      feedTitle: "عرض MeMo في تقويم Apple",
      feedDesc: "لأجهزة iPhone وiCloud: مواعيدك في MeMo، داخل تقويم Apple.",
      feedCopy: "نسخ الرابط",
      feedCopied: "تم النسخ!",
      feedSubscribe: "عرضه في تقويمي",
      feedHint: "iPhone: الإعدادات ← التقويم ← الحسابات ← إضافة ← أخرى ← إضافة تقويم مشترَك. Mac: ملف ← اشتراك تقويم جديد.",
      feedConnect: "اربط تقاويمك",
      feedRegenerate: "إعادة إنشاء الرابط",
      saved: '✓ تم حفظ الإعدادات!',
      saveError: 'فشل حفظ الإعدادات. يُرجى المحاولة مرة أخرى.',
      language: 'اللغة',
      telegramSection: 'ملخص Telegram اليومي',
      sendTime: 'وقت الإرسال',
      timezone: 'منطقتك الزمنية',
      tzDetected: 'جهازك موجود في',
      tzUseDetected: 'استخدم هذه المنطقة الزمنية',
      tzAutoDetect: 'اكتشاف تلقائي من جهازي',
      chatId: 'معرّف محادثة Telegram',
      voicePitch: 'طبقة الصوت',
      voicePitchLow: 'منخفض',
      voicePitchMid: 'عادي',
      voicePitchHigh: 'مرتفع',
      pushIntro: 'تصل الإشعارات إلى هاتفك حتى عندما يكون التطبيق مغلقًا. اضغط للتفعيل والاختبار.',
      pushDenied: 'تم رفض الإذن. فعّل إشعارات MeMo من إعدادات الهاتف/المتصفح.',
      pushUnsupported: 'هذا الجهاز لا يدعم الإشعارات الفورية. على iPhone، ثبّت MeMo على الشاشة الرئيسية.',
      pushUnsupportedApp: 'في التطبيق المثبّت تأتي التذكيرات من الهاتف نفسه لا من المتصفّح: تأكّد فقط من أنك سمحت لـ MeMo بعرض الإشعارات. أما Telegram والبريد فهما إضافة إن أردتهما.',
      pushFailed: 'فشل. حاول مرة أخرى أو تحقق من إعدادات الإشعارات.',
      pushSending: 'جارٍ الإرسال…',
      pushTestButton: '🔔 تفعيل / إرسال اختبار',
      pushSent: '✓ تم الإرسال! تحقق من جهازك.',
      pushError: 'خطأ — فعّل الإشعارات في المتصفح.',
      pushNoDevice: 'لم يصل أي جهاز. حاول مرة أخرى، أو أوقف إشعارات MeMo ثم أعد تفعيلها من إعدادات المتصفح.',
      chatIdPlaceholder: 'مثال 123456789',
      chatIdHelp: (bot) => `كيفية الحصول على معرّف المحادثة: افتح Telegram، وابحث عن @${bot}، وأرسل له أي رسالة، ثم اضغط على الرابط في دليل الإعداد.`,
      testSending: 'جارٍ الإرسال...',
      testButton: '📨 أرسل رسالة اختبار الآن',
      testSuccess: '✓ تم إرسال الرسالة! تحقق من Telegram.',
      testError: 'فشل الإرسال. تأكد من حفظ معرّف المحادثة.',
      saving: 'جارٍ الحفظ...',
      saveButton: 'حفظ الإعدادات',
      telegramSetupTitle: 'كيفية ربط Telegram:',
      telegramStep1: (bot) => `افتح Telegram وابحث عن @${bot}`,
      telegramStep2: 'أرسل له أي رسالة للبدء',
      telegramStep3: 'افتح @userinfobot ← أرسل /start ← انسخ معرّف المحادثة',
      telegramStep4: 'الصقه في الحقل أدناه واحفظ',
      supportTitle: "المساعدة والدعم",
      supportDesc: "هل تحتاج إلى مساعدة أو تريد الإبلاغ عن مشكلة؟ راسلنا وسنرد في أقرب وقت ممكن.",
      emailSupport: "راسلنا عبر البريد",
      contactSupport: 'تواصل مع الدعم على Telegram',
      themeSection: 'المظهر',
      themeLabel: 'النمط',
      themeLight: 'فاتح',
      themeDark: 'داكن',
      countryLabel: 'البلد',
      subCalendarsSection: 'التصنيفات',
      subCalendarName: 'الاسم',
      subCalendarAdd: 'إضافة تصنيف',
      subCalendarOwn: 'لوّن أحداثي',
      subCalendarPickColour: 'اختر لونًا',
      subCalendarAutoHint: 'تحصل تصنيفات التقاويم التي ليست لك على لون مؤقّت — اضغط عليها لتختار لونك.',
      subCalendarDeleteConfirm: 'حذف هذا التصنيف؟ لن تُحذف الأحداث.',
      summarySection: 'ملخص الصباح',
      summaryNowhere: "الملخّص لا يُرسَل: لا يعرف MeMo إلى أين. اكتب بريدك بالأسفل، أو اربط Telegram.",
      summaryChannels: 'إلى أين يُرسل',
      viaTelegram: 'الإرسال عبر Telegram',
      viaEmail: 'الإرسال عبر البريد',
      emailAnyProvider: 'يعمل مع أي عنوان بريد إلكتروني — Gmail وOutlook وiCloud وYahoo…',
      emailSection: 'ملخص البريد الإلكتروني',
      emailLabel: 'عنوان بريدك الإلكتروني',
      emailHelp: 'أدخل أي عنوان بريد إلكتروني — Gmail أو Outlook أو iCloud أو بريد العمل، إلخ.',
      emailTestSending: '⏳ جارٍ الإرسال...',
      emailTestButton: '📧 أرسل بريد اختبار',
      emailTestSuccess: '✓ تم إرسال البريد! تحقق من صندوق الوارد.',
      emailTestError: 'فشل إرسال البريد. تحقق من إعدادات الخادم.',
      voiceLabel: 'صوت القراءة',
      voiceDefault: 'افتراضي النظام',
      voiceTest: '▶ اختبار الصوت',
      voiceSection: "الصوت والقراءة",
      voiceSpeed: "السرعة",
      voiceSlow: "بطيئة",
      voiceFast: "سريعة",
      voiceNoVoices: 'لا تتوفر أصوات لهذه اللغة على هذا الجهاز.',
      voiceFemale: '♀ أنثى',
      voiceMale: '♂ ذكر',
      voiceNoMatch: 'لم يُعثر على صوت مطابق — يُستخدم افتراضي النظام.',
      alarmSection: 'إعدادات الإشعارات',
      alarmVolume: 'مستوى الصوت',
      alarmSnooze: 'مدة التأجيل',
      alarmMaxSnooze: 'أقصى عدد للتأجيل',
      alarmForever: 'دائمًا',
      alarmMin: 'دقيقة',
      alarmSound: 'الصوت',
      weatherReset: 'الطقس',
      weatherActive: 'الطقس مُفعّل',
      weatherDisable: 'تعطيل',
      weatherDesc: 'فعّله لرؤية درجة الحرارة والتوقعات على الشاشة الرئيسية.',
      weatherActivating: 'جارٍ الطلب…',
      weatherEnable: 'تفعيل الطقس',
      weatherReEnable: 'إعادة تفعيل الطقس',
      weatherDeniedHint: 'حظر المتصفح الوصول إلى الموقع. اذهب إلى إعدادات المتصفح لإلغاء الحظر، ثم حاول مرة أخرى.',
      weatherDeniedHintApp: "تم رفض الوصول إلى الموقع. اسمح به لـ MeMo من إعدادات الهاتف ثم أعد المحاولة.",
      weatherUnavailable: 'GPS غير متوفر على هذا الجهاز.',
      exportLabel: 'اربط Apple Calendar أو Outlook أو أي تطبيق آخر غير Google',
      exportButton: 'تنزيل ‎.ics',
      exportHint: 'استورد إلى Apple Calendar أو Outlook أو أي تطبيق آخر',
      exporting: 'جارٍ التصدير…',
    },
    form: {
      allDay: "طوال اليوم",
      timeMissing: "اختر وقتًا، أو فعّل «طوال اليوم».",
      pageTitle: 'حدث جديد',
      required: 'يُرجى ملء جميع الحقول المطلوبة.',
      saveError: 'فشل حفظ الحدث. يُرجى المحاولة مرة أخرى.',
      titleLabel: 'الحدث',
      titlePlaceholder: 'مثال موعد الطبيب',
      cancelButton: 'إلغاء',
      recurringEditTitle: 'تعديل الحدث المتكرر',
      recurringThisOnly: 'هذا الحدث فقط',
      recurringAllSeries: 'كل الأحداث في السلسلة',
      recurringNote: 'حدث متكرر — تنطبق التغييرات على هذه المرة فقط',
      photoLabel: 'صورة',
      openInMaps: 'افتح في خرائط Google',
      recurringEvent: 'حدث متكرر',
      typeLabel: 'النوع *',
      typeDefault: 'اختر النوع...',
      datetimeLabel: 'التاريخ والوقت',
      dateFromLabel: 'من',
      dateToLabel: 'إلى',
      locationLabel: 'الموقع (اختياري)',
      locationPlaceholder: 'مثال شارع روما 10، ميلانو',
      priorityLabel: 'الأولوية',
      priorities: [
        { value: 'red',    label: 'عالية',  sublabel: 'يجب فعله' },
        { value: 'yellow', label: 'متوسطة', sublabel: 'يُستحسن فعله' },
        { value: 'green',  label: 'منخفضة', sublabel: 'إن أمكن' },
      ],
      notesLabel: 'ملاحظات (اختياري)',
      notesPlaceholder: 'أضف ملاحظات، أو استخدم الصوت...',
      attLabel: 'المرفقات',
      attAdd: '📎 إرفاق صورة أو PDF',
      attHint: '5 ملفات كحد أقصى، تُحذف بعد 5 أيام',
      attTooMany: '5 مرفقات كحد أقصى',
      attBadType: 'الصور وPDF فقط',
      attTooBig: 'الملف كبير جدًا (10 ميغابايت كحد أقصى)',
      saving: 'جارٍ الحفظ...',
      saveButton: '✓ حفظ الحدث',
      editPageTitle: 'تعديل الحدث',
      editButton: 'تعديل',
      updateButton: '✓ تحديث',
      dictateLabel: 'تكلّم',
      deleteButton: 'حذف',
      deleteConfirm: 'حذف هذا الحدث من Google Calendar؟',
      deleteError: 'خطأ في حذف الحدث. يُرجى المحاولة مرة أخرى.',
      recurrenceLabel: 'التكرار',
      recurrenceNone: 'لا يتكرر',
      recurrenceDaily: 'كل يوم',
      recurrenceWeekly: 'كل أسبوع',
      recurrenceMonthly: 'كل شهر',
      recurrenceYearly: 'كل سنة',
      recurrenceEnds: 'تذكّر',
      recurrenceNever: 'للأبد',
      recurrenceAfter: 'بعد',
      recurrenceTimes: 'مرات',
      recurrenceOnDate: 'حتى',
      recurrenceEvery: 'كل',
      recurrenceLast: 'الأخير',
      recurrenceMonthlyDay: 'نفس اليوم من الشهر',
      recUnitDays: 'أيام',
      recUnitWeeks: 'أسابيع',
      recUnitMonths: 'أشهر',
      recUnitYears: 'سنوات',
      recurrenceDays: ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'],
      deleteRecurring: 'هذا حدث متكرر.',
      deleteOnlyThis: 'هذا الحدث فقط',
      deleteAllSeries: 'جميع أحداث السلسلة',
      voiceHint: 'اضغط وأملِ: الحدث · التاريخ\nالوقت · الموقع · الأولوية · الملاحظات',
      parsing: 'جارٍ ملء النموذج من الصوت...',
      parseError: 'تعذّر فهم التسجيل. يُرجى ملء الحقول يدويًا.',
      imageHint: 'امسح صورة أو لقطة',
      imageParsing: 'جارٍ قراءة الصورة...',
      imageError: 'تعذّر قراءة الصورة. يُرجى ملء الحقول يدويًا.',
      multiTitle: "الأحداث المكتشفة",
      multiSubtitle: "راجِع وصحّح ثم أنشئ.",
      multiCreate: "إنشاء الكل",
      multiNoLabel: "بلا",
      multiCreating: "جارٍ الإنشاء…",
      multiDetails: "الموقع والملاحظات",
      overlapTitle: "تداخل",
      overlapText: "يتداخل مع «{title}» ({time}). يمكنك الحفظ على أي حال.",
      subCalendarLabel: 'التصنيف',
      subCalendarNone: 'بدون',
      visibilityLabel: 'التقويمات',
      visibilityPrivate: 'أنا فقط',
      visibilityGroup: 'تقويم مشترك',
      visibilitySelectGroup: 'اختر مجموعة',
      conflictTitle: 'تعارض في الجدول',
      conflictHint: 'مشغول في هذا الوقت:',
      reminderLabel: 'تذكير',
      reminderNone: 'بدون تذكير',
      reminderOn: '✓ مُفعّل',
      reminderOff: 'تفعيل',
      reminderOptions: [
        { value: 0,    label: 'عند وقت البدء' },
        { value: 5,    label: 'قبل 5 دقائق' },
        { value: 10,   label: 'قبل 10 دقائق' },
        { value: 15,   label: 'قبل 15 دقيقة' },
        { value: 20,   label: 'قبل 20 دقيقة' },
        { value: 25,   label: 'قبل 25 دقيقة' },
        { value: 30,   label: 'قبل 30 دقيقة' },
        { value: 45,   label: 'قبل 45 دقيقة' },
        { value: 60,   label: 'قبل ساعة' },
        { value: 120,  label: 'قبل ساعتين' },
        { value: 180,  label: 'قبل 3 ساعات' },
        { value: 720,  label: 'قبل 12 ساعة' },
        { value: 1440, label: 'قبل يوم' },
        { value: 2880, label: 'قبل يومين' },
        { value: 10080, label: 'قبل أسبوع' },
      ],
    },
    groups: {
      pageTitle: 'التقويمات المشتركة',
      noGroups: 'لا تقويمات بعد',
      noGroupsSub: 'أنشئ تقويمًا أو انضم برمز دعوة',
      createGroup: 'إنشاء تقويم',
      joinGroup: 'الانضمام برمز',
      planLimit: 'لقد بلغت حد التقاويم المشتركة في باقتك. قم بالترقية لإنشاء المزيد.',
      seePlans: 'عرض الباقات',
      calendarFull: 'هذا التقويم المشترك ممتلئ وفق باقة المالك.',
      groupNameLabel: 'اسم التقويم',
      groupNamePlaceholder: 'مثال تقويم عائلة سميث',
      inviteCode: 'رمز الدعوة',
      inviteCodePlaceholder: 'مثال A3F9B2',
      join: 'انضمام',
      create: 'إنشاء',
      creating: 'جارٍ الإنشاء…',
      joining: 'جارٍ الانضمام…',
      members: 'الأعضاء',
      you: '(أنت)',
      admin: 'مشرف',
      member: 'عضو',
      leave: 'مغادرة التقويم',
      leaveConfirm: 'مغادرة هذا التقويم؟',
      delete: 'حذف التقويم',
      deleteConfirm: 'حذف هذا التقويم؟ سيفقد جميع الأعضاء إمكانية الوصول.',
      shareInvite: 'مشاركة الرمز',
      copied: '✓ تم نسخ الرمز!',
      errorCreate: 'تعذّر إنشاء التقويم. يُرجى المحاولة مرة أخرى.',
      errorJoin: 'رمز غير صالح. تحقق وحاول مرة أخرى.',
      errorAlready: 'أنت عضو بالفعل في هذا التقويم.',
      cancel: 'إلغاء',
      saving: 'جارٍ الحفظ…',
    },
    install: {
      title: '📲 ثبّت MeMo',
      subtitle: 'ثبّته مثل تطبيق حقيقي — دون الحاجة إلى App Store.',
      installBtn: '📲 ثبّت على هذا الجهاز',
      installed: '✓ تم التثبيت!',
      oneLogin: '🔐 سجّل الدخول مرة واحدة — يتذكّرك Memo إلى الأبد.',
      ios: {
        title: '🍎 iPhone / iPad (Safari)',
        steps: ['افتح هذا الرابط في Safari', 'اضغط على زر المشاركة □↑ في الأسفل', 'اضغط على "Add to Home Screen"'],
      },
      android: {
        title: '🤖 Android (Chrome)',
        steps: ['افتح هذا الرابط في Chrome', 'اضغط على ⋮ في أعلى اليمين', 'اضغط على "Add to Home Screen"'],
      },
      desktop: {
        title: '💻 الكمبيوتر (Chrome)',
        steps: ['افتح هذا الرابط في Chrome', 'انقر على أيقونة 📲 في شريط العنوان', 'انقر على "Install"'],
      },
    },
  },

};

export function useT(language) {
  if (T[language]) return T[language];
  // Fallback: pt-BR → pt, en-US/en-GB → en, es-AR/es-ES → es
  const base = language?.split('-')[0];
  return T[base] || T['en'];
}

export default T;
